import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  MessageCircle,
  Send,
  Building2,
  Stethoscope,
  Users,
  DollarSign,
  Award,
  Shield,
  Heart,
  Globe,
  Clock,
  CheckCircle2,
  Star,
  Calendar,
  Quote,
  Bell,
  Download,
  ExternalLink,
  Smile,
  Paperclip,
  FileText,
  Image,
  Upload,
  FolderOpen,
  Trash2,
  Loader2,
  Settings
} from "lucide-react";
import axios from "axios";

// Silent PDF download helper — fetches the PDF as a blob and triggers a
// native browser download (works identically on desktop and mobile, no URL
// bar or new tab is ever shown to the user).
export async function downloadIntakeFormPdf(lang = 'el') {
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intake-form.pdf?lang=${lang}`, {
      method: 'GET',
      credentials: 'omit',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `JHouse_Patient_Intake_Form_${lang}.pdf`;
    document.body.appendChild(a);
    a.click();
    // Small delay before revoke — some mobile browsers need the URL alive during the click
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 1500);
  } catch (_err) {
    // Fallback: open in a new tab if the fetch fails (e.g. CORS / offline)
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/intake-form.pdf?lang=${lang}`, '_blank', 'noopener,noreferrer');
  }
}

// Global axios interceptor: attach admin/partner JWT for /admin/* calls (and any authed endpoints)
axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('partnerToken');
    if (token && config.url && (config.url.includes('/admin/') || config.url.includes('/partner/') || config.url.includes('/partners/'))) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (_e) {}
  return config;
});

import { BACKEND_URL, API, COMPANY } from "@/config";
import { requestNotificationPermission, playNotificationSound, sendNotification } from "@/utils/notifications";
import { ChatWindow, PartnerChatBubble, AdminChatPanel } from "@/components/chat";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import BankSettingsPanel from "@/components/BankSettingsPanel";
import PendingTransfersPanel from "@/components/PendingTransfersPanel";
import PaymentMethodModal from "@/components/PaymentMethodModal";
import PartnersManagementPanel from "@/components/PartnersManagementPanel";
import VisitorChatsPanel from "@/components/VisitorChatsPanel";
import DoctorCardStudio from "@/components/DoctorCardStudio";
import AgentsManagementPanel from "@/components/AgentsManagementPanel";
import LineCollaboratorsPanel from "@/components/LineCollaboratorsPanel";
import QuickRepliesPanel from "@/components/QuickRepliesPanel";
import AgentPortal from "@/components/AgentPortal";
import IntakeUploadModal from "@/components/IntakeUploadModal";
import LoadingScreen from "@/components/LoadingScreen";
import GoldParticles from "@/components/GoldParticles";
import TrustBar from "@/components/TrustBar";
import PhilosophySection from "@/components/PhilosophySection";
import { ShebaPartnershipBar } from "@/components/ShebaPartnershipBar";
import FloatingContactStack from "@/components/FloatingContactStack";
import WelcomeVideo from "@/components/WelcomeVideo";
import CorporateAd from "@/components/CorporateAd";
import HeroVideo from "@/components/HeroVideo";
import { AppStoreBadge } from "@/components/AppStoreBadge";
import { HANDLE_LIST, HELP_INFO, HELP, LINE_CAPTIONS } from "@/components/HelpLinePage";
import HelpLinePage from "@/components/HelpLinePage";
import FlyerPage from "@/components/FlyerPage";
import LegalPage from "@/components/LegalPage";
import { LegalFooter } from "@/components/LegalFooter";

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
  // Reload once when a new service worker takes control, so users always get the latest version.
  let _swRefreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (_swRefreshing) return;
    _swRefreshing = true;
    window.location.reload();
  });
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        console.log('SW registered:', registration);
        try { registration.update(); } catch (e) { /* ignore */ }
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

// Night Pharmacies Widget Component
const NightPharmaciesWidget = () => {
  const [pharmacyData, setPharmacyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  useEffect(() => {
    const fetchPharmacies = async () => {
      try {
        const response = await axios.get(`${API}/night-pharmacies`);
        setPharmacyData(response.data);
        // Set first day and first district as default
        if (response.data.days && response.data.days.length > 0) {
          setSelectedDayIndex(0);
          if (response.data.days[0].districts.length > 0) {
            setSelectedDistrict(response.data.days[0].districts[0].district);
          }
        }
      } catch (err) {
        setError('Δεν ήταν δυνατή η φόρτωση των δεδομένων');
      } finally {
        setLoading(false);
      }
    };
    fetchPharmacies();
  }, []);

  // Update selected district when day changes
  useEffect(() => {
    if (pharmacyData?.days?.[selectedDayIndex]?.districts?.length > 0) {
      setSelectedDistrict(pharmacyData.days[selectedDayIndex].districts[0].district);
    }
  }, [selectedDayIndex, pharmacyData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  const currentDay = pharmacyData?.days?.[selectedDayIndex];
  const currentDistrictData = currentDay?.districts?.find(d => d.district === selectedDistrict);
  const hasTwoDays = pharmacyData?.days?.length > 1;

  return (
    <div className="space-y-6">
      {/* Day Selection Tabs - Only show if we have more than one day */}
      {hasTwoDays && (
        <div className="flex justify-center mb-8">
          <div className="bg-navy-100/80 p-1.5 rounded-2xl flex gap-2 border border-slate-700/50">
            {pharmacyData.days.map((day, index) => (
              <button
                key={index}
                onClick={() => setSelectedDayIndex(index)}
                className={`relative px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  selectedDayIndex === index
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-navy-100'
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <span className="text-xs uppercase tracking-wider opacity-80">
                    {day.is_today ? 'Σήμερα' : (index === 0 ? 'Σήμερα' : 'Επόμενη')}
                  </span>
                  <span className="text-sm font-bold">{day.day_name}</span>
                  <span className="text-xs opacity-70">{day.date}</span>
                </div>
                {selectedDayIndex === index && (
                  <motion.div
                    layoutId="dayIndicator"
                    className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl -z-10"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Title with date */}
      <div className="text-center">
        <p className="text-green-400 font-semibold text-sm uppercase tracking-wider mb-2">
          {currentDay?.title}
        </p>
        {!hasTwoDays && (
          <p className="text-slate-500 text-xs">
            {currentDay?.day_name} {currentDay?.date}
          </p>
        )}
      </div>

      {/* District Tabs */}
      <div className="flex flex-wrap justify-center gap-2">
        {currentDay?.districts?.map((district) => (
          <button
            key={district.district}
            onClick={() => setSelectedDistrict(district.district)}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
              selectedDistrict === district.district
                ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                : 'bg-navy-100/50 text-slate-400 hover:bg-navy-100 hover:text-white'
            }`}
          >
            {district.district}
          </button>
        ))}
      </div>

      {/* Pharmacies Grid */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${selectedDayIndex}-${selectedDistrict}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {currentDistrictData?.pharmacies.map((pharmacy, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="bg-navy-100/50 border border-slate-700/50 rounded-xl p-4 hover:border-green-500/50 hover:shadow-lg hover:shadow-green-500/10 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-bold text-white">{pharmacy.surname} {pharmacy.name}</h4>
                  <p className="text-green-400 text-sm">{pharmacy.area}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">{pharmacy.address}</span>
                </div>
                {pharmacy.details && (
                  <p className="text-slate-500 text-xs pl-6">{pharmacy.details}</p>
                )}
                <div className="flex items-center gap-2 pt-2">
                  <a 
                    href={`tel:${pharmacy.phone1}`}
                    className="flex items-center gap-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  >
                    <Phone className="w-3 h-3" />
                    {pharmacy.phone1}
                  </a>
                  {pharmacy.phone2 && (
                    <a 
                      href={`tel:${pharmacy.phone2}`}
                      className="flex items-center gap-1 bg-slate-700/50 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                    >
                      <Phone className="w-3 h-3" />
                      {pharmacy.phone2}
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>

      {/* Info footer */}
      <div className="text-center pt-4 border-t border-slate-700/50">
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
            <span>Κλικ στο τηλέφωνο για κλήση</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Company Info
// Language Switcher Component
const LanguageSwitcher = () => {
  const { language, setLanguage, languageNames, isRTL } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative" data-testid="language-switcher">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 text-sm uppercase tracking-widest text-slate-300 hover:text-gold transition-colors"
        data-testid="language-switcher-btn"
      >
        <Globe className="w-4 h-4" />
        <span>{languageNames[language]}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-48 bg-navy-100 border border-slate-800 rounded-sm shadow-xl z-[110]`}
          >
            {Object.entries(languageNames).map(([code, name]) => (
              <button
                key={code}
                onClick={() => {
                  setLanguage(code);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-3 text-sm hover:bg-gold/10 hover:text-gold transition-colors ${
                  language === code ? 'text-gold bg-gold/5' : 'text-slate-300'
                }`}
                data-testid={`lang-option-${code}`}
              >
                {name}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'home', href: '#home' },
    { key: 'services', href: '#services' },
    { key: 'whyUs', href: '#why-us' },
    { key: 'testimonials', href: '#testimonials' },
    { key: 'faq', href: '#faq' },
    { key: 'contact', href: '#contact' },
  ];

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled ? 'glass' : 'bg-transparent'
      }`}
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3" data-testid="logo">
            <img src={COMPANY.logo} alt="J.House of Secretary" className="h-12 w-auto" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map(item => (
              <a
                key={item.key}
                href={item.href}
                className="text-sm uppercase tracking-widest text-slate-300 hover:text-gold transition-colors"
                data-testid={`nav-${item.key}`}
              >
                {t(`nav.${item.key}`)}
              </a>
            ))}
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden glass border-t border-slate-800"
            >
              <div className="py-4 space-y-2">
                {navItems.map(item => (
                  <a
                    key={item.key}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 text-sm uppercase tracking-widest text-slate-300 hover:text-gold hover:bg-gold/5 transition-colors"
                  >
                    {t(`nav.${item.key}`)}
                  </a>
                ))}
                <div className="px-4 pt-4 border-t border-slate-800">
                  <LanguageSwitcher />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0 bg-navy-200">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-200 via-navy-200/90 to-navy-200" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1510851896000-498520af2236?crop=entropy&cs=srgb&fm=jpg&q=85')] bg-cover bg-center opacity-20" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-navy-200 to-transparent" />
      </div>

      {/* Content - Added more left padding to center content */}
      <div className="relative z-10 max-w-7xl mx-auto px-8 sm:px-12 lg:px-20 pt-24 pb-16 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Text Content */}
          <div className="text-center lg:text-left">
            {/* Main Headline - Very Clear Message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white bg-gold/20 border-l-4 border-gold px-6 py-4 inline-block">
                {t('hero.headline')}
              </h2>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-gold uppercase tracking-[0.3em] text-sm mb-4"
            >
              {t('hero.tagline')}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-playfair text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4"
            >
              {t('hero.title')}
              <br />
              <span className="gold-gradient">{t('hero.subtitle')}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-slate-400 text-lg md:text-xl max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed"
            >
              {t('hero.description')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <a href="#contact" className="btn-primary flex items-center justify-center gap-2" data-testid="hero-cta-btn">
                <Phone className="w-4 h-4" />
                {t('hero.cta')}
              </a>
              <a href="#services" className="btn-secondary flex items-center justify-center gap-2" data-testid="hero-services-btn">
                {t('hero.ctaSecondary')}
              </a>
            </motion.div>

            {/* WhatsApp Consultation Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-6 flex justify-center lg:justify-start"
            >
              <a 
                href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Γεια σας! Θα ήθελα να κλείσω μια δωρεάν ενημερωτική συνάντηση.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-green-500/25"
                data-testid="hero-whatsapp-btn"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span className="font-semibold">{t('hero.ctaConsultation')}</span>
              </a>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-10 flex flex-wrap gap-6 justify-center lg:justify-start"
            >
              <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-2 text-slate-400 hover:text-gold transition-colors text-sm">
                <Phone className="w-4 h-4 text-gold" />
                <span className="font-mono">{COMPANY.phone}</span>
              </a>
              <a href={`tel:${COMPANY.mobile}`} className="flex items-center gap-2 text-slate-400 hover:text-gold transition-colors text-sm">
                <Phone className="w-4 h-4 text-gold" />
                <span className="font-mono">{COMPANY.mobile}</span>
              </a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 text-slate-400 hover:text-gold transition-colors text-sm">
                <Mail className="w-4 h-4 text-gold" />
                <span>{COMPANY.email}</span>
              </a>
            </motion.div>
          </div>

          {/* Right Side - Badge Only */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:flex flex-col gap-6 justify-center items-center"
          >
            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="bg-gold text-navy-200 px-8 py-4 rounded-lg shadow-lg"
            >
              <p className="text-lg font-bold uppercase tracking-wider">{t('hero.available')}</p>
            </motion.div>

            {/* Features List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="card-glass rounded-xl p-6 space-y-3"
            >
              {[
                'heroFeatures.callAnswering',
                'heroFeatures.appointmentReminders',
                'heroFeatures.patientFollowUp',
                'heroFeatures.telemedicineScheduling',
                'heroFeatures.patientIntake',
                'heroFeatures.clinicWorkflow'
              ].map((key, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{t(key)}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <ChevronDown className="w-8 h-8 text-gold" />
      </motion.div>
    </section>
  );
};

// Value Proposition Section - New section above Services
const ValuePropositionSection = () => {
  const { t } = useLanguage();

  const benefits = [
    {
      key: 'callManagement',
      icon: Phone,
    },
    {
      key: 'gesyReferrals',
      icon: Shield,
    },
    {
      key: 'costSaving',
      icon: DollarSign,
    },
    {
      key: 'compliance',
      icon: Award,
    },
    {
      key: 'packages',
      icon: Users,
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-navy-200 to-navy-100" data-testid="value-proposition-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-white mb-6 leading-tight max-w-4xl mx-auto">
            {t('valueProposition.headline')}
          </h2>
          <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            {t('valueProposition.subheadline')}
          </p>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-16"
        >
          <a 
            href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Γεια σας! Θα ήθελα να κλείσω ένα δωρεάν demo 10 λεπτών.')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-light text-navy-200 px-10 py-5 rounded-xl text-xl font-bold transition-all duration-300 shadow-xl hover:shadow-gold/30 hover:scale-105"
            data-testid="value-prop-cta-btn"
          >
            <Phone className="w-6 h-6" />
            {t('valueProposition.ctaDemo')}
          </a>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`card-glass rounded-xl p-6 hover:border-gold/50 transition-all duration-300 ${
                index === 4 ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="text-slate-200 text-base leading-relaxed">
                    {t(`valueProposition.benefits.${benefit.key}`)}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const { t } = useLanguage();

  const services = [
    {
      key: 'corporate',
      icon: Building2,
      image: 'https://customer-assets.emergentagent.com/job_office-pro-2/artifacts/pmmsheie_0-02-05-f62586bff3cf4c4ac512399fa64961475730feb8c9a6c1329fd5f6b6f046529f_b0c7ed1109d54237.jpg'
    },
    {
      key: 'medical',
      icon: Stethoscope,
      image: 'https://images.unsplash.com/photo-1758691461516-7e716e0ca135?crop=entropy&cs=srgb&fm=jpg&q=85&w=600'
    },
    {
      key: 'virtual',
      icon: Users,
      image: 'https://images.unsplash.com/photo-1758691463384-771db2f192b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=600'
    },
    {
      key: 'gesy',
      icon: Shield,
      image: 'https://customer-assets.emergentagent.com/job_office-pro-2/artifacts/65lh4nca_GESY.jpg'
    },
    {
      key: 'pricing',
      icon: DollarSign,
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600'
    }
  ];

  return (
    <section id="services" className="section-padding bg-navy-200" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl md:text-5xl text-white mb-6">
            {t('services.title')}
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`card-glass rounded-lg overflow-hidden group ${
                index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
              }`}
              data-testid={`service-${service.key}`}
            >
              <div className="relative h-48 lg:h-full min-h-[200px] overflow-hidden">
                <img
                  src={service.image}
                  alt={t(`services.${service.key}.title`)}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-200 via-navy-200/60 to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                      <service.icon className="w-5 h-5 text-gold" />
                    </div>
                    <h3 className="font-playfair text-xl text-white">
                      {t(`services.${service.key}.title`)}
                    </h3>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {t(`services.${service.key}.description`)}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Detailed Services Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          {/* Intro */}
          <div className="card-glass rounded-xl p-8 mb-12">
            <h3 className="font-playfair text-2xl md:text-3xl text-gold mb-4">
              {t('detailedServices.title')}
            </h3>
            <p className="text-slate-300 text-lg leading-relaxed">
              {t('detailedServices.intro')}
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { num: '1', key: 'phoneCalls', icon: Phone },
              { num: '2', key: 'appointments', icon: Clock },
              { num: '3', key: 'emailManagement', icon: Mail },
              { num: '4', key: 'patientComm', icon: MessageCircle },
              { num: '5', key: 'patientIntake', icon: Users },
              { num: '6', key: 'adminSupport', icon: Building2 },
              { num: '7', key: 'telemedicine', icon: Globe },
              { num: '8', key: 'followUp', icon: CheckCircle2 },
              { num: '9', key: 'clinicComm', icon: Stethoscope },
              { num: '10', key: 'newPatients', icon: Heart },
              { num: '11', key: 'medicalCenters', icon: Award },
              { num: '12', key: 'remoteSupport', icon: Shield },
            ].map((service, index) => (
              <motion.div
                key={service.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="card-glass rounded-xl p-6 transition-all duration-300 group hover:-translate-y-2 hover:shadow-xl hover:shadow-gold/20 hover:border-gold/60 cursor-pointer"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 group-hover:bg-gold/40 group-hover:scale-110 transition-all duration-300">
                    <span className="text-gold font-bold text-lg">{service.num}</span>
                  </div>
                  <div>
                    <h4 className="font-playfair text-xl text-white mb-2 group-hover:text-gold transition-colors duration-300">
                      {t(`detailedServices.${service.key}.title`)}
                    </h4>
                  </div>
                </div>
                <ul className="space-y-2">
                  {t(`detailedServices.${service.key}.items`).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-400 text-sm group-hover:text-slate-300 transition-colors duration-300">
                      <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Special Featured Service - GESY Referrals - Spans Full Width */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-8 relative overflow-hidden rounded-2xl border-2 border-gold/50 bg-gradient-to-r from-navy-100 via-navy-50 to-navy-100"
          >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-gold rounded-full filter blur-3xl transform translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-gold rounded-full filter blur-3xl transform -translate-x-1/2 translate-y-1/2" />
            </div>
            
            <div className="relative p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                {/* Left - Number and Title */}
                <div className="lg:col-span-1 flex flex-col items-center lg:items-start">
                  <div className="w-20 h-20 rounded-full bg-gold flex items-center justify-center mb-4 shadow-lg shadow-gold/30">
                    <span className="text-navy-200 font-bold text-2xl">13</span>
                  </div>
                  <h4 className="font-playfair text-2xl md:text-3xl text-white text-center lg:text-left">
                    {t('detailedServices.gesyReferrals.title')}
                  </h4>
                  <div className="mt-4 inline-flex items-center gap-2 bg-gold/20 px-4 py-2 rounded-full">
                    <CheckCircle2 className="w-5 h-5 text-gold" />
                    <span className="text-gold font-semibold text-sm uppercase tracking-wider">ΓΕΣΥ</span>
                  </div>
                </div>
                
                {/* Right - Features Grid */}
                <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {t('detailedServices.gesyReferrals.items').map((item, i) => (
                    <div key={i} className="flex items-start gap-3 bg-navy-200/50 backdrop-blur-sm rounded-lg p-4 border border-slate-700/50">
                      <CheckCircle2 className="w-6 h-6 text-gold flex-shrink-0 mt-0.5" />
                      <span className="text-slate-200">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* GDPR Compliance Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-8 relative overflow-hidden rounded-2xl border-2 border-green-500/50 bg-gradient-to-r from-navy-100 via-navy-50 to-navy-100"
          >
            <div className="relative p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Left - Image */}
                <div className="flex justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_office-pro-2/artifacts/fq6b8dpw_Stripping-GDPR-down-to-its-bare-bones-768x605.webp"
                    alt="GDPR Compliance"
                    className="max-w-full h-auto rounded-xl shadow-xl border border-slate-700/50"
                    style={{ maxHeight: '300px' }}
                  />
                </div>
                
                {/* Right - Content */}
                <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-green-500/20 px-4 py-2 rounded-full mb-4">
                    <Shield className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-semibold text-sm uppercase tracking-wider">GDPR</span>
                  </div>
                  <h4 className="font-playfair text-2xl md:text-3xl text-white mb-4">
                    {t('gdprSection.title')}
                  </h4>
                  <p className="text-slate-300 text-lg leading-relaxed">
                    {t('gdprSection.description')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Why Us Section
const WhyUsSection = () => {
  const { t } = useLanguage();

  const features = [
    { key: 'expertise', icon: Award },
    { key: 'personalized', icon: Shield },
    { key: 'commitment', icon: Heart },
  ];

  return (
    <section id="why-us" className="section-padding bg-navy-100" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl md:text-5xl text-white mb-6">
            {t('whyUs.title')}
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            {t('whyUs.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="card-glass rounded-lg p-8 text-center group hover:border-gold/50"
              data-testid={`why-us-${feature.key}`}
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                <feature.icon className="w-8 h-8 text-gold" />
              </div>
              <h3 className="font-playfair text-2xl text-white mb-4">
                {t(`whyUs.${feature.key}.title`)}
              </h3>
              <p className="text-slate-400 leading-relaxed">
                {t(`whyUs.${feature.key}.description`)}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Features Banner */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {[
            { icon: Globe, label: '7 Languages' },
            { icon: Clock, label: '24/7 Support' },
            { icon: CheckCircle2, label: 'Professional Team' },
            { icon: Shield, label: 'Confidential' },
          ].map((item, index) => (
            <div key={index} className="flex items-center justify-center gap-3 py-4 px-6 border border-slate-800 rounded-sm">
              <item.icon className="w-5 h-5 text-gold" />
              <span className="text-slate-300 text-sm uppercase tracking-wider">{item.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Facilities Section
const FacilitiesSection = () => {
  const { t } = useLanguage();

  const facilities = [
    {
      image: 'https://customer-assets.emergentagent.com/job_office-pro-2/artifacts/v2d4pxf5_0-02-05-8a4722a29b7434424299c2729dc61aff1faeddaed3332c722e2c07cbb7f62b2c_589a05c9efc25ca8.jpg',
      alt: 'J.House of Secretary Office'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_office-pro-2/artifacts/ts3taa82_0-02-05-4c5670c349c30c9168d85af6aa1ef3e1ca9731e4932cb18583d92b085b70302e_3fbc9cc4c164f178.jpg',
      alt: 'J.House of Secretary Workspace'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_office-pro-2/artifacts/p8g7xbxz_4.jpg',
      alt: 'J.House of Secretary Reception'
    }
  ];

  return (
    <section id="facilities" className="section-padding bg-navy-200" data-testid="facilities-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl md:text-5xl text-white mb-6">
            {t('facilities.title')}
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            {t('facilities.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {facilities.map((facility, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative overflow-hidden rounded-xl"
              data-testid={`facility-${index}`}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={facility.image}
                  alt={facility.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              {/* Gold overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-navy-200 via-transparent to-transparent opacity-60" />
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-gold/50 rounded-xl transition-colors duration-300" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const { t } = useLanguage();
  const testimonials = t('testimonials.items');

  return (
    <section id="testimonials" className="section-padding bg-navy-200" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl md:text-5xl text-white mb-6">
            {t('testimonials.title')}
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {Array.isArray(testimonials) && testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="card-glass rounded-lg p-8 relative"
              data-testid={`testimonial-${index}`}
            >
              <Quote className="absolute top-6 right-6 w-8 h-8 text-gold/20" />
              
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-gold fill-gold" />
                ))}
              </div>
              
              <p className="text-slate-300 mb-6 leading-relaxed italic">
                "{testimonial.text}"
              </p>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                  <span className="text-gold font-bold">{testimonial.name.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-white font-medium">{testimonial.name}</p>
                  <p className="text-slate-500 text-sm">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);
  const faqItems = t('faq.items');

  return (
    <section id="faq" className="section-padding bg-navy-100" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl md:text-5xl text-white mb-6">
            {t('faq.title')}
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </motion.div>

        <div className="space-y-4">
          {Array.isArray(faqItems) && faqItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="card-glass rounded-lg overflow-hidden"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <span className="text-white font-medium pr-4">{item.question}</span>
                <ChevronDown 
                  className={`w-5 h-5 text-gold flex-shrink-0 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="px-6 pb-6 text-slate-400 leading-relaxed">
                      {item.answer}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState({ loading: false, success: false, error: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: false });

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language
      });
      setStatus({ loading: false, success: true, error: false });
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setStatus({ loading: false, success: false, error: false }), 5000);
    } catch (error) {
      setStatus({ loading: false, success: false, error: true });
      setTimeout(() => setStatus({ loading: false, success: false, error: false }), 5000);
    }
  };

  return (
    <section id="contact" className="section-padding bg-navy-200" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl md:text-5xl text-white mb-6">
            {t('contact.title')}
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="card-glass rounded-lg p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="label-style">{t('contact.form.name')}</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input-style border"
                  data-testid="contact-name-input"
                />
              </div>
              
              <div>
                <label className="label-style">{t('contact.form.email')}</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-style border"
                  data-testid="contact-email-input"
                />
              </div>
              
              <div>
                <label className="label-style">{t('contact.form.phone')}</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="input-style border"
                  data-testid="contact-phone-input"
                />
              </div>
              
              <div>
                <label className="label-style">{t('contact.form.message')}</label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="textarea-style border"
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={status.loading}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="contact-submit-btn"
              >
                {status.loading ? (
                  t('contact.form.sending')
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t('contact.form.submit')}
                  </>
                )}
              </button>

              {status.success && (
                <p className="text-green-400 text-center" data-testid="contact-success-msg">
                  {t('contact.form.success')}
                </p>
              )}
              {status.error && (
                <p className="text-red-400 text-center" data-testid="contact-error-msg">
                  {t('contact.form.error')}
                </p>
              )}
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="card-glass rounded-lg p-8 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-1">{t('contact.info.phone')}</p>
                  <a href={`tel:${COMPANY.phone}`} className="text-white text-lg font-mono hover:text-gold transition-colors">
                    {COMPANY.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-1">{t('contact.info.mobile')}</p>
                  <a href={`tel:${COMPANY.mobile}`} className="text-white text-lg font-mono hover:text-gold transition-colors">
                    {COMPANY.mobile}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-1">{t('contact.info.email')}</p>
                  <a href={`mailto:${COMPANY.email}`} className="text-white hover:text-gold transition-colors break-all">
                    {COMPANY.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-1">{t('contact.info.address')}</p>
                  <p className="text-white">{t('contact.info.addressLine')}</p>
                </div>
              </div>

              {/* WhatsApp Consultation Button */}
              <div className="pt-4 border-t border-slate-700">
                <a 
                  href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Γεια σας! Θα ήθελα να κλείσω μια δωρεάν ενημερωτική συνάντηση.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 bg-green-600 hover:bg-green-700 text-white px-6 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-green-500/25 w-full"
                  data-testid="contact-whatsapp-btn"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="font-semibold">{t('hero.ctaConsultation')}</span>
                </a>
              </div>
            </div>

            {/* Map */}
            <div className="card-glass rounded-lg overflow-hidden h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3260.8!2d33.3656!3d35.1856!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzXCsDExJzA4LjIiTiAzM8KwMjEnNTYuMiJF!5e0!3m2!1sen!2s!4v1620000000000!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Office Location"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Live Chat Widget
const LiveChatWidget = () => {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId] = useState(() => `session_${Date.now()}`);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ sender: 'agent', message: t('chat.welcome'), timestamp: new Date().toISOString() }]);
    }
  }, [isOpen, messages.length, t]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { sender: 'user', message: input, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: input,
        language
      });
      setMessages(prev => [...prev, { 
        sender: 'agent', 
        message: response.data.message, 
        timestamp: response.data.timestamp 
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        sender: 'agent', 
        message: t('chat.offline'), 
        timestamp: new Date().toISOString() 
      }]);
    }
  };

  return (
    <div className="fixed bottom-6 right-24 z-[9999]" data-testid="live-chat-widget">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="absolute bottom-16 right-0 w-80 sm:w-96 card-glass rounded-lg overflow-hidden shadow-2xl"
          >
            {/* Header */}
            <div className="bg-gold p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-navy-200" />
                <span className="font-bold text-navy-200">{t('chat.title')}</span>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-navy-200 hover:opacity-70">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-lg ${
                      msg.sender === 'user'
                        ? 'bg-gold text-navy-200'
                        : 'bg-slate-800 text-white'
                    }`}
                  >
                    {msg.message}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-slate-800">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder={t('chat.placeholder')}
                  className="input-style border flex-1 h-10 text-sm"
                  data-testid="chat-input"
                />
                <button
                  onClick={sendMessage}
                  className="w-10 h-10 bg-gold rounded-sm flex items-center justify-center hover:bg-gold-light transition-colors"
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4 text-navy-200" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all ${
          isOpen ? 'bg-slate-800' : 'bg-gold animate-glow'
        }`}
        data-testid="chat-toggle-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-navy-200" />
        )}
      </button>
    </div>
  );
};

// Partners Section - Logos of our partners
const PartnersSection = () => {
  const { t } = useLanguage();
  
  const partners = [
    {
      name: 'Mediterranean Hospital of Cyprus',
      logo: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/oyt7aetv_MEDITERRENIAN.jpg',
    },
    {
      name: 'Apostolos Loukas Medical Center',
      logo: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/e8jyze7t_%CE%91%CE%A0%CE%9F%CE%A3%CE%A4%CE%9F%CE%9B%CE%9F%CE%A3%20%CE%9B%CE%9F%CE%A5%CE%9A%CE%91%CE%A3.jpg',
    },
    {
      name: 'Fetal Medicine Centre',
      logo: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/4vsrsssc_FETAL.jpg',
    },
    {
      name: 'Pashias Dental Clinic',
      logo: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/1x3zdm0s_PASHIAS.png',
    },
    {
      name: 'Dr Kazamias',
      logo: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/u1cwk4i9_%CE%9A%CE%91%CE%96%CE%91%CE%9C%CE%99%CE%91%CE%A3.jpg',
    },
    {
      name: 'Dr Christodoulidis',
      logo: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/hl4kmhye_%CE%94%CE%A1%20%CE%A7%CE%A1%CE%99%CE%A3%CE%A4%CE%9F%CE%94%CE%9F%CE%A5%CE%9B%CE%99%CE%94%CE%97%CE%A3.jpg',
    },
    {
      name: 'Dr Koumouri',
      logo: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/x4ugl8ra_0-02-05-83e3efbe740bb32f4fb67e41aa86ec3378e80f2c76269395dbbf7f1aff6253e0_9100c6c5d426ce09.jpg',
    },
    {
      name: 'Dr Gregoriou',
      logo: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/c0e3ovo6_0-02-05-732ecfce02d5d6f89e4ee868906c8ec25c19cfa2699e464d016c48b899c7f3a4_91914cdda31bb1e9.jpg',
    },
    {
      name: 'Nonis Clinical Laboratory',
      logo: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/5e4c6sba_nonis-clinical-laboratory-logo.jpg',
    },
    {
      name: 'Μαρία Κοσμετάτου - Ρευματολόγος',
      logo: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/7q0ge2bq_1000055665.jpg',
      scale: 'scale-125',
    },
    {
      name: 'Christine Kylilis - Acupuncture & Chinese Medicine',
      logo: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/5bnjfnst_0-02-05-0ac4ee5462ddb59011ed77e76e63937560137dbb4ebee3721337f8e41ba19ce2_fb66e66eca89a0a5.jpg',
    },
    {
      name: 'Dr Andriani Evripidou Didichenko - Gynaecologist & Obstetrician',
      logo: 'https://customer-assets-jai6qajn.emergentagent.net/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/raucpn0b_DR%20ANDRIANI%20EVRIPIDOU.jpg',
    },
    {
      name: 'Caring Hands - Private Nursing Services',
      logo: 'https://customer-assets-jai6qajn.emergentagent.net/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/0lcak90f_CARING%20HANDS.jpg',
    },
  ];

  // Duplicate partners for seamless infinite scroll
  const allPartners = [...partners, ...partners];

  return (
    <section className="py-16 bg-navy-100/30 overflow-hidden" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-playfair text-3xl md:text-4xl text-white mb-4">
            {t('partners.title')}
          </h2>
          <p className="text-slate-400 text-lg">
            {t('partners.subtitle')}
          </p>
        </motion.div>

        {/* Marquee Container */}
        <div className="relative">
          {/* Gradient overlays for smooth fade effect */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-navy-100/80 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-navy-100/80 to-transparent z-10 pointer-events-none" />
          
          {/* Scrolling container */}
          <div className="flex animate-marquee">
            {allPartners.map((partner, index) => (
              <div
                key={index}
                className="flex-shrink-0 mx-6"
              >
                <div className="w-40 h-40 md:w-48 md:h-48 bg-white rounded-xl border-2 border-gold/30 hover:border-gold transition-all duration-300 p-4 flex items-center justify-center shadow-lg hover:shadow-gold/20 group">
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className={`max-w-full max-h-full object-contain filter group-hover:brightness-110 transition-all duration-300 ${partner.scale || ''}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t, language } = useLanguage();

  return (
    <footer className="bg-navy border-t border-slate-800 py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={COMPANY.logo} alt="J.House of Secretary" className="h-10 w-auto" />
            <span className="text-slate-400">
              &copy; {new Date().getFullYear()} J.House of Secretary. {t('footer.rights')}
            </span>
          </div>
          
          <div className="flex items-center gap-6">
            <a href="/privacy" className="text-slate-400 hover:text-gold transition-colors text-sm" data-testid="footer-privacy-link">
              {t('footer.privacy')}
            </a>
            <a href="/terms" className="text-slate-400 hover:text-gold transition-colors text-sm" data-testid="footer-terms-link">
              {t('footer.terms')}
            </a>
            <a href="/professionals" className="text-slate-400 hover:text-gold transition-colors text-sm" data-testid="footer-professionals-link">
              {language === 'el' ? 'Συνεργασία με Επαγγελματίες Υγείας' : 'Healthcare Professionals'}
            </a>
            <a href="/disclaimer" className="text-slate-400 hover:text-gold transition-colors text-sm" data-testid="footer-disclaimer-link">
              {language === 'el' ? 'Αποποίηση Ιατρικής Ευθύνης' : 'Medical Disclaimer'}
            </a>
            <a href={`tel:${COMPANY.phone}`} className="text-slate-400 hover:text-gold transition-colors">
              <Phone className="w-5 h-5" />
            </a>
            <a href={`mailto:${COMPANY.email}`} className="text-slate-400 hover:text-gold transition-colors">
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Monthly Folders Admin Component
const MonthlyFoldersAdmin = ({ username, partnerName }) => {
  const [folders, setFolders] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [monthVisits, setMonthVisits] = useState([]);
  const [editingVisit, setEditingVisit] = useState(null);

  const greekMonths = [
    'Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος',
    'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'
  ];

  useEffect(() => {
    fetchFolders();
  }, [username, selectedYear]);

  const fetchFolders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/partners/${username}/monthly-folders?year=${selectedYear}`);
      setFolders(response.data.folders);
    } catch (err) {
      console.error('Error fetching folders:', err);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = async (folder) => {
    setSelectedMonth(folder.month);
    setEditData({
      total_calls: folder.total_calls || 0,
      answered_calls: folder.answered_calls || 0,
      missed_calls: folder.missed_calls || 0,
      outgoing_calls: folder.outgoing_calls || 0,
      total_visits: folder.total_visits || 0,
      attended_visits: folder.attended_visits || 0,
      no_show_visits: folder.no_show_visits || 0,
      amount_due: folder.amount_due || 0,
      amount_paid: folder.amount_paid || 0,
      payment_status: folder.payment_status || 'pending',
      admin_notes: folder.admin_notes || ''
    });
    // Fetch visits for this month
    try {
      const monthStr = `${selectedYear}-${String(folder.month).padStart(2, '0')}`;
      const response = await axios.get(`${API}/partners/${username}/visits?month=${monthStr}`);
      setMonthVisits(response.data.visits || []);
    } catch (err) {
      setMonthVisits([]);
    }
  };

  const saveVisitEdit = async (visitId) => {
    if (!editingVisit) return;
    try {
      await axios.put(`${API}/admin/patient-visits/${visitId}`, editingVisit);
      setEditingVisit(null);
      // Refresh visits
      const monthStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
      const response = await axios.get(`${API}/partners/${username}/visits?month=${monthStr}`);
      setMonthVisits(response.data.visits || []);
      fetchFolders();
    } catch (err) {
      alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    }
  };

  const deleteVisit = async (visitId) => {
    if (!window.confirm('Θέλετε σίγουρα να διαγράψετε αυτή την επίσκεψη;')) return;
    try {
      await axios.delete(`${API}/admin/patient-visits/${visitId}`);
      const monthStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
      const response = await axios.get(`${API}/partners/${username}/visits?month=${monthStr}`);
      setMonthVisits(response.data.visits || []);
      fetchFolders();
    } catch (err) {
      alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    }
  };

  const saveMonthData = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/partners/${username}/monthly-folders/${selectedYear}/${selectedMonth}`, editData);
      setSelectedMonth(null);
      setEditData(null);
      fetchFolders();
    } catch (err) {
      alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (month, type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const endpoint = type === 'calls' 
          ? `${API}/admin/partners/${username}/monthly-folders/${selectedYear}/${month}/upload-calls`
          : `${API}/admin/partners/${username}/monthly-folders/${selectedYear}/${month}/upload-visits`;
        const res = await axios.post(endpoint, formData);
        alert(res.data.message);
        fetchFolders();
      } catch (err) {
        alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
      }
    };
    input.click();
  };

  return (
    <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <span>📁</span> Μηνιαίοι Φάκελοι - {partnerName}
        </h3>
        <select 
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          className="px-3 py-2 bg-navy-200/50 border border-slate-700 rounded-lg text-white text-sm"
        >
          <option value={2026}>2026</option>
          <option value={2025}>2025</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-gold border-t-transparent"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {folders.map((folder) => {
            const hasData = folder.total_calls > 0 || folder.total_visits > 0;
            const isPaid = folder.payment_status === 'paid';
            const isPending = folder.payment_status === 'pending' || folder.payment_status === 'partial';
            
            return (
              <div 
                key={folder.month}
                onClick={() => openEditModal(folder)}
                className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all hover:scale-105 ${
                  hasData 
                    ? isPaid 
                      ? 'bg-green-500/10 border-green-500/50' 
                      : 'bg-red-500/10 border-red-500/50'
                    : 'bg-navy-200/30 border-slate-700/50 hover:border-slate-600'
                }`}
              >
                <div className="text-center">
                  <p className="text-white font-medium text-sm">{folder.month_name}</p>
                  <div className="mt-2 space-y-1 text-xs">
                    <p className="text-slate-400">
                      📞 <span className={hasData ? 'text-white' : 'text-slate-500'}>{folder.total_calls}</span>
                    </p>
                    <p className="text-slate-400">
                      👥 <span className={hasData ? 'text-white' : 'text-slate-500'}>{folder.total_visits}</span>
                    </p>
                    {folder.amount_due > 0 && (
                      <p className={`font-medium ${isPaid ? 'text-green-400' : 'text-red-400'}`}>
                        €{folder.amount_due?.toFixed(0)}
                      </p>
                    )}
                  </div>
                </div>
                {hasData && (
                  <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${isPaid ? 'bg-green-400' : 'bg-red-400'}`}></div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {selectedMonth && editData && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-navy-200 rounded-2xl border border-gold/30 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-navy-200 border-b border-slate-700 p-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">
                {greekMonths[selectedMonth - 1]} {selectedYear} - {partnerName}
              </h3>
              <button onClick={() => { setSelectedMonth(null); setEditData(null); }} className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white">✕</button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Calls Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium flex items-center gap-2"><span>📞</span> Κλήσεις</h4>
                  <button onClick={() => handleFileUpload(selectedMonth, 'calls')} className="text-xs px-3 py-1 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30">📤 Upload CSV</button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-slate-400 text-xs">Σύνολο</label>
                    <input type="number" value={editData.total_calls} onChange={(e) => setEditData({...editData, total_calls: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 bg-navy-100/50 border border-slate-700 rounded-lg text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs">Απαντήθηκαν</label>
                    <input type="number" value={editData.answered_calls} onChange={(e) => setEditData({...editData, answered_calls: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 bg-navy-100/50 border border-slate-700 rounded-lg text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs">Αναπάντητες</label>
                    <input type="number" value={editData.missed_calls} onChange={(e) => setEditData({...editData, missed_calls: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 bg-navy-100/50 border border-slate-700 rounded-lg text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs">Εξερχόμενες</label>
                    <input type="number" value={editData.outgoing_calls} onChange={(e) => setEditData({...editData, outgoing_calls: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 bg-navy-100/50 border border-slate-700 rounded-lg text-white text-sm" />
                  </div>
                </div>
              </div>

              {/* Visits Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium flex items-center gap-2"><span>👥</span> Επισκέψεις</h4>
                  <button onClick={() => handleFileUpload(selectedMonth, 'visits')} className="text-xs px-3 py-1 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30">📤 Upload CSV</button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-slate-400 text-xs">Σύνολο</label>
                    <input type="number" value={editData.total_visits} onChange={(e) => setEditData({...editData, total_visits: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 bg-navy-100/50 border border-slate-700 rounded-lg text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs">Εμφανίστηκαν</label>
                    <input type="number" value={editData.attended_visits} onChange={(e) => setEditData({...editData, attended_visits: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 bg-navy-100/50 border border-slate-700 rounded-lg text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs">No-Show</label>
                    <input type="number" value={editData.no_show_visits} onChange={(e) => setEditData({...editData, no_show_visits: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 bg-navy-100/50 border border-slate-700 rounded-lg text-white text-sm" />
                  </div>
                </div>
              </div>

              {/* Individual Visits List */}
              {monthVisits.length > 0 && (
                <div>
                  <h4 className="text-white font-medium flex items-center gap-2 mb-3"><span>📋</span> Λίστα Ασθενών ({monthVisits.length})</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {monthVisits.map((visit) => (
                      <div key={visit.id} className={`rounded-lg border p-3 ${visit.attended ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
                        {editingVisit && editingVisit._editId === visit.id ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={editingVisit.patient_name || ''}
                                onChange={(e) => setEditingVisit({...editingVisit, patient_name: e.target.value})}
                                placeholder="Ονοματεπώνυμο"
                                className="px-2 py-1.5 bg-navy-100/50 border border-slate-600 rounded text-white text-sm"
                              />
                              <input
                                type="text"
                                value={editingVisit.patient_initials || ''}
                                onChange={(e) => setEditingVisit({...editingVisit, patient_initials: e.target.value})}
                                placeholder="Αρχικά"
                                className="px-2 py-1.5 bg-navy-100/50 border border-slate-600 rounded text-white text-sm"
                              />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <input
                                type="date"
                                value={editingVisit.appointment_date || ''}
                                onChange={(e) => setEditingVisit({...editingVisit, appointment_date: e.target.value})}
                                className="px-2 py-1.5 bg-navy-100/50 border border-slate-600 rounded text-white text-sm"
                              />
                              <input
                                type="time"
                                value={editingVisit.appointment_time || ''}
                                onChange={(e) => setEditingVisit({...editingVisit, appointment_time: e.target.value})}
                                className="px-2 py-1.5 bg-navy-100/50 border border-slate-600 rounded text-white text-sm"
                              />
                              <select
                                value={editingVisit.attended ? 'true' : 'false'}
                                onChange={(e) => setEditingVisit({...editingVisit, attended: e.target.value === 'true'})}
                                className="px-2 py-1.5 bg-navy-100/50 border border-slate-600 rounded text-white text-sm"
                              >
                                <option value="true">Εμφανίστηκε</option>
                                <option value="false">Δεν εμφανίστηκε</option>
                              </select>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => saveVisitEdit(visit.id)} className="px-3 py-1 bg-gold text-navy-200 text-xs font-medium rounded hover:bg-amber-500">Αποθήκευση</button>
                              <button onClick={() => setEditingVisit(null)} className="px-3 py-1 bg-slate-700 text-slate-300 text-xs rounded hover:bg-slate-600">Ακύρωση</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${visit.attended ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                {visit.attended ? '✓' : '✗'}
                              </span>
                              <div>
                                <p className="text-white text-sm font-medium">{visit.patient_name || visit.patient_initials}</p>
                                {visit.patient_name && <p className="text-slate-500 text-xs">{visit.patient_initials}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-400 text-xs">{visit.appointment_date} {visit.appointment_time || ''}</span>
                              <button 
                                onClick={() => setEditingVisit({...visit, _editId: visit.id})}
                                className="w-7 h-7 rounded bg-slate-700/50 hover:bg-slate-600 flex items-center justify-center text-slate-400 hover:text-gold transition-colors"
                                title="Επεξεργασία"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                              </button>
                              <button 
                                onClick={() => deleteVisit(visit.id)}
                                className="w-7 h-7 rounded bg-slate-700/50 hover:bg-red-500/30 flex items-center justify-center text-slate-400 hover:text-red-400 transition-colors"
                                title="Διαγραφή"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Financial Section */}
              <div>
                <h4 className="text-white font-medium flex items-center gap-2 mb-3"><span>💰</span> Οικονομικά</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-slate-400 text-xs">Οφειλή (€)</label>
                    <input type="number" step="0.01" value={editData.amount_due} onChange={(e) => setEditData({...editData, amount_due: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 bg-navy-100/50 border border-slate-700 rounded-lg text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs">Πληρωμένα (€)</label>
                    <input type="number" step="0.01" value={editData.amount_paid} onChange={(e) => setEditData({...editData, amount_paid: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 bg-navy-100/50 border border-slate-700 rounded-lg text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs">Κατάσταση</label>
                    <select value={editData.payment_status} onChange={(e) => setEditData({...editData, payment_status: e.target.value})} className="w-full px-3 py-2 bg-navy-100/50 border border-slate-700 rounded-lg text-white text-sm">
                      <option value="pending">Εκκρεμεί</option>
                      <option value="partial">Μερική</option>
                      <option value="paid">Εξοφλημένο</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-slate-400 text-xs">Σημειώσεις Admin</label>
                <textarea 
                  value={editData.admin_notes} 
                  onChange={(e) => setEditData({...editData, admin_notes: e.target.value})}
                  className="w-full px-3 py-2 bg-navy-100/50 border border-slate-700 rounded-lg text-white text-sm h-20 resize-none"
                  placeholder="Σημειώσεις για τον μήνα..."
                ></textarea>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-700">
                <button onClick={() => { setSelectedMonth(null); setEditData(null); }} className="flex-1 py-3 border border-slate-600 text-slate-300 rounded-xl hover:bg-slate-700/50">Ακύρωση</button>
                <button onClick={saveMonthData} disabled={saving} className="flex-1 py-3 bg-gradient-to-r from-gold to-amber-600 text-navy-200 font-bold rounded-xl hover:from-amber-500 hover:to-gold disabled:opacity-50">
                  {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Monthly Folders Partner Component (Read Only View)
const MonthlyFoldersPartner = ({ username }) => {
  const [folders, setFolders] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(true);

  const greekMonths = [
    'Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος',
    'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'
  ];

  useEffect(() => {
    fetchFolders();
  }, [username, selectedYear]);

  const fetchFolders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/partners/${username}/monthly-folders?year=${selectedYear}`);
      setFolders(response.data.folders);
    } catch (err) {
      console.error('Error fetching folders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const totals = folders.reduce((acc, f) => ({
    calls: acc.calls + (f.total_calls || 0),
    visits: acc.visits + (f.total_visits || 0),
    due: acc.due + (f.amount_due || 0),
    paid: acc.paid + (f.amount_paid || 0)
  }), { calls: 0, visits: 0, due: 0, paid: 0 });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-playfair text-white flex items-center gap-2">
          <span>📁</span> Μηνιαίοι Φάκελοι
        </h2>
        <select 
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          className="px-4 py-2 bg-navy-200/50 border border-slate-700 rounded-xl text-white"
        >
          <option value={2026}>2026</option>
          <option value={2025}>2025</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <p className="text-slate-400 text-sm">Σύνολο Κλήσεων</p>
          <p className="text-2xl font-bold text-blue-400">{totals.calls}</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <p className="text-slate-400 text-sm">Σύνολο Επισκέψεων</p>
          <p className="text-2xl font-bold text-green-400">{totals.visits}</p>
        </div>
        <div className="bg-gold/10 border border-gold/30 rounded-xl p-4">
          <p className="text-slate-400 text-sm">Συνολική Οφειλή</p>
          <p className="text-2xl font-bold text-gold">€{totals.due.toFixed(2)}</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
          <p className="text-slate-400 text-sm">Πληρωμένα</p>
          <p className="text-2xl font-bold text-emerald-400">€{totals.paid.toFixed(2)}</p>
        </div>
      </div>

      {/* Monthly Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-gold border-t-transparent"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {folders.map((folder) => {
            const hasData = folder.total_calls > 0 || folder.total_visits > 0;
            const isPaid = folder.payment_status === 'paid';
            const isCurrentMonth = folder.month === new Date().getMonth() + 1 && selectedYear === new Date().getFullYear();
            
            return (
              <div 
                key={folder.month}
                onClick={() => setSelectedFolder(folder)}
                className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all hover:scale-105 ${
                  isCurrentMonth 
                    ? 'bg-gold/10 border-gold' 
                    : hasData 
                      ? isPaid 
                        ? 'bg-green-500/10 border-green-500/50' 
                        : 'bg-red-500/10 border-red-500/50'
                      : 'bg-navy-100/30 border-slate-700/50 hover:border-slate-600'
                }`}
              >
                {isCurrentMonth && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gold text-navy-200 text-[10px] font-bold px-2 rounded">
                    ΤΩΡΑ
                  </div>
                )}
                <div className="text-center">
                  <p className="text-white font-medium">{folder.month_name}</p>
                  <div className="mt-3 space-y-1">
                    <p className="text-slate-400 text-sm">
                      📞 <span className={hasData ? 'text-white font-medium' : 'text-slate-500'}>{folder.total_calls}</span>
                    </p>
                    <p className="text-slate-400 text-sm">
                      👥 <span className={hasData ? 'text-white font-medium' : 'text-slate-500'}>{folder.total_visits}</span>
                    </p>
                    {folder.amount_due > 0 && (
                      <p className={`text-sm font-medium ${isPaid ? 'text-green-400' : 'text-red-400'}`}>
                        €{folder.amount_due?.toFixed(0)}
                      </p>
                    )}
                  </div>
                </div>
                {hasData && (
                  <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${isPaid ? 'bg-green-400' : 'bg-red-400'}`}></div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Folder Detail Modal */}
      {selectedFolder && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-navy-200 rounded-2xl border border-gold/30 w-full max-w-lg"
          >
            <div className="border-b border-slate-700 p-5 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">
                {greekMonths[selectedFolder.month - 1]} {selectedYear}
              </h3>
              <button onClick={() => setSelectedFolder(null)} className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white">✕</button>
            </div>
            
            <div className="p-5 space-y-5">
              {/* Calls */}
              <div className="bg-navy-100/30 rounded-xl p-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2"><span>📞</span> Κλήσεις</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-slate-400 text-xs">Σύνολο</p>
                    <p className="text-white text-lg font-medium">{selectedFolder.total_calls}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Απαντήθηκαν</p>
                    <p className="text-green-400 text-lg font-medium">{selectedFolder.answered_calls}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Αναπάντητες</p>
                    <p className="text-red-400 text-lg font-medium">{selectedFolder.missed_calls}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Εξερχόμενες</p>
                    <p className="text-blue-400 text-lg font-medium">{selectedFolder.outgoing_calls}</p>
                  </div>
                </div>
              </div>

              {/* Visits */}
              <div className="bg-navy-100/30 rounded-xl p-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2"><span>👥</span> Επισκέψεις</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-slate-400 text-xs">Σύνολο</p>
                    <p className="text-white text-lg font-medium">{selectedFolder.total_visits}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Εμφανίστηκαν</p>
                    <p className="text-green-400 text-lg font-medium">{selectedFolder.attended_visits}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">No-Show</p>
                    <p className="text-red-400 text-lg font-medium">{selectedFolder.no_show_visits}</p>
                  </div>
                </div>
              </div>

              {/* Financials */}
              <div className="bg-navy-100/30 rounded-xl p-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2"><span>💰</span> Οικονομικά</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-slate-400 text-xs">Οφειλή</p>
                    <p className="text-gold text-lg font-medium">€{selectedFolder.amount_due?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Πληρωμένα</p>
                    <p className="text-green-400 text-lg font-medium">€{selectedFolder.amount_paid?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Κατάσταση</p>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                      selectedFolder.payment_status === 'paid' 
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {selectedFolder.payment_status === 'paid' ? 'Εξοφλημένο' : selectedFolder.payment_status === 'partial' ? 'Μερική' : 'Εκκρεμεί'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              {selectedFolder.admin_notes && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                  <h4 className="text-amber-400 font-medium mb-2 flex items-center gap-2"><span>📝</span> Σημείωση</h4>
                  <p className="text-slate-300 text-sm">{selectedFolder.admin_notes}</p>
                </div>
              )}

              <button onClick={() => setSelectedFolder(null)} className="w-full py-3 bg-gradient-to-r from-gold to-amber-600 text-navy-200 font-bold rounded-xl hover:from-amber-500 hover:to-gold">
                Κλείσιμο
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

const InternationalRequestsPanel = ({
  requests, loading, statusFilter, setStatusFilter,
  onUpdate, onDelete, notesDraft, setNotesDraft, onRefresh,
}) => {
  const statusColors = {
    new: 'bg-red-500/15 text-red-400 border-red-500/30',
    in_progress: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    completed: 'bg-green-500/15 text-green-400 border-green-500/30',
  };
  const statusLabels = {
    new: 'Νέο',
    in_progress: 'Σε εξέλιξη',
    completed: 'Ολοκληρωμένο',
  };

  const formatSize = (b) => b < 1024 * 1024 ? `${Math.round(b/1024)} KB` : `${(b/1024/1024).toFixed(1)} MB`;
  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString('el-GR'); } catch (e) { return iso; }
  };

  const counts = {
    all: requests.length,
    new: requests.filter(r => r.status === 'new').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    completed: requests.filter(r => r.status === 'completed').length,
  };

  return (
    <div className="space-y-6" data-testid="international-requests-panel">
      <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Globe className="w-6 h-6 text-gold" />
            Αιτήματα Διεθνούς Πρόσβασης Υγείας
          </h2>
          <button
            onClick={onRefresh}
            className="px-3 py-1.5 bg-gold/10 text-gold border border-gold/30 rounded-lg hover:bg-gold/20 text-sm"
            data-testid="intl-refresh-btn"
          >
            Ανανέωση
          </button>
        </div>

        {/* Status filter chips */}
        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'all', label: 'Όλα' },
            { id: 'new', label: 'Νέα' },
            { id: 'in_progress', label: 'Σε εξέλιξη' },
            { id: 'completed', label: 'Ολοκληρωμένα' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                statusFilter === f.id
                  ? 'bg-gold text-navy-200 border-gold font-medium'
                  : 'bg-navy-200/50 text-slate-300 border-slate-700 hover:bg-navy-200'
              }`}
              data-testid={`intl-filter-${f.id}`}
            >
              {f.label} <span className="opacity-60 ml-1">({counts[f.id] ?? 0})</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-12 text-center">
          <Globe className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Δεν υπάρχουν αιτήματα σε αυτή την κατηγορία</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((r) => {
            const currentNotes = notesDraft[r.id] !== undefined ? notesDraft[r.id] : r.admin_notes || '';
            return (
              <div
                key={r.id}
                className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-5"
                data-testid={`intl-request-${r.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-white">{r.name}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full border ${statusColors[r.status] || statusColors.new}`}>
                        {statusLabels[r.status] || r.status}
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm">
                      {formatDate(r.created_at)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={r.status}
                      onChange={(e) => onUpdate(r.id, { status: e.target.value })}
                      className="bg-navy-200 border border-slate-700 text-white rounded-lg px-3 py-1.5 text-sm"
                      data-testid={`intl-status-select-${r.id}`}
                    >
                      <option value="new">Νέο</option>
                      <option value="in_progress">Σε εξέλιξη</option>
                      <option value="completed">Ολοκληρωμένο</option>
                    </select>
                    <button
                      onClick={() => onDelete(r.id)}
                      className="p-2 bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/20"
                      title="Διαγραφή"
                      data-testid={`intl-delete-btn-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-3">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gold flex-shrink-0" />
                    <a href={`tel:${r.phone}`} className="text-slate-200 hover:text-gold">{r.phone}</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gold flex-shrink-0" />
                    <a href={`mailto:${r.email}`} className="text-slate-200 hover:text-gold truncate">{r.email}</a>
                  </div>
                </div>

                {r.message && (
                  <div className="bg-navy-200/50 border border-slate-700 rounded-lg p-3 mb-3">
                    <p className="text-xs text-slate-500 mb-1">Μήνυμα ασθενή:</p>
                    <p className="text-slate-200 text-sm whitespace-pre-wrap">{r.message}</p>
                  </div>
                )}

                {r.files_count > 0 && (
                  <div className="bg-navy-200/30 border border-slate-700 rounded-lg p-3 mb-3">
                    <p className="text-xs text-slate-500 mb-2">
                      Συνημμένα αρχεία: <span className="text-gold font-medium">{r.files_count}</span>
                      <span className="ml-2">• Σύνολο: {formatSize(r.total_size_bytes || 0)}</span>
                    </p>
                    <ul className="space-y-1">
                      {(r.files_summary || []).map((f, i) => (
                        <li key={i} className="flex items-center justify-between text-xs text-slate-300">
                          <span className="flex items-center gap-2 truncate">
                            <FileText className="w-3 h-3 text-slate-500" />
                            <span className="truncate">{f.filename}</span>
                          </span>
                          <span className="text-slate-500 ml-2">{formatSize(f.size)}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-slate-500 mt-2 italic">
                      Τα αρχεία εστάλησαν ως attachments στο email: j.houseofsecretary@cytanet.com.cy
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Σημειώσεις διαχειριστή</label>
                  <textarea
                    value={currentNotes}
                    onChange={(e) => setNotesDraft(prev => ({ ...prev, [r.id]: e.target.value }))}
                    rows={2}
                    placeholder="Προσθέστε εσωτερικές σημειώσεις..."
                    className="w-full bg-navy-200 border border-slate-700 text-white rounded-lg p-2 text-sm resize-none focus:border-gold focus:outline-none"
                    data-testid={`intl-notes-input-${r.id}`}
                  />
                  {notesDraft[r.id] !== undefined && notesDraft[r.id] !== (r.admin_notes || '') && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => {
                          onUpdate(r.id, { admin_notes: notesDraft[r.id] });
                          setNotesDraft(prev => {
                            const next = { ...prev };
                            delete next[r.id];
                            return next;
                          });
                        }}
                        className="px-3 py-1 text-xs bg-gold text-navy-200 font-medium rounded hover:bg-amber-400"
                        data-testid={`intl-notes-save-${r.id}`}
                      >
                        Αποθήκευση
                      </button>
                      <button
                        onClick={() => setNotesDraft(prev => {
                          const next = { ...prev };
                          delete next[r.id];
                          return next;
                        })}
                        className="px-3 py-1 text-xs text-slate-400 hover:text-white"
                      >
                        Ακύρωση
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CallbacksPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/callbacks`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('callbacks fetch', e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchItems(); }, []);

  const setStatus = async (id, status) => {
    try { await axios.patch(`${API}/admin/callbacks/${id}`, { status }); fetchItems(); }
    catch (e) { console.error(e); }
  };
  const remove = async (id) => {
    if (!window.confirm('Διαγραφή αιτήματος;')) return;
    try { await axios.delete(`${API}/admin/callbacks/${id}`); fetchItems(); }
    catch (e) { console.error(e); }
  };

  const STATUS = {
    new: { label: 'Νέο', cls: 'bg-gold/20 text-gold border-gold/40' },
    in_progress: { label: 'Σε εξέλιξη', cls: 'bg-blue-500/20 text-blue-300 border-blue-400/40' },
    done: { label: 'Ολοκληρώθηκε', cls: 'bg-green-500/20 text-green-300 border-green-400/40' },
  };
  const shown = filter === 'all' ? items : items.filter(i => i.status === filter);
  const fmt = (iso) => { try { return new Date(iso).toLocaleString('el-GR'); } catch { return iso; } };

  return (
    <div className="space-y-6" data-testid="callbacks-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <span>📞</span> Αιτήματα Κλήσης ({items.length})
        </h2>
        <div className="flex items-center gap-2">
          {['all', 'new', 'in_progress', 'done'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`callbacks-filter-${f}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${filter === f ? 'border-gold text-gold bg-gold/10' : 'border-slate-600 text-slate-400 hover:text-white'}`}>
              {f === 'all' ? 'Όλα' : STATUS[f].label}
            </button>
          ))}
          <button onClick={fetchItems} data-testid="callbacks-refresh-btn" className="px-3 py-1.5 rounded-lg text-xs border border-slate-600 text-slate-300 hover:text-white">↻</button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-4 border-gold border-t-transparent"></div></div>
      ) : shown.length === 0 ? (
        <p className="text-slate-400 text-center py-10" data-testid="callbacks-empty">Δεν υπάρχουν αιτήματα.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shown.map((c) => {
            const st = STATUS[c.status] || STATUS.new;
            return (
              <div key={c.id} className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-4 space-y-3" data-testid={`callback-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-base truncate">{c.name}</p>
                    <a href={`tel:${c.phone}`} className="text-gold text-sm font-medium hover:underline">{c.phone}</a>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${st.cls}`}>{st.label}</span>
                </div>
                {c.specialty && <p className="text-slate-300 text-sm"><span className="text-slate-500">Ειδικότητα:</span> {c.specialty}</p>}
                {c.note && <p className="text-slate-300 text-sm"><span className="text-slate-500">Σημείωση:</span> {c.note}</p>}
                <p className="text-slate-500 text-xs">{fmt(c.created_at)}</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <a href={`tel:${c.phone}`} data-testid={`callback-call-${c.id}`} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-500">📞 Κλήση</a>
                  {c.status !== 'in_progress' && <button onClick={() => setStatus(c.id, 'in_progress')} data-testid={`callback-progress-${c.id}`} className="px-3 py-1.5 rounded-lg text-xs border border-blue-400/50 text-blue-300 hover:bg-blue-500/10">Σε εξέλιξη</button>}
                  {c.status !== 'done' && <button onClick={() => setStatus(c.id, 'done')} data-testid={`callback-done-${c.id}`} className="px-3 py-1.5 rounded-lg text-xs border border-green-400/50 text-green-300 hover:bg-green-500/10">Ολοκληρώθηκε</button>}
                  <button onClick={() => remove(c.id)} data-testid={`callback-delete-${c.id}`} className="px-3 py-1.5 rounded-lg text-xs border border-red-400/50 text-red-300 hover:bg-red-500/10 ml-auto">Διαγραφή</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AdminDashboard = ({ onBack, onLogout }) => {
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerDetails, setPartnerDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editingStats, setEditingStats] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [onlineStatus, setOnlineStatus] = useState({});
  const [showAdminChat, setShowAdminChat] = useState(false);
  const [adminChatUnread, setAdminChatUnread] = useState(0);
  const [chatUnreadPerPartner, setChatUnreadPerPartner] = useState({});
  const [showPartnerChat, setShowPartnerChat] = useState(false);
  const [statsForm, setStatsForm] = useState({
    total_calls: 0,
    calls_this_month: 0,
    appointments_booked: 0,
    appointments_this_month: 0,
    missed_calls_handled: 0,
    satisfaction_rate: 0
  });
  const [savingStats, setSavingStats] = useState(false);
  
  // Payment management states
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showPrepaidModal, setShowPrepaidModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    invoice_number: '',
    description: '',
    amount: 0,
    status: 'paid',
    period: ''
  });
  const [prepaidMonths, setPrepaidMonths] = useState(12);
  const [savingPayment, setSavingPayment] = useState(false);
  
  // Pending phone verifications
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [showVerifications, setShowVerifications] = useState(false);

  // International Health Requests
  const [adminView, setAdminView] = useState('partners'); // 'partners' | 'international'
  const [intlRequests, setIntlRequests] = useState([]);
  const [intlLoading, setIntlLoading] = useState(false);
  const [intlStatusFilter, setIntlStatusFilter] = useState('all');
  const [intlNotesDraft, setIntlNotesDraft] = useState({});

  const fetchIntlRequests = async () => {
    setIntlLoading(true);
    try {
      const params = intlStatusFilter !== 'all' ? { status: intlStatusFilter } : {};
      const res = await axios.get(`${API}/admin/international-requests`, { params });
      setIntlRequests(res.data.requests || []);
    } catch (err) {
      console.error('Error fetching intl requests', err);
    } finally {
      setIntlLoading(false);
    }
  };

  useEffect(() => {
    if (adminView === 'international') {
      fetchIntlRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminView, intlStatusFilter]);

  const updateIntlRequest = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/international-requests/${id}`, patch);
      fetchIntlRequests();
    } catch (err) {
      console.error('Error updating intl request', err);
      alert('Αποτυχία ενημέρωσης αιτήματος');
    }
  };

  const deleteIntlRequest = async (id) => {
    if (!window.confirm('Διαγραφή αιτήματος; Αυτή η ενέργεια δεν αναιρείται.')) return;
    try {
      await axios.delete(`${API}/admin/international-requests/${id}`);
      fetchIntlRequests();
    } catch (err) {
      console.error('Error deleting intl request', err);
      alert('Αποτυχία διαγραφής');
    }
  };

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const response = await axios.get(`${API}/admin/partners`);
        setPartners(response.data);
      } catch (err) {
        console.error('Error fetching partners:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPartners();
  }, []);

  // Track previous unread count to detect new notifications
  const prevUnreadCountRef = useRef(0);
  
  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications/admin`);
      const newNotifications = response.data.notifications || [];
      const newUnreadCount = response.data.unread_count || 0;
      
      // Check if there are new notifications
      if (newUnreadCount > prevUnreadCountRef.current && prevUnreadCountRef.current > 0) {
        // New notification arrived - play sound and show system notification
        const latestNotification = newNotifications.find(n => !n.read);
        if (latestNotification) {
          sendNotification(latestNotification.title, latestNotification.message);
        }
      }
      
      prevUnreadCountRef.current = newUnreadCount;
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Poll for notifications every 15 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/admin/read-all`);
      fetchNotifications();
    } catch (err) {
      console.error('Error marking all read:', err);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/admin/partners`);
      setPartners(response.data);
    } catch (err) {
      console.error('Error fetching partners:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch online status
  const fetchOnlineStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/partners/online-status`);
      setOnlineStatus(response.data.online_status || {});
    } catch (err) {
      console.error('Error fetching online status:', err);
    }
  };

  // Poll online status every 30 seconds
  useEffect(() => {
    fetchOnlineStatus();
    const interval = setInterval(fetchOnlineStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Poll admin chat unread count per partner
  useEffect(() => {
    const fetchChatUnread = async () => {
      try {
        const response = await axios.get(`${API}/chat/admin/conversations`);
        const convs = response.data.conversations || [];
        const unreadMap = {};
        let total = 0;
        for (const c of convs) {
          if (c.unread_count > 0) {
            unreadMap[c.partner_username] = c.unread_count;
            total += c.unread_count;
          }
        }
        setChatUnreadPerPartner(unreadMap);
        setAdminChatUnread(total);
      } catch (err) {
        console.error('Error fetching chat unread:', err);
      }
    };
    fetchChatUnread();
    const interval = setInterval(fetchChatUnread, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchPartnerDetails = async (username) => {
    setDetailsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/partners/${username}/full`);
      setPartnerDetails(response.data);
      setSelectedPartner(username);
    } catch (err) {
      console.error('Error fetching partner details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDeletePartner = async (username) => {
    if (!window.confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε τον συνεργάτη ${username};`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/partners/${username}`);
      setPartners(partners.filter(p => p.username !== username));
      if (selectedPartner === username) {
        setSelectedPartner(null);
        setPartnerDetails(null);
      }
    } catch (err) {
      console.error('Error deleting partner:', err);
    }
  };

  const fetchPendingVerifications = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-verifications`);
      setPendingVerifications(response.data);
    } catch (err) {
      console.error('Error fetching pending verifications:', err);
    }
  };

  // Fetch pending verifications every 30 seconds
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/pending-verifications`);
        setPendingVerifications(response.data);
      } catch (err) {
        console.error('Error fetching pending verifications:', err);
      }
    };
    
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleEditStats = () => {
    if (partnerDetails?.stats) {
      setStatsForm({
        total_calls: partnerDetails.stats.total_calls || 0,
        calls_this_month: partnerDetails.stats.calls_this_month || 0,
        appointments_booked: partnerDetails.stats.appointments_booked || 0,
        appointments_this_month: partnerDetails.stats.appointments_this_month || 0,
        missed_calls_handled: partnerDetails.stats.missed_calls_handled || 0,
        satisfaction_rate: partnerDetails.stats.satisfaction_rate || 0
      });
    }
    setEditingStats(true);
  };

  const handleSaveStats = async () => {
    if (!partnerDetails?.partner?.username) {
      alert('Δεν έχει επιλεγεί συνεργάτης');
      return;
    }
    
    setSavingStats(true);
    try {
      console.log('Saving stats for:', partnerDetails.partner.username, 'Data:', statsForm);
      const response = await axios.put(`${API}/admin/partners/${partnerDetails.partner.username}/stats`, statsForm);
      console.log('Save response:', response.data);
      // Refresh partner details
      await fetchPartnerDetails(partnerDetails.partner.username);
      setEditingStats(false);
      alert('Τα στατιστικά αποθηκεύτηκαν επιτυχώς!');
    } catch (err) {
      console.error('Error saving stats:', err);
      console.error('Error response:', err.response?.data);
      alert('Σφάλμα κατά την αποθήκευση: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingStats(false);
    }
  };

  const handleAddPayment = async () => {
    if (!partnerDetails?.partner?.username) return;
    if (!paymentForm.invoice_number || !paymentForm.description) {
      alert('Συμπληρώστε τον αριθμό τιμολογίου και την περιγραφή');
      return;
    }
    
    setSavingPayment(true);
    try {
      await axios.post(`${API}/admin/partners/${partnerDetails.partner.username}/payments`, paymentForm);
      await fetchPartnerDetails(partnerDetails.partner.username);
      setShowAddPaymentModal(false);
      setPaymentForm({ invoice_number: '', description: '', amount: 0, status: 'paid', period: '' });
      alert('Η πληρωμή προστέθηκε επιτυχώς!');
    } catch (err) {
      console.error('Error adding payment:', err);
      alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingPayment(false);
    }
  };

  const handleAddPrepaidMonths = async () => {
    if (!partnerDetails?.partner?.username) return;
    
    setSavingPayment(true);
    try {
      const response = await axios.post(
        `${API}/admin/partners/${partnerDetails.partner.username}/payments/bulk-prepaid?months=${prepaidMonths}`
      );
      await fetchPartnerDetails(partnerDetails.partner.username);
      setShowPrepaidModal(false);
      setPrepaidMonths(12);
      alert(response.data.message);
    } catch (err) {
      console.error('Error adding prepaid months:', err);
      alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την πληρωμή;')) return;
    
    try {
      await axios.delete(`${API}/admin/partners/${partnerDetails.partner.username}/payments/${paymentId}`);
      await fetchPartnerDetails(partnerDetails.partner.username);
    } catch (err) {
      console.error('Error deleting payment:', err);
      alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    }
  };

  const [editingPayment, setEditingPayment] = useState(null);

  const handleSavePayment = async (paymentId) => {
    if (!editingPayment) return;
    try {
      await axios.put(`${API}/admin/partners/${partnerDetails.partner.username}/payments/${paymentId}`, editingPayment);
      setEditingPayment(null);
      await fetchPartnerDetails(partnerDetails.partner.username);
    } catch (err) {
      alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Header */}
      <header className="bg-navy-200/95 backdrop-blur-md border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 pt-12 pb-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => {
                if (selectedPartner) {
                  setSelectedPartner(null);
                  setPartnerDetails(null);
                } else {
                  onBack();
                }
              }} 
              className="flex items-center gap-2 text-slate-400 hover:text-gold transition-colors"
            >
              <ChevronDown className="w-5 h-5 rotate-90" />
              <span>Πίσω</span>
            </button>
            <div className="flex items-center gap-3">
              <img src={COMPANY.logo} alt="J.House of Secretary" className="h-10 w-auto" />
              <span className="text-gold font-semibold">ADMIN PANEL</span>
            </div>
            <div className="flex items-center gap-3">
              {/* Pending Verifications Button */}
              {pendingVerifications.length > 0 && (
                <button
                  onClick={() => setShowVerifications(!showVerifications)}
                  className="relative px-3 py-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-colors flex items-center gap-2 text-sm"
                >
                  <span>📱</span>
                  <span className="hidden sm:inline">Επαληθεύσεις</span>
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                    {pendingVerifications.length}
                  </span>
                </button>
              )}
              {/* Notifications Bell */}
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-slate-400 hover:text-gold transition-colors"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <button onClick={onLogout} className="flex items-center gap-2 text-slate-400 hover:text-red-400 transition-colors text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Αποσύνδεση</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Pending Verifications Panel */}
      {showVerifications && pendingVerifications.length > 0 && (
        <div className="bg-amber-500/10 border-b border-amber-500/30">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-amber-400 font-semibold flex items-center gap-2">
                <span>📱</span> Εκκρεμείς Επαληθεύσεις Κινητού
              </h3>
              <button onClick={() => setShowVerifications(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {pendingVerifications.map((v) => (
                <div key={v.phone_number} className="bg-navy-200/50 rounded-lg p-3 border border-amber-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">{v.phone_number}</span>
                    <span className="text-amber-400 text-xs">{v.minutes_remaining} λεπτά</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 text-sm">Κωδικός:</span>
                    <span className="text-2xl font-bold text-green-400 tracking-widest select-all">{v.otp_code}</span>
                  </div>
                  <p className="text-slate-500 text-xs mt-2">Στείλτε αυτόν τον κωδικό στον χρήστη μέσω WhatsApp/Viber</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="bg-navy-100 border-b border-gold/30">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gold font-semibold flex items-center gap-2">
                <Bell className="w-5 h-5" /> Ειδοποιήσεις
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button 
                    onClick={markAllRead}
                    className="text-slate-400 hover:text-white text-sm"
                  >
                    Σήμανση όλων ως αναγνωσμένα
                  </button>
                )}
                <button onClick={() => setShowNotifications(false)} className="text-slate-400 hover:text-white">✕</button>
              </div>
            </div>
            <div className="mb-3 pb-3 border-b border-slate-700/50">
              <PushNotificationToggle userId="admin" role="admin" />
            </div>
            {notifications.length === 0 ? (
              <p className="text-slate-500 text-center py-4">Δεν υπάρχουν ειδοποιήσεις</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {notifications.map((n) => (
                  <div 
                    key={n.id} 
                    onClick={() => !n.read && markNotificationRead(n.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      n.read 
                        ? 'bg-navy-200/30 border-slate-700/30' 
                        : 'bg-navy-200/70 border-gold/30 hover:border-gold/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${
                        n.type === 'new_registration' ? 'bg-green-500/20 text-green-400' :
                        n.type === 'payment' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-gold/20 text-gold'
                      }`}>
                        {n.type === 'new_registration' ? '👤' : 
                         n.type === 'payment' ? '💳' : '📢'}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${n.read ? 'text-slate-400' : 'text-white'}`}>{n.title}</p>
                        <p className={`text-sm ${n.read ? 'text-slate-500' : 'text-slate-300'}`}>{n.message}</p>
                        <p className="text-xs text-slate-600 mt-1">
                          {new Date(n.created_at).toLocaleString('el-GR')}
                        </p>
                      </div>
                      {!n.read && (
                        <div className="w-2 h-2 bg-gold rounded-full"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* View toggle */}
        <div className="flex gap-2 mb-6 border-b border-slate-800 overflow-x-auto">
          <button
            onClick={() => setAdminView('partners')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              adminView === 'partners'
                ? 'border-gold text-gold'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="admin-view-partners-btn"
          >
            <Users className="w-4 h-4" />
            Συνεργάτες
          </button>
          <button
            onClick={() => setAdminView('international')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              adminView === 'international'
                ? 'border-gold text-gold'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="admin-view-international-btn"
          >
            <Globe className="w-4 h-4" />
            Διεθνή Αιτήματα
            {intlRequests.filter(r => r.status === 'new').length > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {intlRequests.filter(r => r.status === 'new').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setAdminView('pending-transfers')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              adminView === 'pending-transfers'
                ? 'border-gold text-gold'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="admin-view-transfers-btn"
          >
            <Building2 className="w-4 h-4" />
            Εκκρεμείς Μεταφορές
          </button>
          <button
            onClick={() => setAdminView('bank-settings')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              adminView === 'bank-settings'
                ? 'border-gold text-gold'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="admin-view-bank-settings-btn"
          >
            <Settings className="w-4 h-4" />
            Τραπεζικά Στοιχεία
          </button>
          <button
            onClick={() => setAdminView('partners-management')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              adminView === 'partners-management'
                ? 'border-gold text-gold'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="admin-view-partners-mgmt-btn"
          >
            <Image className="w-4 h-4" />
            Logos Συνεργατών
          </button>
          <button
            onClick={() => setAdminView('visitor-chats')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              adminView === 'visitor-chats'
                ? 'border-gold text-gold'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="admin-view-visitor-chats-btn"
          >
            <span>💬</span>
            Ζωντανές Συνομιλίες
          </button>
          <button
            onClick={() => setAdminView('line-collaborators')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              adminView === 'line-collaborators'
                ? 'border-gold text-gold'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="admin-view-line-collaborators-btn"
          >
            <span>📋</span>
            Συνεργάτες Γραμμής
          </button>
          <button
            onClick={() => setAdminView('doctor-cards')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              adminView === 'doctor-cards'
                ? 'border-gold text-gold'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="admin-view-doctor-cards-btn"
          >
            <span>🪪</span>
            Κάρτες Γιατρών
          </button>
          <button
            onClick={() => setAdminView('quick-replies')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              adminView === 'quick-replies'
                ? 'border-gold text-gold'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="admin-view-quick-replies-btn"
          >
            <span>💬</span>
            Αυτόματα Μηνύματα
          </button>
          <button
            onClick={() => setAdminView('callbacks')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              adminView === 'callbacks'
                ? 'border-gold text-gold'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="admin-view-callbacks-btn"
          >
            <span>📞</span>
            Αιτήματα Κλήσης
          </button>
        </div>

        {adminView === 'callbacks' ? (
          <CallbacksPanel />
        ) : adminView === 'international' ? (
          <InternationalRequestsPanel
            requests={intlRequests}
            loading={intlLoading}
            statusFilter={intlStatusFilter}
            setStatusFilter={setIntlStatusFilter}
            onUpdate={updateIntlRequest}
            onDelete={deleteIntlRequest}
            notesDraft={intlNotesDraft}
            setNotesDraft={setIntlNotesDraft}
            onRefresh={fetchIntlRequests}
          />
        ) : adminView === 'pending-transfers' ? (
          <PendingTransfersPanel />
        ) : adminView === 'bank-settings' ? (
          <BankSettingsPanel />
        ) : adminView === 'partners-management' ? (
          <PartnersManagementPanel />
        ) : adminView === 'visitor-chats' ? (
          <div className="space-y-6">
            <AgentsManagementPanel />
            <VisitorChatsPanel />
          </div>
        ) : adminView === 'line-collaborators' ? (
          <LineCollaboratorsPanel />
        ) : adminView === 'doctor-cards' ? (
          <DoctorCardStudio />
        ) : adminView === 'quick-replies' ? (
          <QuickRepliesPanel />
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Partners List */}
          <div className="lg:col-span-1">
            <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700 bg-navy-200/50">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span>👥</span> Συνεργάτες ({partners.length})
                </h2>
              </div>
              
              {loading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-gold border-t-transparent"></div>
                </div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto">
                  {partners.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">Δεν υπάρχουν συνεργάτες</p>
                  ) : (
                    partners.map((partner) => (
                      <button
                        key={partner.id}
                        onClick={() => {
                          fetchPartnerDetails(partner.username);
                          // Auto-open chat if partner has unread messages
                          if (chatUnreadPerPartner[partner.username] > 0) {
                            setShowPartnerChat(true);
                          }
                          // Scroll to details on mobile
                          setTimeout(() => {
                            const detailsEl = document.getElementById('partner-details');
                            if (detailsEl && window.innerWidth < 1024) {
                              detailsEl.scrollIntoView({ behavior: 'smooth' });
                            }
                          }, 300);
                        }}
                        className={`w-full text-left p-4 border-b border-slate-700/50 cursor-pointer transition-all ${
                          selectedPartner === partner.username
                            ? 'bg-gold/10 border-l-4 border-l-gold'
                            : 'hover:bg-navy-200/50 active:bg-navy-200/70'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {/* Online Status Indicator */}
                            <div className={`w-3 h-3 rounded-full ${
                              onlineStatus[partner.username] 
                                ? 'bg-green-500 animate-pulse shadow-lg shadow-green-500/50' 
                                : 'bg-slate-600'
                            }`} title={onlineStatus[partner.username] ? 'Online' : 'Offline'} />
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-white font-medium">{partner.name}</p>
                                {chatUnreadPerPartner[partner.username] > 0 && (
                                  <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gold/20 border border-gold/40 rounded-full text-[10px] text-gold font-bold animate-pulse" data-testid={`chat-badge-${partner.username}`}>
                                    <MessageCircle className="w-3 h-3" />
                                    {chatUnreadPerPartner[partner.username]}
                                  </span>
                                )}
                              </div>
                              <p className="text-slate-400 text-sm">@{partner.username}</p>
                              <p className="text-slate-500 text-xs">{partner.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              partner.partner_type === 'full'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              {partner.partner_type === 'full' ? 'Πλήρης' : 'Patient Line'}
                            </span>
                            <p className={`text-xs mt-1 ${
                              onlineStatus[partner.username] 
                                ? 'text-green-400' 
                                : 'text-slate-500'
                            }`}>
                              {onlineStatus[partner.username] ? '● Online' : '● Offline'}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Partner Details */}
          <div id="partner-details" className="lg:col-span-2">
            {!selectedPartner ? (
              <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-8 text-center">
                <div className="text-6xl mb-4">👆</div>
                <p className="text-slate-400">Επιλέξτε έναν συνεργάτη για να δείτε τα στοιχεία του</p>
              </div>
            ) : detailsLoading ? (
              <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-8 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-gold border-t-transparent"></div>
              </div>
            ) : partnerDetails ? (
              <div className="space-y-6">
                {/* Partner Info */}
                <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      {partnerDetails.partner.logo ? (
                        <img src={partnerDetails.partner.logo} alt={partnerDetails.partner.name} className="h-16 w-16 rounded-lg object-cover border border-gold/30" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-gold/20 flex items-center justify-center">
                          <span className="text-2xl font-playfair text-gold">{partnerDetails.partner.name?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-semibold text-white">{partnerDetails.partner.name}</h2>
                          {/* Online Status Badge */}
                          <span className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 ${
                            onlineStatus[partnerDetails.partner.username]
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-slate-700 text-slate-400'
                          }`}>
                            <span className={`w-2 h-2 rounded-full ${
                              onlineStatus[partnerDetails.partner.username]
                                ? 'bg-green-500 animate-pulse'
                                : 'bg-slate-500'
                            }`}></span>
                            {onlineStatus[partnerDetails.partner.username] ? 'Online' : 'Offline'}
                          </span>
                        </div>
                        <p className="text-slate-400">@{partnerDetails.partner.username}</p>
                        <p className="text-slate-500 text-sm">{partnerDetails.partner.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeletePartner(partnerDetails.partner.username)}
                      className="px-3 py-1 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
                    >
                      Διαγραφή
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-slate-400 text-sm">Εταιρεία</p>
                      <p className="text-white">{partnerDetails.partner.company || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Τύπος</p>
                      <p className="text-white">{partnerDetails.partner.partner_type === 'full' ? 'Πλήρης' : 'Patient Line'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Συνδρομή</p>
                      <button 
                        onClick={async () => {
                          try {
                            const newStatus = !partnerDetails.partner.subscription_active;
                            await axios.put(`${API}/admin/partners/${partnerDetails.partner.username}/subscription?active=${newStatus}`);
                            fetchPartnerDetails(partnerDetails.partner.username);
                          } catch (err) {
                            console.error('Error toggling subscription:', err);
                          }
                        }}
                        className={`px-3 py-1 rounded-lg transition-colors font-medium ${
                          partnerDetails.partner.subscription_active 
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        }`}
                      >
                        {partnerDetails.partner.subscription_active ? '✓ ΕΝΕΡΓΗ' : '✗ ΑΝΕΝΕΡΓΗ'}
                      </button>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Εγγραφή</p>
                      <p className="text-white">{partnerDetails.partner.created_at ? new Date(partnerDetails.partner.created_at).toLocaleDateString('el-GR') : '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <span>📊</span> Στατιστικά
                    </h3>
                    {!editingStats ? (
                      <button
                        onClick={handleEditStats}
                        className="px-3 py-1.5 bg-gold/20 text-gold rounded-lg hover:bg-gold/30 transition-colors text-sm flex items-center gap-1"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Επεξεργασία
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingStats(false)}
                          className="px-3 py-1.5 bg-slate-500/20 text-slate-400 rounded-lg hover:bg-slate-500/30 transition-colors text-sm"
                        >
                          Ακύρωση
                        </button>
                        <button
                          onClick={handleSaveStats}
                          disabled={savingStats}
                          className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-sm flex items-center gap-1 disabled:opacity-50"
                        >
                          {savingStats ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-400 border-t-transparent"></div>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          Αποθήκευση
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {!editingStats ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-navy-200/50 rounded-lg p-4">
                        <p className="text-slate-400 text-sm">Σύνολο Κλήσεων</p>
                        <p className="text-2xl font-bold text-white">{partnerDetails.stats?.total_calls || 0}</p>
                      </div>
                      <div className="bg-navy-200/50 rounded-lg p-4">
                        <p className="text-slate-400 text-sm">Κλήσεις Μήνα</p>
                        <p className="text-2xl font-bold text-white">{partnerDetails.stats?.calls_this_month || 0}</p>
                      </div>
                      <div className="bg-navy-200/50 rounded-lg p-4">
                        <p className="text-slate-400 text-sm">Ραντεβού</p>
                        <p className="text-2xl font-bold text-white">{partnerDetails.stats?.appointments_booked || 0}</p>
                      </div>
                      <div className="bg-navy-200/50 rounded-lg p-4">
                        <p className="text-slate-400 text-sm">Ικανοποίηση</p>
                        <p className="text-2xl font-bold text-green-400">{partnerDetails.stats?.satisfaction_rate || 0}%</p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="bg-navy-200/50 rounded-lg p-4">
                        <label className="text-slate-400 text-sm block mb-2">Σύνολο Κλήσεων</label>
                        <input
                          type="number"
                          min="0"
                          value={statsForm.total_calls}
                          onChange={(e) => setStatsForm({...statsForm, total_calls: parseInt(e.target.value) || 0})}
                          className="w-full bg-navy-100 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                        />
                      </div>
                      <div className="bg-navy-200/50 rounded-lg p-4">
                        <label className="text-slate-400 text-sm block mb-2">Κλήσεις Μήνα</label>
                        <input
                          type="number"
                          min="0"
                          value={statsForm.calls_this_month}
                          onChange={(e) => setStatsForm({...statsForm, calls_this_month: parseInt(e.target.value) || 0})}
                          className="w-full bg-navy-100 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                        />
                      </div>
                      <div className="bg-navy-200/50 rounded-lg p-4">
                        <label className="text-slate-400 text-sm block mb-2">Σύνολο Ραντεβού</label>
                        <input
                          type="number"
                          min="0"
                          value={statsForm.appointments_booked}
                          onChange={(e) => setStatsForm({...statsForm, appointments_booked: parseInt(e.target.value) || 0})}
                          className="w-full bg-navy-100 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                        />
                      </div>
                      <div className="bg-navy-200/50 rounded-lg p-4">
                        <label className="text-slate-400 text-sm block mb-2">Ραντεβού Μήνα</label>
                        <input
                          type="number"
                          min="0"
                          value={statsForm.appointments_this_month}
                          onChange={(e) => setStatsForm({...statsForm, appointments_this_month: parseInt(e.target.value) || 0})}
                          className="w-full bg-navy-100 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                        />
                      </div>
                      <div className="bg-navy-200/50 rounded-lg p-4">
                        <label className="text-slate-400 text-sm block mb-2">Χαμένες Κλήσεις</label>
                        <input
                          type="number"
                          min="0"
                          value={statsForm.missed_calls_handled}
                          onChange={(e) => setStatsForm({...statsForm, missed_calls_handled: parseInt(e.target.value) || 0})}
                          className="w-full bg-navy-100 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                        />
                      </div>
                      <div className="bg-navy-200/50 rounded-lg p-4">
                        <label className="text-slate-400 text-sm block mb-2">Ικανοποίηση (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={statsForm.satisfaction_rate}
                          onChange={(e) => setStatsForm({...statsForm, satisfaction_rate: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))})}
                          className="w-full bg-navy-100 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Payments */}
                <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <span>💳</span> Πληρωμές
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowPrepaidModal(true)}
                        className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-sm flex items-center gap-1"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Ετήσια Προπληρωμή
                      </button>
                      <button
                        onClick={() => setShowAddPaymentModal(true)}
                        className="px-3 py-1.5 bg-gold/20 text-gold rounded-lg hover:bg-gold/30 transition-colors text-sm flex items-center gap-1"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Προσθήκη
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <p className="text-slate-400 text-sm">Εξοφλημένα</p>
                      <p className="text-2xl font-bold text-green-400">€{partnerDetails.total_paid?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                      <p className="text-slate-400 text-sm">Εκκρεμή</p>
                      <p className="text-2xl font-bold text-amber-400">€{partnerDetails.total_pending?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left text-slate-400 text-sm py-2">Τιμολόγιο</th>
                          <th className="text-left text-slate-400 text-sm py-2">Περιγραφή</th>
                          <th className="text-right text-slate-400 text-sm py-2">Ποσό</th>
                          <th className="text-center text-slate-400 text-sm py-2">Κατάσταση</th>
                          <th className="text-center text-slate-400 text-sm py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerDetails.payments?.length === 0 && (
                          <tr>
                            <td colSpan="5" className="py-6 text-center text-slate-500">
                              Δεν υπάρχουν πληρωμές
                            </td>
                          </tr>
                        )}
                        {partnerDetails.payments?.map((payment) => (
                          <tr key={payment.id || payment.invoice_number} className="border-b border-slate-700/50">
                            {editingPayment && editingPayment._editId === payment.id ? (
                              <>
                                <td className="py-3">
                                  <input type="text" value={editingPayment.invoice_number || ''} onChange={(e) => setEditingPayment({...editingPayment, invoice_number: e.target.value})} className="w-full px-2 py-1 bg-navy-100/50 border border-slate-600 rounded text-white text-sm" />
                                </td>
                                <td className="py-3">
                                  <input type="text" value={editingPayment.description || ''} onChange={(e) => setEditingPayment({...editingPayment, description: e.target.value})} className="w-full px-2 py-1 bg-navy-100/50 border border-slate-600 rounded text-white text-sm" />
                                </td>
                                <td className="py-3">
                                  <input type="number" step="0.01" value={editingPayment.amount || 0} onChange={(e) => setEditingPayment({...editingPayment, amount: parseFloat(e.target.value) || 0})} className="w-24 px-2 py-1 bg-navy-100/50 border border-slate-600 rounded text-white text-sm text-right" />
                                </td>
                                <td className="py-3 text-center">
                                  <select value={editingPayment.status || 'pending'} onChange={(e) => setEditingPayment({...editingPayment, status: e.target.value})} className="px-2 py-1 bg-navy-100/50 border border-slate-600 rounded text-white text-sm">
                                    <option value="paid">Εξοφλημένο</option>
                                    <option value="pending">Εκκρεμεί</option>
                                  </select>
                                </td>
                                <td className="py-3 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button onClick={() => handleSavePayment(payment.id)} className="px-2 py-1 bg-gold text-navy-200 text-xs font-medium rounded hover:bg-amber-500">Αποθ.</button>
                                    <button onClick={() => setEditingPayment(null)} className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded hover:bg-slate-600">Ακύρ.</button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="py-3 text-white">{payment.invoice_number}</td>
                                <td className="py-3 text-slate-300 text-sm">
                                  {payment.description}
                                  {payment.prepaid && (
                                    <span className="ml-2 px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                                      Προπληρωμή
                                    </span>
                                  )}
                                </td>
                                <td className="py-3 text-right text-white">
                                  {payment.prepaid ? (
                                    <span className="text-green-400">Καλύπτεται</span>
                                  ) : (
                                    `€${payment.amount?.toFixed(2)}`
                                  )}
                                </td>
                                <td className="py-3 text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs ${
                                    payment.status === 'paid'
                                      ? 'bg-green-500/20 text-green-400'
                                      : 'bg-amber-500/20 text-amber-400'
                                  }`}>
                                    {payment.status === 'paid' ? 'Εξοφλημένο' : 'Εκκρεμεί'}
                                  </span>
                                </td>
                                <td className="py-3 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => setEditingPayment({...payment, _editId: payment.id})}
                                      className="w-7 h-7 rounded bg-slate-700/50 hover:bg-slate-600 flex items-center justify-center text-slate-400 hover:text-gold transition-colors"
                                      title="Επεξεργασία"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeletePayment(payment.id)}
                                      className="w-7 h-7 rounded bg-slate-700/50 hover:bg-red-500/30 flex items-center justify-center text-slate-400 hover:text-red-400 transition-colors"
                                      title="Διαγραφή"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Upload Data Section */}
                <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <span>📤</span> Ανέβασμα Δεδομένων
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Upload 3CX Data */}
                    <div className="bg-navy-200/50 rounded-lg p-4">
                      <h4 className="text-white font-medium mb-3">📞 Δεδομένα 3CX (Κλήσεις)</h4>
                      <input 
                        type="file" 
                        accept=".csv"
                        id="3cx-upload"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const month = prompt('Εισάγετε τον μήνα (π.χ. 2026-04):');
                          if (!month) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('partner_username', partnerDetails.partner.username);
                          formData.append('month', month);
                          try {
                            await axios.post(`${API}/admin/upload-3cx-data`, formData);
                            alert('Τα δεδομένα ανέβηκαν επιτυχώς!');
                            fetchPartnerDetails(partnerDetails.partner.username);
                          } catch (err) {
                            alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
                          }
                        }}
                      />
                      <label htmlFor="3cx-upload" className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors">
                        <span>📁</span> Επιλογή CSV
                      </label>
                    </div>

                    {/* Upload Patient Visits */}
                    <div className="bg-navy-200/50 rounded-lg p-4">
                      <h4 className="text-white font-medium mb-3">👥 Επισκέψεις Ασθενών</h4>
                      <input 
                        type="file" 
                        accept=".csv"
                        id="visits-upload"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('partner_username', partnerDetails.partner.username);
                          try {
                            const res = await axios.post(`${API}/admin/patient-visits/bulk`, formData);
                            alert(`Προστέθηκαν ${res.data.total} επισκέψεις!`);
                            fetchPartnerDetails(partnerDetails.partner.username);
                          } catch (err) {
                            alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
                          }
                        }}
                      />
                      <label htmlFor="visits-upload" className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors">
                        <span>📁</span> Επιλογή CSV
                      </label>
                    </div>
                  </div>

                  {/* Quick Add Patient Visit */}
                  <div className="mt-4 p-4 bg-navy-200/30 rounded-lg border border-slate-700/50">
                    <h4 className="text-white font-medium mb-3">➕ Γρήγορη Προσθήκη Επίσκεψης</h4>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      try {
                        await axios.post(`${API}/admin/patient-visits`, {
                          partner_username: partnerDetails.partner.username,
                          patient_name: formData.get('patient_name'),
                          appointment_date: formData.get('date'),
                          appointment_time: formData.get('time'),
                          attended: formData.get('attended') === 'true'
                        });
                        alert('Η επίσκεψη προστέθηκε!');
                        e.target.reset();
                        fetchPartnerDetails(partnerDetails.partner.username);
                      } catch (err) {
                        alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
                      }
                    }} className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <input name="patient_name" type="text" placeholder="Ονοματεπώνυμο ασθενή" className="px-3 py-2 bg-navy-200/50 border border-slate-700 rounded-lg text-white text-sm" required />
                      <input name="date" type="date" className="px-3 py-2 bg-navy-200/50 border border-slate-700 rounded-lg text-white text-sm" required />
                      <input name="time" type="time" className="px-3 py-2 bg-navy-200/50 border border-slate-700 rounded-lg text-white text-sm" />
                      <select name="attended" className="px-3 py-2 bg-navy-200/50 border border-slate-700 rounded-lg text-white text-sm">
                        <option value="true">✓ Εμφανίστηκε</option>
                        <option value="false">✗ Δεν εμφανίστηκε</option>
                      </select>
                      <button type="submit" className="px-4 py-2 bg-gold text-navy-200 font-medium rounded-lg hover:bg-amber-500 transition-colors">
                        Προσθήκη
                      </button>
                    </form>
                  </div>
                </div>

                {/* Monthly Folders Section */}
                <MonthlyFoldersAdmin username={partnerDetails.partner.username} partnerName={partnerDetails.partner.name} />

                {/* Chat with Partner - Collapsible */}
                <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 overflow-hidden">
                  <button 
                    onClick={() => setShowPartnerChat(!showPartnerChat)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-navy-200/30 transition-colors"
                    data-testid="chat-toggle-section"
                  >
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-gold" />
                      <span className="text-white font-medium text-sm">Μηνύματα</span>
                      {chatUnreadPerPartner[partnerDetails.partner.username] > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                          {chatUnreadPerPartner[partnerDetails.partner.username]}
                        </span>
                      )}
                    </div>
                    <svg className={`w-4 h-4 text-slate-400 transition-transform ${showPartnerChat ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showPartnerChat && (
                    <ChatWindow
                      currentUser="admin"
                      currentRole="admin"
                      partnerUsername={partnerDetails.partner.username}
                      partnerName={partnerDetails.partner.name}
                    />
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        )}
      </main>

      {/* Add Payment Modal */}
      {showAddPaymentModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-navy-200 rounded-2xl border border-gold/30 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Προσθήκη Πληρωμής</h3>
              <button onClick={() => setShowAddPaymentModal(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-slate-400 text-sm block mb-1">Αριθμός Τιμολογίου *</label>
                <input
                  type="text"
                  value={paymentForm.invoice_number}
                  onChange={(e) => setPaymentForm({...paymentForm, invoice_number: e.target.value})}
                  placeholder="π.χ. INV-2026-001"
                  className="w-full bg-navy-100 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="text-slate-400 text-sm block mb-1">Περιγραφή *</label>
                <input
                  type="text"
                  value={paymentForm.description}
                  onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})}
                  placeholder="π.χ. Υπηρεσίες Μαρτίου 2026"
                  className="w-full bg-navy-100 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="text-slate-400 text-sm block mb-1">Περίοδος</label>
                <input
                  type="text"
                  value={paymentForm.period}
                  onChange={(e) => setPaymentForm({...paymentForm, period: e.target.value})}
                  placeholder="π.χ. Μάρτιος 2026"
                  className="w-full bg-navy-100 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-400 text-sm block mb-1">Ποσό (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value) || 0})}
                    className="w-full bg-navy-100 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-sm block mb-1">Κατάσταση</label>
                  <select
                    value={paymentForm.status}
                    onChange={(e) => setPaymentForm({...paymentForm, status: e.target.value})}
                    className="w-full bg-navy-100 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-gold focus:outline-none"
                  >
                    <option value="paid">Εξοφλημένο</option>
                    <option value="pending">Εκκρεμεί</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddPaymentModal(false)}
                className="flex-1 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-500"
              >
                Ακύρωση
              </button>
              <button
                onClick={handleAddPayment}
                disabled={savingPayment}
                className="flex-1 py-2 bg-gold text-navy-200 font-semibold rounded-lg hover:bg-gold/90 disabled:opacity-50"
              >
                {savingPayment ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prepaid Months Modal */}
      {showPrepaidModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-navy-200 rounded-2xl border border-green-500/30 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Ετήσια Προπληρωμή</h3>
              <button onClick={() => setShowPrepaidModal(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>
            
            <p className="text-slate-400 text-sm mb-4">
              Προσθέστε μήνες προπληρωμένης υπηρεσίας για τον συνεργάτη <span className="text-white font-medium">{partnerDetails?.partner?.name}</span>. 
              Οι μήνες θα εμφανίζονται ως "Εξοφλημένοι" στο ιστορικό πληρωμών.
            </p>
            
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4">
              <label className="text-slate-300 text-sm block mb-2">Αριθμός μηνών προπληρωμής</label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="1"
                  max="24"
                  value={prepaidMonths}
                  onChange={(e) => setPrepaidMonths(parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className="text-2xl font-bold text-green-400 w-12 text-center">{prepaidMonths}</span>
              </div>
              <p className="text-slate-500 text-xs mt-2">
                {prepaidMonths === 12 ? '1 έτος' : prepaidMonths === 24 ? '2 έτη' : `${prepaidMonths} μήνες`}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowPrepaidModal(false)}
                className="flex-1 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-500"
              >
                Ακύρωση
              </button>
              <button
                onClick={handleAddPrepaidMonths}
                disabled={savingPayment}
                className="flex-1 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-400 disabled:opacity-50"
              >
                {savingPayment ? 'Προσθήκη...' : `Προσθήκη ${prepaidMonths} μηνών`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Partner Dashboard Component with Stats, Reports, Payments
const PartnerDashboard = ({ user, onBack, onLogout, getGreeting, paymentSuccess }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [payments, setPayments] = useState({ payments: [], summary: {} });
  const [visits, setVisits] = useState({ visits: [], summary: {} });
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentSuccessAlert, setShowPaymentSuccessAlert] = useState(paymentSuccess);
  const [showSecretaryModal, setShowSecretaryModal] = useState(false);
  const [selectedCallPackage, setSelectedCallPackage] = useState(null);
  const [addEmailService, setAddEmailService] = useState(false);
  const [secretaryPaymentLoading, setSecretaryPaymentLoading] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [add24x7Service, setAdd24x7Service] = useState(false);
  const [showContactConfirmation, setShowContactConfirmation] = useState(false);
  const [premiumPaymentLoading, setPremiumPaymentLoading] = useState(false);
  const [showPatientLineModal, setShowPatientLineModal] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(0);
  const [patientLinePaymentLoading, setPatientLinePaymentLoading] = useState(false);
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Payment method selection modal
  const [paymentModalConfig, setPaymentModalConfig] = useState(null); // null | { package_id, custom_config, amount, packageName, onCardPayment }
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Annual subscription option - 10% discount
  const [isAnnualPackage, setIsAnnualPackage] = useState(false);
  const [isAnnualPremium, setIsAnnualPremium] = useState(false);
  
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(user?.name || '');
  const [editEmail, setEditEmail] = useState(user?.email || '');
  const [editCompany, setEditCompany] = useState(user?.company || '');
  const [editPassword, setEditPassword] = useState('');
  const [editConfirmPassword, setEditConfirmPassword] = useState('');
  const [profileUpdateLoading, setProfileUpdateLoading] = useState(false);
  const [profileUpdateSuccess, setProfileUpdateSuccess] = useState('');
  const [profileUpdateError, setProfileUpdateError] = useState('');

  // PWA Install prompt
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show install banner if not already installed
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowInstallBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  // Handle profile update
  const handleProfileUpdate = async () => {
    setProfileUpdateError('');
    setProfileUpdateSuccess('');
    
    // Validate passwords if provided
    if (editPassword && editPassword !== editConfirmPassword) {
      setProfileUpdateError('Οι κωδικοί δεν ταιριάζουν');
      return;
    }
    
    if (editPassword && editPassword.length < 6) {
      setProfileUpdateError('Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες');
      return;
    }

    setProfileUpdateLoading(true);
    try {
      const updateData = {
        name: editName,
        email: editEmail,
        company: editCompany
      };
      
      if (editPassword) {
        updateData.password = editPassword;
      }

      const response = await axios.put(`${API}/partners/${user.username}/profile`, updateData);
      
      if (response.data.success) {
        // Update local storage with new data
        const updatedUser = { ...user, name: editName, email: editEmail, company: editCompany };
        localStorage.setItem('jhouse_partner_user', JSON.stringify(updatedUser));
        
        setProfileUpdateSuccess('Το προφίλ ενημερώθηκε επιτυχώς!');
        setIsEditingProfile(false);
        setEditPassword('');
        setEditConfirmPassword('');
        
        // Reload page to refresh user data
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (err) {
      setProfileUpdateError(err.response?.data?.detail || 'Σφάλμα κατά την ενημέρωση');
    } finally {
      setProfileUpdateLoading(false);
    }
  };

  const patientLineRegistration = 150;
  const patientLineMonthlyPrice = 100;

  // Calculate Patient Line total
  const calculatePatientLineTotal = () => {
    // Registration fee + (selected months * monthly price)
    // Note: 1st month is FREE, so if selectedMonths = 0, only registration is charged
    const monthsCharge = selectedMonths * patientLineMonthlyPrice;
    const subtotal = patientLineRegistration + monthsCharge;
    const transactionFee = (subtotal * 0.034) + 0.30;
    return { 
      registration: patientLineRegistration,
      monthsCharge,
      subtotal, 
      fee: transactionFee, 
      total: subtotal + transactionFee 
    };
  };

  // Call packages data (without hospital 24/7 - that's now Premium)
  const callPackages = [
    { id: 'calls_500', name: '0-500 κλήσεις', calls: '0-500', price: 270 },
    { id: 'calls_1000', name: '500-1000 κλήσεις', calls: '500-1000', price: 450 },
    { id: 'calls_1500', name: '1000-1500 κλήσεις', calls: '1000-1500', price: 650 },
    { id: 'calls_2000', name: '1500-2000 κλήσεις', calls: '1500-2000', price: 800 },
    { id: 'calls_2500', name: '2000-2500 κλήσεις', calls: '2000-2500', price: 1000 }
  ];

  // Track previous unread count to detect new notifications
  const prevUnreadCountRef = useRef(0);

  // Fetch notifications for partner
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications/${user.username}`);
      const newNotifications = response.data.notifications || [];
      const newUnreadCount = response.data.unread_count || 0;
      
      // Check if there are new notifications
      if (newUnreadCount > prevUnreadCountRef.current && prevUnreadCountRef.current > 0) {
        // New notification arrived - play sound and show system notification
        const latestNotification = newNotifications.find(n => !n.read);
        if (latestNotification) {
          sendNotification(latestNotification.title, latestNotification.message);
        }
      }
      
      prevUnreadCountRef.current = newUnreadCount;
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Poll for notifications every 15 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [user.username]);

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/${user.username}/read-all`);
      fetchNotifications();
    } catch (err) {
      console.error('Error marking all read:', err);
    }
  };

  // Update activity status every 2 minutes
  useEffect(() => {
    const updateActivity = async () => {
      try {
        await axios.post(`${API}/partners/${user.username}/activity`);
      } catch (err) {
        console.error('Error updating activity:', err);
      }
    };
    
    updateActivity(); // Update immediately
    const interval = setInterval(updateActivity, 120000); // Every 2 minutes
    return () => clearInterval(interval);
  }, [user.username]);

  const registrationFee = 150;
  const premiumRegistrationFee = 300;
  const emailServicePrice = 200;

  // Calculate total
  const calculateTotal = () => {
    let monthlyServices = 0;
    let registrationTotal = registrationFee;
    
    if (selectedCallPackage) {
      const pkg = callPackages.find(p => p.id === selectedCallPackage);
      monthlyServices += pkg?.price || 0;
    }
    if (addEmailService) {
      monthlyServices += emailServicePrice;
    }
    
    // If annual package, multiply monthly by 12 and apply 10% discount
    let servicesTotal = monthlyServices;
    let discount = 0;
    if (isAnnualPackage && monthlyServices > 0) {
      const yearlyTotal = monthlyServices * 12;
      discount = yearlyTotal * 0.10; // 10% discount
      servicesTotal = yearlyTotal - discount;
    }
    
    const total = registrationTotal + servicesTotal;
    // Add transaction fee (3.4% + €0.30)
    const transactionFee = (total * 0.034) + 0.30;
    return { 
      subtotal: total, 
      fee: transactionFee, 
      total: total + transactionFee,
      monthly: monthlyServices,
      discount: discount,
      isAnnual: isAnnualPackage
    };
  };

  // Auto-hide payment success alert after 5 seconds
  useEffect(() => {
    if (showPaymentSuccessAlert) {
      const timer = setTimeout(() => setShowPaymentSuccessAlert(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showPaymentSuccessAlert]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requests = [
          axios.get(`${API}/partners/${user.username}/stats`),
          axios.get(`${API}/partners/${user.username}/reports`),
          axios.get(`${API}/partners/${user.username}/payments`),
          axios.get(`${API}/partners/${user.username}/payment-history`)
        ];
        
        // Fetch visits for patient_line partners
        if (user.partner_type === 'patient_line' || user.partner_type === 'full') {
          requests.push(axios.get(`${API}/partners/${user.username}/visits`));
        }
        
        const responses = await Promise.all(requests);
        // Only set stats if they exist and have real data
        const statsData = responses[0].data;
        if (statsData && !statsData.detail) {
          setStats(statsData);
        } else {
          // Set empty stats
          setStats({
            total_calls: 0,
            calls_this_month: 0,
            appointments_booked: 0,
            appointments_this_month: 0,
            satisfaction_rate: 0
          });
        }
        setReports(responses[1].data);
        setPayments(responses[2].data);
        setPaymentHistory(responses[3].data || []);
        if (responses[4]) {
          setVisits(responses[4].data);
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        // Set empty stats on error
        setStats({
          total_calls: 0,
          calls_this_month: 0,
          appointments_booked: 0,
          appointments_this_month: 0,
          satisfaction_rate: 0
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user.username, user.partner_type]);

  // Tabs based on partner type
  const baseTabs = [
    { id: 'overview', label: 'Επισκόπηση', icon: 'overview' },
    { id: 'services', label: 'Υπηρεσίες', icon: 'services' },
    { id: 'folders', label: 'Μηνιαίοι Φάκελοι', icon: 'folders' },
    { id: 'visits', label: 'Επισκέψεις Ασθενών', icon: 'visits' },
    { id: 'stats', label: 'Στατιστικά', icon: 'stats' },
    { id: 'reports', label: 'Αναφορές', icon: 'reports' },
    { id: 'payments', label: 'Πληρωμές', icon: 'payments' },
    { id: 'chat', label: 'Μηνύματα', icon: 'chat' },
    { id: 'profile', label: 'Προφίλ', icon: 'profile' }
  ];

  // Render gold icons
  const renderTabIcon = (iconType) => {
    const iconClass = "w-5 h-5 text-gold";
    switch(iconType) {
      case 'overview':
        return <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>;
      case 'services':
        return <img src="/icon-services.png" alt="Υπηρεσίες" className="w-8 h-8 rounded-full" />;
      case 'folders':
        return <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24"><path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/></svg>;
      case 'visits':
        return <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>;
      case 'stats':
        return <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24"><path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/></svg>;
      case 'reports':
        return <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>;
      case 'payments':
        return <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24"><path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/></svg>;
      case 'chat':
        return <MessageCircle className={iconClass} />;
      case 'international':
        return <Globe className={iconClass} />;
      case 'profile':
        return <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24"><path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg>;
      default:
        return null;
    }
  };
  
  const tabs = baseTabs;

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Header */}
      <header className="bg-navy-200/95 backdrop-blur-md border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 pt-12 pb-4">
          <div className="flex items-center justify-between">
            <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-gold transition-colors">
              <ChevronDown className="w-5 h-5 rotate-90" />
              <span>Πίσω</span>
            </button>
            <img src={COMPANY.logo} alt="J.House of Secretary" className="h-10 w-auto" />
            <div className="flex items-center gap-3">
              {/* Notifications Bell */}
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-slate-400 hover:text-gold transition-colors"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <button onClick={onLogout} className="flex items-center gap-2 text-slate-400 hover:text-red-400 transition-colors text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Αποσύνδεση</span>
              </button>
            </div>
          </div>
          
          {/* Welcome Bar */}
          <div className="flex items-center gap-4 mt-4 pb-2">
            {user.logo ? (
              <img src={user.logo} alt={user.name} className="h-14 w-auto rounded-lg border border-gold/30" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-gold/20 flex items-center justify-center">
                <span className="text-2xl font-playfair text-gold">{user.name?.charAt(0)}</span>
              </div>
            )}
            <div>
              <p className="text-gold text-sm">{getGreeting()}</p>
              <h1 className="text-white text-xl font-semibold">Δρ. {user.name}</h1>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-all whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-[#0a1628] text-gold border-t border-x border-gold/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {renderTabIcon(tab.icon)}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="bg-navy-100 border-b border-gold/30">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gold font-semibold flex items-center gap-2">
                <Bell className="w-5 h-5" /> Ειδοποιήσεις
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button 
                    onClick={markAllRead}
                    className="text-slate-400 hover:text-white text-sm"
                  >
                    Σήμανση όλων
                  </button>
                )}
                <button onClick={() => setShowNotifications(false)} className="text-slate-400 hover:text-white">✕</button>
              </div>
            </div>
            <div className="mb-3 pb-3 border-b border-slate-700/50">
              <PushNotificationToggle userId={user.username} role="partner" />
            </div>
            {notifications.length === 0 ? (
              <p className="text-slate-500 text-center py-4">Δεν υπάρχουν ειδοποιήσεις</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {notifications.map((n) => (
                  <div 
                    key={n.id} 
                    onClick={() => !n.read && markNotificationRead(n.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      n.read 
                        ? 'bg-navy-200/30 border-slate-700/30' 
                        : 'bg-navy-200/70 border-gold/30 hover:border-gold/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${
                        n.type === 'visit_update' ? 'bg-blue-500/20 text-blue-400' :
                        n.type === 'stats_update' ? 'bg-green-500/20 text-green-400' :
                        n.type === 'payment_added' ? 'bg-purple-500/20 text-purple-400' :
                        'bg-gold/20 text-gold'
                      }`}>
                        {n.type === 'visit_update' ? '👤' : 
                         n.type === 'stats_update' ? '📊' : 
                         n.type === 'payment_added' ? '💳' : '📢'}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium text-sm ${n.read ? 'text-slate-400' : 'text-white'}`}>{n.title}</p>
                        <p className={`text-xs ${n.read ? 'text-slate-500' : 'text-slate-300'}`}>{n.message}</p>
                        <p className="text-xs text-slate-600 mt-1">
                          {new Date(n.created_at).toLocaleString('el-GR')}
                        </p>
                      </div>
                      {!n.read && (
                        <div className="w-2 h-2 bg-gold rounded-full"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Success Alert */}
      {showPaymentSuccessAlert && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] animate-pulse">
          <div className="bg-green-500/90 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
            <span className="text-2xl">🎉</span>
            <div>
              <p className="font-bold">Η πληρωμή ολοκληρώθηκε επιτυχώς!</p>
              <p className="text-sm text-green-100">Ευχαριστούμε για την εμπιστοσύνη σας.</p>
            </div>
            <button onClick={() => setShowPaymentSuccessAlert(false)} className="ml-4 text-white/80 hover:text-white">✕</button>
          </div>
        </div>
      )}

      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div className="fixed bottom-4 left-4 right-4 z-[100] md:left-auto md:right-4 md:w-96">
          <div className="bg-gradient-to-r from-gold to-amber-500 text-navy-200 px-5 py-4 rounded-xl shadow-2xl">
            <div className="flex items-center gap-3">
              <Download className="w-8 h-8" />
              <div className="flex-1">
                <p className="font-bold text-sm">Εγκαταστήστε την εφαρμογή!</p>
                <p className="text-xs opacity-80">Προσθέστε στην αρχική οθόνη για γρήγορη πρόσβαση</p>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => setShowInstallBanner(false)} 
                  className="text-navy-200/60 hover:text-navy-200 text-sm"
                >
                  Όχι
                </button>
                <button 
                  onClick={handleInstallClick}
                  className="bg-navy-200 text-gold px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-navy-100 transition-colors"
                >
                  Εγκατάσταση
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold border-t-transparent"></div>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-navy-100/50 rounded-xl p-5 border border-slate-700/50">
                    <p className="text-slate-400 text-sm mb-1">Κλήσεις Μήνα</p>
                    <p className="text-3xl font-bold text-white">{stats?.calls_this_month || 0}</p>
                    {stats?.calls_this_month > 0 && (
                      <p className="text-slate-500 text-xs mt-1">Τρέχων μήνας</p>
                    )}
                  </div>
                  <div className="bg-navy-100/50 rounded-xl p-5 border border-slate-700/50">
                    <p className="text-slate-400 text-sm mb-1">Ραντεβού Μήνα</p>
                    <p className="text-3xl font-bold text-white">{stats?.appointments_this_month || 0}</p>
                    {stats?.appointments_this_month > 0 && (
                      <p className="text-slate-500 text-xs mt-1">Τρέχων μήνας</p>
                    )}
                  </div>
                  <div className="bg-navy-100/50 rounded-xl p-5 border border-slate-700/50">
                    <p className="text-slate-400 text-sm mb-1">Ικανοποίηση</p>
                    <p className="text-3xl font-bold text-green-400">{stats?.satisfaction_rate || 0}%</p>
                    {stats?.satisfaction_rate > 0 && (
                      <p className="text-slate-500 text-xs mt-1">Βαθμολογία πελατών</p>
                    )}
                  </div>
                  <div className="bg-navy-100/50 rounded-xl p-5 border border-slate-700/50">
                    <p className="text-slate-400 text-sm mb-1">Εκκρεμείς Πληρωμές</p>
                    <p className="text-3xl font-bold text-amber-400">€{payments.summary?.total_pending?.toFixed(2) || '0.00'}</p>
                    <p className="text-slate-500 text-xs mt-1">Προς εξόφληση</p>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-navy-100/50 rounded-xl p-6 border border-slate-700/50">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <span>📄</span> Πρόσφατες Αναφορές
                    </h3>
                    <div className="space-y-3">
                      {reports.slice(0, 3).map(report => (
                        <div key={report.id} className="flex items-center justify-between py-2 border-b border-slate-700/30 last:border-0">
                          <div>
                            <p className="text-slate-300 text-sm">{report.title}</p>
                            <p className="text-slate-500 text-xs">{report.period}</p>
                          </div>
                          <button className="text-gold hover:text-amber-400 text-sm">Λήψη</button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-navy-100/50 rounded-xl p-6 border border-slate-700/50">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <span>💳</span> Πρόσφατες Πληρωμές
                    </h3>
                    <div className="space-y-3">
                      {payments.payments?.slice(0, 3).map(payment => (
                        <div key={payment.id} className="flex items-center justify-between py-2 border-b border-slate-700/30 last:border-0">
                          <div>
                            <p className="text-slate-300 text-sm">{payment.invoice_number}</p>
                            <p className="text-slate-500 text-xs">{payment.issued_date}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-medium">€{payment.total.toFixed(2)}</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${payment.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                              {payment.status === 'paid' ? 'Εξοφλημένο' : 'Εκκρεμεί'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <motion.div
                key="services"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <div className="bg-navy-100/50 rounded-xl p-6 border border-slate-700/50">
                  <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                    <img src="/icon-services.png" alt="Υπηρεσίες" className="w-16 h-16 rounded-full" />
                    Επιλογή Υπηρεσιών
                  </h2>
                  <p className="text-slate-400 mb-6">Επιλέξτε τις υπηρεσίες που ταιριάζουν στις ανάγκες σας</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Patient Line Package - Click to Configure */}
                    <div 
                      onClick={() => setShowPatientLineModal(true)}
                      className="bg-navy-200/50 rounded-xl border-2 border-green-500/50 hover:border-green-500 transition-all p-6 flex flex-col relative cursor-pointer hover:scale-[1.02]"
                    >
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        1ος ΜΗΝΑΣ ΔΩΡΕΑΝ
                      </div>
                      <div className="flex-1">
                        <div className="bg-[#0a1628] rounded-xl p-3 w-fit mx-auto mb-4 border border-green-500/30">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/9qlqafhw_IMG_1223.jpeg" 
                            alt="Γραμμή του Ασθενή" 
                            className="h-32 w-auto block mix-blend-lighten"
                            style={{ filter: 'brightness(1.2) saturate(1.6) sepia(0.3) hue-rotate(-5deg)' }}
                          />
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">Γραμμή του Ασθενή</h3>
                        <p className="text-slate-400 text-sm mb-4">Διαχείριση κλήσεων και ραντεβού ασθενών</p>
                        <ul className="space-y-2 mb-6">
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-green-400">✓</span> Απάντηση κλήσεων ασθενών
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-green-400">✓</span> Κλείσιμο ραντεβού
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-green-400">✓</span> Υπενθυμίσεις ραντεβού
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-green-400">✓</span> Αναφορές κλήσεων
                          </li>
                        </ul>
                      </div>
                      <div className="border-t border-slate-700 pt-4">
                        <p className="text-slate-400 text-sm mb-1">Εγγραφή: <span className="text-green-400 font-bold">€150/χρόνο</span></p>
                        <p className="text-green-400 text-sm font-medium mb-1">🎁 1ος μήνας ΔΩΡΕΑΝ!</p>
                        <p className="text-slate-400 text-sm mb-3">Μετά: <span className="text-white font-bold">€100/μήνα</span></p>
                        <button 
                          className="w-full py-3 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                        >
                          <span>Επιλέξτε Πακέτο</span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Full Secretary Package - Click to Configure */}
                    <div 
                      onClick={() => setShowSecretaryModal(true)}
                      className="bg-navy-200/50 rounded-xl border-2 border-red-500/50 hover:border-red-500 p-6 flex flex-col relative cursor-pointer transition-all hover:scale-[1.02]"
                    >
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-red-600 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        7 ΜΕΡΕΣ ΔΩΡΕΑΝ ΔΟΚΙΜΗ
                      </div>
                      <div className="flex-1">
                        <div className="bg-[#0a1628] rounded-xl p-3 w-fit mx-auto mb-4 border border-red-500/30">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/vbrct4d0_IMG_1224.jpeg" 
                            alt="Γραμματειακή Υποστήριξη" 
                            className="h-32 w-auto block mix-blend-lighten"
                            style={{ filter: 'brightness(1.2) saturate(1.6) sepia(0.3) hue-rotate(-5deg)' }}
                          />
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">Γραμματειακή Υποστήριξη</h3>
                        <p className="text-slate-400 text-sm mb-4">Πλήρης ιατρική τηλεγραμματεία</p>
                        <ul className="space-y-2 mb-6">
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-red-400">✓</span> Απάντηση & διαχείριση κλήσεων
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-red-400">✓</span> Διαχείριση ραντεβού ιατρών
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-red-400">✓</span> Υποστήριξη πολλών ειδικοτήτων
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-red-400">✓</span> Χωρίς δέσμευση - Ακύρωση οποτεδήποτε
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-red-400">✓</span> GDPR & ιατρικό απόρρητο
                          </li>
                        </ul>
                      </div>
                      <div className="border-t border-slate-700 pt-4">
                        <p className="text-red-400 text-sm font-medium mb-1">🎁 7 μέρες ΔΩΡΕΑΝ δοκιμή!</p>
                        <p className="text-slate-400 text-sm mb-2">Εγγραφή: <span className="text-red-400 font-bold">€150/χρόνο</span></p>
                        <p className="text-slate-400 text-sm mb-3">Πακέτα από <span className="text-red-400 font-bold">€270</span>/μήνα</p>
                        <button 
                          className="w-full py-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                        >
                          <span>Επιλέξτε Πακέτο</span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Website Creation Package */}
                    <div 
                      onClick={() => setShowWebsiteModal(true)}
                      className="bg-navy-200/50 rounded-xl border-2 border-blue-500/50 hover:border-blue-500 p-6 flex flex-col relative cursor-pointer transition-all hover:scale-[1.02]"
                    >
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                        ΙΑΤΡΙΚΕΣ ΙΣΤΟΣΕΛΙΔΕΣ
                      </div>
                      <div className="flex-1">
                        <div className="bg-[#0a1628] rounded-xl p-3 w-fit mx-auto mb-4 border border-blue-500/30">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/qzalgc9k_IMG_1220.jpeg" 
                            alt="Δημιουργία Ιστοσελίδας" 
                            className="h-32 w-auto block rounded-lg mix-blend-lighten"
                            style={{ filter: 'brightness(1.2) saturate(1.6) sepia(0.3) hue-rotate(-5deg)' }}
                          />
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">Δημιουργία Ιστοσελίδας</h3>
                        <p className="text-slate-400 text-sm mb-4">Επαγγελματική ιστοσελίδα για ιατρεία</p>
                        <ul className="space-y-2 mb-6">
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-blue-400">✓</span> Σύγχρονο responsive design
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-blue-400">✓</span> Φωτογραφίες ιατρείου
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-blue-400">✓</span> Google Reviews & Maps
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-blue-400">✓</span> Φόρμα επικοινωνίας
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-blue-400">✓</span> SEO βελτιστοποίηση
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-blue-400">✓</span> SSL πιστοποιητικό
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-blue-400">✓</span> Ετήσια υποστήριξη & Hosting
                          </li>
                        </ul>
                      </div>
                      <div className="border-t border-slate-700 pt-4">
                        <p className="text-slate-400 text-sm mb-3">Επικοινωνήστε για προσφορά</p>
                        <button 
                          className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                        >
                          <span>Μάθετε Περισσότερα</span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Premium Package - Click to Configure */}
                    <div 
                      onClick={() => setShowPremiumModal(true)}
                      className="bg-gradient-to-br from-navy-200/80 to-amber-900/20 rounded-xl border-2 border-gold/50 hover:border-gold p-6 flex flex-col relative transition-all cursor-pointer hover:scale-[1.02]"
                    >
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gold to-amber-500 text-navy-200 text-xs font-bold px-4 py-1 rounded-full">
                        ΝΟΣΟΚΟΜΕΙΑ & ΚΛΙΝΙΚΕΣ
                      </div>
                      <div className="flex-1">
                        <div className="bg-[#0a1628] rounded-xl p-3 w-fit mx-auto mb-4 border border-gold/30 flex items-center justify-center">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/10q3icv9_IMG_1225.jpeg" 
                            alt="Προνομιακό Πακέτο" 
                            className="h-32 w-auto block mx-auto mix-blend-lighten mt-2"
                            style={{ filter: 'brightness(1.2) saturate(1.6) sepia(0.3) hue-rotate(-5deg)' }}
                          />
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">Προνομιακό Πακέτο</h3>
                        <p className="text-slate-400 text-sm mb-4">Για Νοσοκομεία & Ιατρικά Κέντρα</p>
                        <ul className="space-y-2 mb-6">
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-gold">✓</span> Απεριόριστες κλήσεις
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-gold">✓</span> Απάντηση & διαχείριση κλήσεων
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-gold">✓</span> Διαχείριση ραντεβού ιατρών
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-gold">✓</span> Υποστήριξη πολλών ειδικοτήτων
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-gold">✓</span> 4 γλώσσες (EL, EN, RU, AR)
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-gold">✓</span> GDPR & ιατρικό απόρρητο
                          </li>
                          <li className="text-slate-300 text-sm flex items-center gap-2">
                            <span className="text-gold">✓</span> Επιλογή 24/7 εξυπηρέτησης
                          </li>
                        </ul>
                      </div>
                      <div className="border-t border-slate-700 pt-4">
                        <p className="text-slate-400 text-sm mb-2">Εγγραφή: <span className="text-gold font-bold">€300/χρόνο</span></p>
                        <p className="text-slate-400 text-sm mb-3">Πακέτα από <span className="text-gold font-bold">€3600</span>/μήνα</p>
                        <button 
                          className="w-full py-3 bg-gradient-to-r from-gold to-amber-600 hover:from-amber-500 hover:to-gold text-navy-200 font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                        >
                          <span>Επιλέξτε Πακέτο</span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Payment Methods Info */}
                  <div className="mt-8 p-6 bg-navy-200/30 rounded-xl border border-slate-700/50">
                    <p className="text-slate-400 text-sm text-center mb-4">Αποδεκτοί τρόποι πληρωμής</p>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                      {/* Visa */}
                      <div className="bg-white rounded-lg px-3 py-2">
                        <svg className="h-8 w-auto" viewBox="0 0 750 471">
                          <path d="M278.198 334.228l33.36-195.763h53.358l-33.384 195.763h-53.334zm246.905-191.54c-10.57-3.966-27.135-8.222-47.822-8.222-52.725 0-89.863 26.551-90.18 64.604-.297 28.129 26.514 43.821 46.754 53.185 20.77 9.597 27.752 15.716 27.654 24.283-.133 13.123-16.586 19.115-31.924 19.115-21.355 0-32.701-2.967-50.225-10.273l-6.878-3.111-7.487 43.822c12.463 5.467 35.508 10.199 59.438 10.445 56.09 0 92.502-26.248 92.916-66.884.199-22.27-14.016-39.215-44.801-53.188-18.65-9.056-30.072-15.099-29.951-24.269 0-8.137 9.668-16.838 30.557-16.838 17.449-.271 30.088 3.534 39.936 7.5l4.781 2.259 7.232-42.428zm137.308-4.223h-41.232c-12.773 0-22.332 3.486-27.94 16.234l-79.244 179.402h56.031s9.159-24.121 11.232-29.418c6.125 0 60.555.084 68.336.084 1.596 6.854 6.492 29.334 6.492 29.334h49.512l-43.187-195.636zm-65.418 126.408c4.414-11.279 21.26-54.724 21.26-54.724-.316.521 4.381-11.334 7.074-18.684l3.607 16.878s10.217 46.729 12.352 56.527h-44.293v.003zM209.878 138.465l-52.239 133.496-5.565-27.129c-9.726-31.274-40.025-65.157-73.898-82.12l47.767 171.204 56.455-.064 84.004-195.386-56.524-.001" fill="#0E4595"/>
                          <path d="M131.92 138.465H45.879l-.682 4.073c66.939 16.204 111.232 55.363 129.618 102.415l-18.709-89.96c-3.229-12.396-12.597-16.095-24.186-16.528" fill="#F2AE14"/>
                        </svg>
                      </div>
                      {/* Mastercard */}
                      <div className="bg-white rounded-lg px-3 py-2">
                        <svg className="h-8 w-auto" viewBox="0 0 152.407 108">
                          <path fill="#ff5f00" d="M60.412 25.697h31.5v56.606h-31.5z"/>
                          <path d="M62.412 54a35.938 35.938 0 0 1 13.75-28.303 36 36 0 1 0 0 56.606A35.938 35.938 0 0 1 62.412 54z" fill="#eb001b"/>
                          <path d="M134.407 54a35.999 35.999 0 0 1-58.245 28.303 36.005 36.005 0 0 0 0-56.606A35.999 35.999 0 0 1 134.407 54z" fill="#f79e1b"/>
                        </svg>
                      </div>
                      {/* Apple Pay */}
                      <div className="bg-white rounded-lg px-3 py-2">
                        <svg className="h-8 w-auto" viewBox="0 0 165.521 105.965">
                          <path d="M150.698 0H14.823C6.626 0 0 6.626 0 14.823v76.319c0 8.197 6.626 14.823 14.823 14.823h135.875c8.197 0 14.823-6.626 14.823-14.823V14.823C165.521 6.626 158.895 0 150.698 0z"/>
                          <path d="M35.766 34.739c1.261-1.577 2.126-3.707 1.892-5.916-1.813.079-4.097 1.182-5.404 2.759-1.182 1.34-2.205 3.549-1.934 5.6 2.047.158 4.018-1.024 5.446-2.443zm1.871 3.076c-3.012-.158-5.601 1.735-7.019 1.735-1.419 0-3.628-1.656-5.996-1.577-3.091.079-5.996 1.813-7.573 4.587-3.233 5.601-0.869 13.883 2.285 18.47 1.498 2.285 3.312 4.824 5.68 4.745 2.285-.079 3.154-1.498 5.916-1.498 2.759 0 3.549 1.498 5.996 1.419 2.443-.079 4.018-2.285 5.521-4.587 1.735-2.522 2.443-4.982 2.522-5.14-.079-.079-4.903-1.892-4.982-7.494-.079-4.666 3.786-6.874 3.944-7.019-2.126-3.154-5.521-3.549-6.716-3.628l.422-.013zm22.347-5.837v35.343h5.442v-12.1h7.494c6.874 0 11.698-4.745 11.698-11.619s-4.745-11.619-11.54-11.619h-13.094v-.005zm5.442 4.587h6.241c4.745 0 7.415 2.522 7.415 6.953s-2.68 7.019-7.494 7.019h-6.162V36.565zm29.38 31.073c3.391 0 6.557-1.735 7.968-4.508h.158v4.191h5.061V48.263c0-5.14-4.097-8.452-10.409-8.452-5.916 0-10.251 3.391-10.409 8.057h4.903c.395-2.205 2.443-3.628 5.363-3.628 3.391 0 5.363 1.656 5.363 4.587v2.047l-7.019.395c-6.557.395-10.093 3.076-10.093 7.731 0 4.745 3.628 7.889 9.114 7.889zm1.419-4.033c-2.996 0-4.903-1.419-4.903-3.628 0-3.707 1.892-4.191 6.874-4.587l6.241-.395v2.126c0 3.786-3.154 6.478-8.212 6.478v.006zm18.47 13.41c5.284 0 7.81-2.047 9.982-8.215l9.507-26.626h-5.521l-6.399 20.472h-.158l-6.399-20.472h-5.68l9.192 25.603-.474 1.498c-.79 2.68-2.126 3.707-4.429 3.707-.395 0-1.182-.079-1.498-.079v4.191c.316.079 1.577.158 1.877.158v-.237z" fill="#fff"/>
                        </svg>
                      </div>
                      {/* Google Pay */}
                      <div className="bg-white rounded-lg px-3 py-2">
                        <svg className="h-8 w-auto" viewBox="0 0 435.97 173.13">
                          <path fill="#5f6368" d="M206.2 84.58v50.75h-16.1V10h42.7a38.61 38.61 0 0 1 27.65 10.85A34.88 34.88 0 0 1 272 47.3a34.72 34.72 0 0 1-11.55 26.6q-11.2 10.68-27.65 10.67H206.2zm0-59.15v43.72h27a21.28 21.28 0 0 0 15.93-6.48 21.36 21.36 0 0 0 0-30.63 21 21 0 0 0-15.93-6.62h-27z"/>
                          <path fill="#5f6368" d="M309.1 46.78q17.85 0 28.18 9.54t10.32 26.16v52.85h-15.4v-11.9h-.7q-10 14.63-26.6 14.62-14.18 0-23.63-8.4a26.59 26.59 0 0 1-9.45-20.93q0-13.23 10-21.17t26.33-7.96q14 0 23.1 5.12v-3.57a18.49 18.49 0 0 0-6.65-14.36 22.76 22.76 0 0 0-15.58-5.95q-13.65 0-21.7 11.37l-14.18-8.93q12.07-16.8 35.96-16.49zm-20.83 62.3a12.86 12.86 0 0 0 5.25 10.41 19.22 19.22 0 0 0 12.43 4.3 25.53 25.53 0 0 0 18.08-7.61q8-7.61 8-17.33-7.35-5.88-20.3-5.88-9.45 0-16.45 4.87a15.05 15.05 0 0 0-7 11.24z"/>
                          <path fill="#5f6368" d="M436 49.53l-53.9 123.95h-16.62l20-43.23-35.35-80.72h17.5l25.55 61.6h.35l24.85-61.6z"/>
                          <path fill="#4285f4" d="M141.14 73.64A85.79 85.79 0 0 0 139.9 59H72v27.73h38.89a33.33 33.33 0 0 1-14.38 21.88v17.95h23.21c13.59-12.53 21.42-31.06 21.42-52.92z"/>
                          <path fill="#34a853" d="M72 144c19.43 0 35.79-6.38 47.72-17.38l-23.21-17.95c-6.47 4.36-14.79 6.88-24.51 6.88-18.78 0-34.72-12.66-40.42-29.72H7.67v18.55A72 72 0 0 0 72 144z"/>
                          <path fill="#fbbc04" d="M31.58 85.83a43.14 43.14 0 0 1 0-27.66V39.62H7.67a72 72 0 0 0 0 64.76z"/>
                          <path fill="#ea4335" d="M72 28.45a39.09 39.09 0 0 1 27.62 10.8l20.55-20.55A69.18 69.18 0 0 0 72 0 72 72 0 0 0 7.67 39.62l23.91 18.55C37.28 41.11 53.22 28.45 72 28.45z"/>
                        </svg>
                      </div>
                      {/* PayPal */}
                      <div className="bg-white rounded-lg px-3 py-2">
                        <svg className="h-8 w-auto" viewBox="0 0 124 33">
                          <path fill="#253B80" d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.142-2.696-1.746-4.985-1.746zM47 13.154c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.469 1.044.332 1.906zM66.654 13.075h-3.275a.57.57 0 0 0-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .562.66h2.95a.95.95 0 0 0 .939-.803l1.77-11.209a.568.568 0 0 0-.561-.658zm-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883-.484-.574-.668-1.391-.514-2.301.295-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.697 1.411.554 2.317zM84.096 13.075h-3.291a.954.954 0 0 0-.787.417l-4.539 6.686-1.924-6.425a.953.953 0 0 0-.912-.678h-3.234a.57.57 0 0 0-.541.754l3.625 10.638-3.408 4.811a.57.57 0 0 0 .465.9h3.287a.949.949 0 0 0 .781-.408l10.946-15.8a.57.57 0 0 0-.468-.895z"/>
                          <path fill="#179BD7" d="M94.992 6.749h-6.84a.95.95 0 0 0-.938.802l-2.766 17.537a.569.569 0 0 0 .562.658h3.51a.665.665 0 0 0 .656-.562l.785-4.971a.95.95 0 0 1 .938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.142-2.694-1.746-4.983-1.746zm.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.725-4.583a.568.568 0 0 1 .562-.481h.473c1.234 0 2.4 0 3.002.704.359.42.468 1.044.331 1.906zM115.434 13.075h-3.273a.567.567 0 0 0-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .564.66h2.949a.95.95 0 0 0 .938-.803l1.771-11.209a.571.571 0 0 0-.565-.658zm-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.666-1.391-.514-2.301.297-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.699 1.411.554 2.317zM119.295 7.23l-2.807 17.858a.569.569 0 0 0 .562.658h2.822c.469 0 .867-.34.939-.803l2.768-17.536a.57.57 0 0 0-.562-.659h-3.16a.571.571 0 0 0-.562.482z"/>
                        </svg>
                      </div>
                    </div>
                    <p className="text-slate-500 text-xs text-center mt-3">🔒 Ασφαλείς πληρωμές μέσω Stripe</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Monthly Folders Tab */}
            {activeTab === 'folders' && (
              <motion.div
                key="folders"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <MonthlyFoldersPartner username={user.username} />
              </motion.div>
            )}

            {/* Stats Tab */}
            {activeTab === 'stats' && (
              <motion.div
                key="stats"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-playfair text-white mb-6">Στατιστικά Υπηρεσιών</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-6 border border-blue-500/30">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-500/30 flex items-center justify-center">
                        <Phone className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-slate-400 text-sm">Συνολικές Κλήσεις</p>
                        <p className="text-3xl font-bold text-white">{stats?.total_calls || 0}</p>
                      </div>
                    </div>
                    <p className="text-blue-400 text-sm">Αυτόν τον μήνα: {stats?.calls_this_month || 0}</p>
                  </div>

                  <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-6 border border-green-500/30">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-green-500/30 flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-green-400" />
                      </div>
                      <div>
                        <p className="text-slate-400 text-sm">Ραντεβού</p>
                        <p className="text-3xl font-bold text-white">{stats?.appointments_booked || 0}</p>
                      </div>
                    </div>
                    <p className="text-green-400 text-sm">Αυτόν τον μήνα: {stats?.appointments_this_month || 0}</p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-6 border border-purple-500/30">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-purple-500/30 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-slate-400 text-sm">Μ.Ο. Απόκρισης</p>
                        <p className="text-3xl font-bold text-white">{stats?.average_response_time || 'N/A'}</p>
                      </div>
                    </div>
                    <p className="text-purple-400 text-sm">Χαμένες κλήσεις: {stats?.missed_calls_handled || 0}</p>
                  </div>
                </div>

                <div className="bg-navy-100/50 rounded-xl p-6 border border-slate-700/50">
                  <h3 className="text-white font-semibold mb-4">Αξιολόγηση Υπηρεσιών</h3>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-5xl font-bold text-gold">{stats?.satisfaction_rate || 0}%</p>
                      <p className="text-slate-400 text-sm mt-2">Ικανοποίηση Πελατών</p>
                    </div>
                    <div className="flex-1 h-4 bg-navy-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-gold to-amber-500 rounded-full transition-all duration-1000"
                        style={{ width: `${stats?.satisfaction_rate || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Reports Tab */}
            {activeTab === 'reports' && (
              <motion.div
                key="reports"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-playfair text-white mb-6">Αναφορές</h2>
                
                <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-navy-200/50">
                      <tr>
                        <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">Τίτλος</th>
                        <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">Περίοδος</th>
                        <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">Κατάσταση</th>
                        <th className="text-right text-slate-400 text-sm font-medium px-6 py-4">Ενέργειες</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map(report => (
                        <tr key={report.id} className="border-t border-slate-700/30 hover:bg-navy-200/30">
                          <td className="px-6 py-4">
                            <p className="text-white">{report.title}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-slate-400 text-sm">{report.period}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
                              Έτοιμο
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button className="text-gold hover:text-amber-400 text-sm font-medium">
                              📥 Λήψη PDF
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <motion.div
                key="payments"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-playfair text-white mb-6">Πληρωμές & Τιμολόγια</h2>
                
                {/* Real Payment History from Stripe */}
                {paymentHistory.length > 0 && (
                  <div className="bg-gradient-to-r from-gold/10 to-amber-500/5 rounded-xl border border-gold/30 p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gold mb-4 flex items-center gap-2">
                      <span>💳</span> Ιστορικό Πληρωμών Stripe
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gold/20">
                            <th className="text-left text-slate-400 text-sm font-medium py-3">Ημ/νία</th>
                            <th className="text-left text-slate-400 text-sm font-medium py-3">Πακέτο</th>
                            <th className="text-right text-slate-400 text-sm font-medium py-3">Υπηρεσία</th>
                            <th className="text-right text-slate-400 text-sm font-medium py-3">Έξοδα</th>
                            <th className="text-right text-slate-400 text-sm font-medium py-3">Σύνολο</th>
                            <th className="text-center text-slate-400 text-sm font-medium py-3">Κατάσταση</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentHistory.map(item => (
                            <tr key={item.id} className="border-b border-slate-700/30 hover:bg-navy-200/20">
                              <td className="py-3 text-white">{item.date}</td>
                              <td className="py-3 text-slate-300">{item.package}</td>
                              <td className="py-3 text-right text-white">€{item.base_amount?.toFixed(2)}</td>
                              <td className="py-3 text-right text-slate-400">€{item.transaction_fee?.toFixed(2)}</td>
                              <td className="py-3 text-right text-gold font-medium">€{item.total?.toFixed(2)}</td>
                              <td className="py-3 text-center">
                                <span className={`px-3 py-1 rounded-full text-xs ${
                                  item.status_code === 'paid' 
                                    ? 'bg-green-500/20 text-green-400' 
                                    : 'bg-amber-500/20 text-amber-400'
                                }`}>
                                  {item.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Payment Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-green-500/10 rounded-xl p-5 border border-green-500/30">
                    <p className="text-slate-400 text-sm mb-1">Εξοφλημένα</p>
                    <p className="text-3xl font-bold text-green-400">€{payments.summary?.total_paid?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="bg-amber-500/10 rounded-xl p-5 border border-amber-500/30">
                    <p className="text-slate-400 text-sm mb-1">Εκκρεμείς</p>
                    <p className="text-3xl font-bold text-amber-400">€{payments.summary?.total_pending?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="bg-navy-100/50 rounded-xl p-5 border border-slate-700/50">
                    <p className="text-slate-400 text-sm mb-1">Σύνολο</p>
                    <p className="text-3xl font-bold text-white">€{((payments.summary?.total_paid || 0) + (payments.summary?.total_pending || 0)).toFixed(2)}</p>
                  </div>
                </div>

                {/* Invoices Table */}
                <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-navy-200/50">
                      <tr>
                        <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">Τιμολόγιο</th>
                        <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">Περιγραφή</th>
                        <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">Ημ/νία</th>
                        <th className="text-right text-slate-400 text-sm font-medium px-6 py-4">Ποσό</th>
                        <th className="text-center text-slate-400 text-sm font-medium px-6 py-4">Κατάσταση</th>
                        <th className="text-right text-slate-400 text-sm font-medium px-6 py-4">Ενέργειες</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.payments?.map(payment => (
                        <tr key={payment.id} className="border-t border-slate-700/30 hover:bg-navy-200/30">
                          <td className="px-6 py-4">
                            <p className="text-white font-medium">{payment.invoice_number}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-slate-400 text-sm">{payment.description}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-slate-400 text-sm">{payment.issued_date}</p>
                            <p className="text-slate-500 text-xs">Λήξη: {payment.due_date}</p>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <p className="text-white font-medium">€{payment.total.toFixed(2)}</p>
                            <p className="text-slate-500 text-xs">(ΦΠΑ: €{payment.vat.toFixed(2)})</p>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`px-3 py-1 rounded-full text-xs ${
                              payment.status === 'paid' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {payment.status === 'paid' ? 'Εξοφλημένο' : 'Εκκρεμεί'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button className="text-gold hover:text-amber-400 text-sm font-medium mr-3">
                              📥 PDF
                            </button>
                            {payment.status === 'pending' && (
                              <button className="text-green-400 hover:text-green-300 text-sm font-medium">
                                💳 Πληρωμή
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Visits Tab - For All Partners with Patient Line */}
            {activeTab === 'visits' && (
              <motion.div
                key="visits"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-playfair text-white">Επισκέψεις Ασθενών</h2>
                  <button 
                    onClick={() => window.open(`${API}/partners/${user.username}/download-stats`, '_blank')}
                    className="flex items-center gap-2 px-4 py-2 bg-gold/20 text-gold rounded-lg hover:bg-gold/30 transition-colors"
                  >
                    <span>📥</span> Λήψη CSV
                  </button>
                </div>
                
                {/* Visits Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-500/10 rounded-xl p-5 border border-blue-500/30">
                    <p className="text-slate-400 text-sm mb-1">Σύνολο</p>
                    <p className="text-3xl font-bold text-blue-400">{visits.summary?.total || 0}</p>
                  </div>
                  <div className="bg-green-500/10 rounded-xl p-5 border border-green-500/30">
                    <p className="text-slate-400 text-sm mb-1">Εμφανίστηκαν</p>
                    <p className="text-3xl font-bold text-green-400">{visits.summary?.attended || 0}</p>
                  </div>
                  <div className="bg-red-500/10 rounded-xl p-5 border border-red-500/30">
                    <p className="text-slate-400 text-sm mb-1">Δεν Εμφανίστηκαν</p>
                    <p className="text-3xl font-bold text-red-400">{visits.summary?.no_show || 0}</p>
                  </div>
                  <div className="bg-amber-500/10 rounded-xl p-5 border border-amber-500/30">
                    <p className="text-slate-400 text-sm mb-1">Ποσοστό Εμφάνισης</p>
                    <p className="text-3xl font-bold text-amber-400">{visits.summary?.attendance_rate || 0}%</p>
                  </div>
                </div>

                {/* Visits Table */}
                <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 overflow-hidden">
                  <div className="p-4 border-b border-slate-700/50">
                    <h3 className="text-white font-semibold">Λίστα Επισκέψεων</h3>
                    <p className="text-slate-500 text-xs mt-1">Ιστορικό επισκέψεων ασθενών</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-navy-200/50">
                        <tr>
                          <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">Ασθενής</th>
                          <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">Ημερομηνία</th>
                          <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">Ώρα</th>
                          <th className="text-center text-slate-400 text-sm font-medium px-6 py-4">Εμφάνιση</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visits.visits?.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center py-8 text-slate-500">
                              Δεν υπάρχουν καταγεγραμμένες επισκέψεις
                            </td>
                          </tr>
                        ) : (
                          visits.visits?.map((visit) => (
                            <tr key={visit.id} className="border-t border-slate-700/30 hover:bg-navy-200/30">
                              <td className="px-6 py-4">
                                <div>
                                  {visit.patient_name && <p className="text-white font-medium">{visit.patient_name}</p>}
                                  <span className="text-slate-400 text-sm">{visit.patient_initials}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-slate-300">{visit.appointment_date}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-slate-400">{visit.appointment_time || '-'}</p>
                              </td>
                              <td className="px-6 py-4 text-center">
                                {visit.attended ? (
                                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-green-500/20 text-green-400 text-xl font-bold">
                                    ✓
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-500/20 text-red-400 text-xl font-bold">
                                    ✗
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <motion.div
                key="chat"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 overflow-hidden">
                  <ChatWindow
                    currentUser={user.username}
                    currentRole="partner"
                    partnerUsername="admin"
                    partnerName="J. House of Secretary"
                  />
                </div>
              </motion.div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <motion.div
                key="profile"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <div className="bg-navy-100/50 rounded-xl p-6 border border-slate-700/50">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                      <svg className="w-6 h-6 text-gold" fill="currentColor" viewBox="0 0 24 24"><path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg>
                      Ρυθμίσεις Προφίλ
                    </h2>
                    {!isEditingProfile && (
                      <button
                        onClick={() => {
                          setIsEditingProfile(true);
                          setEditName(user.name || '');
                          setEditEmail(user.email || '');
                          setEditCompany(user.company || '');
                          setProfileUpdateError('');
                          setProfileUpdateSuccess('');
                        }}
                        className="px-4 py-2 bg-gold text-navy-200 font-medium rounded-xl hover:bg-amber-400 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                        Επεξεργασία
                      </button>
                    )}
                  </div>

                  {/* Success/Error Messages */}
                  {profileUpdateSuccess && (
                    <div className="mb-4 p-4 bg-green-500/20 border border-green-500/50 rounded-xl text-green-400">
                      {profileUpdateSuccess}
                    </div>
                  )}
                  {profileUpdateError && (
                    <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400">
                      {profileUpdateError}
                    </div>
                  )}
                  
                  <div className="space-y-6">
                    {/* User Info Display/Edit */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-gold text-sm font-medium mb-2">Ονοματεπώνυμο</label>
                        {isEditingProfile ? (
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full text-white bg-navy-200/50 px-4 py-3 rounded-xl border border-gold/50 focus:border-gold focus:outline-none"
                          />
                        ) : (
                          <p className="text-white bg-navy-200/50 px-4 py-3 rounded-xl border border-slate-700">{user.name}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-gold text-sm font-medium mb-2">Email</label>
                        {isEditingProfile ? (
                          <input
                            type="email"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            className="w-full text-white bg-navy-200/50 px-4 py-3 rounded-xl border border-gold/50 focus:border-gold focus:outline-none"
                          />
                        ) : (
                          <p className="text-white bg-navy-200/50 px-4 py-3 rounded-xl border border-slate-700">{user.email}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-gold text-sm font-medium mb-2">Όνομα Χρήστη</label>
                        <p className="text-white bg-navy-200/50 px-4 py-3 rounded-xl border border-slate-700">{user.username}</p>
                        {isEditingProfile && <p className="text-slate-500 text-xs mt-1">Το όνομα χρήστη δεν μπορεί να αλλάξει</p>}
                      </div>
                      <div>
                        <label className="block text-gold text-sm font-medium mb-2">Εταιρεία / Κλινική</label>
                        {isEditingProfile ? (
                          <input
                            type="text"
                            value={editCompany}
                            onChange={(e) => setEditCompany(e.target.value)}
                            className="w-full text-white bg-navy-200/50 px-4 py-3 rounded-xl border border-gold/50 focus:border-gold focus:outline-none"
                          />
                        ) : (
                          <p className="text-white bg-navy-200/50 px-4 py-3 rounded-xl border border-slate-700">{user.company || '-'}</p>
                        )}
                      </div>
                    </div>

                    {/* Password Change Section - Only when editing */}
                    {isEditingProfile && (
                      <div className="border-t border-slate-700 pt-6">
                        <h3 className="text-lg font-medium text-white mb-4">Αλλαγή Κωδικού (προαιρετικά)</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-gold text-sm font-medium mb-2">Νέος Κωδικός</label>
                            <input
                              type="password"
                              value={editPassword}
                              onChange={(e) => setEditPassword(e.target.value)}
                              placeholder="Αφήστε κενό αν δεν θέλετε αλλαγή"
                              className="w-full text-white bg-navy-200/50 px-4 py-3 rounded-xl border border-gold/50 focus:border-gold focus:outline-none placeholder-slate-500"
                            />
                          </div>
                          <div>
                            <label className="block text-gold text-sm font-medium mb-2">Επιβεβαίωση Κωδικού</label>
                            <input
                              type="password"
                              value={editConfirmPassword}
                              onChange={(e) => setEditConfirmPassword(e.target.value)}
                              placeholder="Επαναλάβετε τον νέο κωδικό"
                              className="w-full text-white bg-navy-200/50 px-4 py-3 rounded-xl border border-gold/50 focus:border-gold focus:outline-none placeholder-slate-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons when editing */}
                    {isEditingProfile && (
                      <div className="flex gap-4 pt-4">
                        <button
                          onClick={() => {
                            setIsEditingProfile(false);
                            setEditPassword('');
                            setEditConfirmPassword('');
                            setProfileUpdateError('');
                          }}
                          className="flex-1 py-3 border border-slate-600 text-slate-300 hover:bg-slate-700/50 font-medium rounded-xl transition-all"
                        >
                          Ακύρωση
                        </button>
                        <button
                          onClick={handleProfileUpdate}
                          disabled={profileUpdateLoading}
                          className={`flex-1 py-3 font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${
                            !profileUpdateLoading
                              ? 'bg-gradient-to-r from-gold to-amber-600 hover:from-amber-500 hover:to-gold text-navy-200'
                              : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                          }`}
                        >
                          {profileUpdateLoading ? (
                            <>
                              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              <span>Αποθήκευση...</span>
                            </>
                          ) : (
                            <span>Αποθήκευση Αλλαγών</span>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Partner Type Selection */}
                    <div className="border-t border-slate-700 pt-6">
                      <h3 className="text-lg font-medium text-white mb-4">Τύπος Συνεργασίας</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Γραμματειακή Υποστήριξη - Full */}
                        <div 
                          className={`p-5 rounded-xl border-2 transition-all ${
                            user.subscription_active && user.partner_type === 'full' 
                              ? 'border-red-500 bg-red-500/10' 
                              : 'border-slate-700 hover:border-red-500/50 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (!(user.subscription_active && user.partner_type === 'full')) {
                              setShowSecretaryModal(true);
                            }
                          }}
                        >
                          <div className="flex items-center gap-4">
                            <img 
                              src="/logo_secretary.png" 
                              alt="Γραμματειακή Υποστήριξη" 
                              className="w-16 h-16 rounded-xl"
                              style={{ filter: 'brightness(1.2) saturate(1.4)' }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-white font-semibold">Γραμματειακή Υποστήριξη</p>
                                {user.subscription_active && user.partner_type === 'full' && (
                                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">ΕΝΕΡΓΟ</span>
                                )}
                              </div>
                              <p className="text-slate-400 text-sm mt-1">Πλήρης ιατρική τηλεγραμματεία</p>
                              {!(user.subscription_active && user.partner_type === 'full') && (
                                <p className="text-red-400 text-xs mt-2 font-medium">Κάντε κλικ για αγορά →</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Γραμμή του Ασθενή - Patient Line */}
                        <div 
                          className={`p-5 rounded-xl border-2 transition-all ${
                            user.subscription_active && user.partner_type === 'patient_line' 
                              ? 'border-green-500 bg-green-500/10' 
                              : 'border-slate-700 hover:border-green-500/50 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (!(user.subscription_active && user.partner_type === 'patient_line')) {
                              setShowPatientLineModal(true);
                            }
                          }}
                        >
                          <div className="flex items-center gap-4">
                            <img 
                              src="/logo_patient_line.png" 
                              alt="Γραμμή του Ασθενή" 
                              className="w-16 h-16 rounded-xl"
                              style={{ filter: 'brightness(1.1) saturate(1.3)' }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-white font-semibold">Γραμμή του Ασθενή</p>
                                {user.subscription_active && user.partner_type === 'patient_line' && (
                                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">ΕΝΕΡΓΟ</span>
                                )}
                              </div>
                              <p className="text-slate-400 text-sm mt-1">Διαχείριση κλήσεων και ραντεβού</p>
                              {!(user.subscription_active && user.partner_type === 'patient_line') && (
                                <p className="text-green-400 text-xs mt-2 font-medium">Κάντε κλικ για αγορά →</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Δημιουργία Ιστοσελίδας - Website */}
                        <div 
                          className={`p-5 rounded-xl border-2 transition-all ${
                            user.subscription_active && user.partner_type === 'website' 
                              ? 'border-blue-500 bg-blue-500/10' 
                              : 'border-slate-700 hover:border-blue-500/50 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (!(user.subscription_active && user.partner_type === 'website')) {
                              setShowWebsiteModal(true);
                            }
                          }}
                        >
                          <div className="flex items-center gap-4">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/qzalgc9k_IMG_1220.jpeg" 
                              alt="Δημιουργία Ιστοσελίδας" 
                              className="w-16 h-16 rounded-xl object-cover"
                              style={{ filter: 'brightness(1.1) saturate(1.3)' }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-white font-semibold">Δημιουργία Ιστοσελίδας</p>
                                {user.subscription_active && user.partner_type === 'website' && (
                                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">ΕΝΕΡΓΟ</span>
                                )}
                              </div>
                              <p className="text-slate-400 text-sm mt-1">Επαγγελματική ιστοσελίδα για ιατρούς, ιατρεία και κλινικές</p>
                              {!(user.subscription_active && user.partner_type === 'website') && (
                                <p className="text-blue-400 text-xs mt-2 font-medium">Κάντε κλικ για αγορά →</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Προνομιακό Πακέτο - Premium */}
                        <div 
                          className={`p-5 rounded-xl border-2 transition-all ${
                            user.subscription_active && user.partner_type === 'premium' 
                              ? 'border-gold bg-gold/10' 
                              : 'border-slate-700 hover:border-gold/50 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (!(user.subscription_active && user.partner_type === 'premium')) {
                              setShowPremiumModal(true);
                            }
                          }}
                        >
                          <div className="flex items-center gap-4">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/10q3icv9_IMG_1225.jpeg" 
                              alt="Προνομιακό Πακέτο" 
                              className="w-16 h-16 rounded-xl object-cover"
                              style={{ filter: 'brightness(1.1) saturate(1.3)' }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-white font-semibold">Προνομιακό Πακέτο</p>
                                {user.subscription_active && user.partner_type === 'premium' && (
                                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">ΕΝΕΡΓΟ</span>
                                )}
                              </div>
                              <p className="text-slate-400 text-sm mt-1">Για Νοσοκομεία & Ιατρικά Κέντρα</p>
                              {!(user.subscription_active && user.partner_type === 'premium') && (
                                <p className="text-gold text-xs mt-2 font-medium">Κάντε κλικ για αγορά →</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-slate-500 text-sm mt-4">
                        * Μπορείτε να αναβαθμίσετε ή να αλλάξετε υπηρεσία ανά πάσα στιγμή
                      </p>
                      
                      {/* Payment Methods */}
                      <div className="mt-6 pt-6 border-t border-slate-700">
                        <p className="text-slate-400 text-sm text-center mb-4">Αποδεκτοί τρόποι πληρωμής</p>
                        <div className="flex flex-wrap items-center justify-center gap-4">
                          {/* Visa */}
                          <div className="bg-white rounded-lg px-3 py-2">
                            <svg className="h-8 w-auto" viewBox="0 0 750 471">
                              <path d="M278.198 334.228l33.36-195.763h53.358l-33.384 195.763h-53.334zm246.905-191.54c-10.57-3.966-27.135-8.222-47.822-8.222-52.725 0-89.863 26.551-90.18 64.604-.297 28.129 26.514 43.821 46.754 53.185 20.77 9.597 27.752 15.716 27.654 24.283-.133 13.123-16.586 19.115-31.924 19.115-21.355 0-32.701-2.967-50.225-10.273l-6.878-3.111-7.487 43.822c12.463 5.467 35.508 10.199 59.438 10.445 56.09 0 92.502-26.248 92.916-66.884.199-22.27-14.016-39.215-44.801-53.188-18.65-9.056-30.072-15.099-29.951-24.269 0-8.137 9.668-16.838 30.557-16.838 17.449-.271 30.088 3.534 39.936 7.5l4.781 2.259 7.232-42.428zm137.308-4.223h-41.232c-12.773 0-22.332 3.486-27.94 16.234l-79.244 179.402h56.031s9.159-24.121 11.232-29.418c6.125 0 60.555.084 68.336.084 1.596 6.854 6.492 29.334 6.492 29.334h49.512l-43.187-195.636zm-65.418 126.408c4.414-11.279 21.26-54.724 21.26-54.724-.316.521 4.381-11.334 7.074-18.684l3.607 16.878s10.217 46.729 12.352 56.527h-44.293v.003zM209.878 138.465l-52.239 133.496-5.565-27.129c-9.726-31.274-40.025-65.157-73.898-82.12l47.767 171.204 56.455-.064 84.004-195.386-56.524-.001" fill="#0E4595"/>
                              <path d="M131.92 138.465H45.879l-.682 4.073c66.939 16.204 111.232 55.363 129.618 102.415l-18.709-89.96c-3.229-12.396-12.597-16.095-24.186-16.528" fill="#F2AE14"/>
                            </svg>
                          </div>
                          {/* Mastercard */}
                          <div className="bg-white rounded-lg px-3 py-2">
                            <svg className="h-8 w-auto" viewBox="0 0 152.407 108">
                              <path fill="#ff5f00" d="M60.412 25.697h31.5v56.606h-31.5z"/>
                              <path d="M62.412 54a35.938 35.938 0 0 1 13.75-28.303 36 36 0 1 0 0 56.606A35.938 35.938 0 0 1 62.412 54z" fill="#eb001b"/>
                              <path d="M134.407 54a35.999 35.999 0 0 1-58.245 28.303 36.005 36.005 0 0 0 0-56.606A35.999 35.999 0 0 1 134.407 54z" fill="#f79e1b"/>
                            </svg>
                          </div>
                          {/* Apple Pay */}
                          <div className="bg-white rounded-lg px-3 py-2">
                            <svg className="h-8 w-auto" viewBox="0 0 165.521 105.965">
                              <path d="M150.698 0H14.823C6.626 0 0 6.626 0 14.823v76.319c0 8.197 6.626 14.823 14.823 14.823h135.875c8.197 0 14.823-6.626 14.823-14.823V14.823C165.521 6.626 158.895 0 150.698 0z"/>
                              <path d="M35.766 34.739c1.261-1.577 2.126-3.707 1.892-5.916-1.813.079-4.097 1.182-5.404 2.759-1.182 1.34-2.205 3.549-1.934 5.6 2.047.158 4.018-1.024 5.446-2.443zm1.871 3.076c-3.012-.158-5.601 1.735-7.019 1.735-1.419 0-3.628-1.656-5.996-1.577-3.091.079-5.996 1.813-7.573 4.587-3.233 5.601-0.869 13.883 2.285 18.47 1.498 2.285 3.312 4.824 5.68 4.745 2.285-.079 3.154-1.498 5.916-1.498 2.759 0 3.549 1.498 5.996 1.419 2.443-.079 4.018-2.285 5.521-4.587 1.735-2.522 2.443-4.982 2.522-5.14-.079-.079-4.903-1.892-4.982-7.494-.079-4.666 3.786-6.874 3.944-7.019-2.126-3.154-5.521-3.549-6.716-3.628l.422-.013zm22.347-5.837v35.343h5.442v-12.1h7.494c6.874 0 11.698-4.745 11.698-11.619s-4.745-11.619-11.54-11.619h-13.094v-.005zm5.442 4.587h6.241c4.745 0 7.415 2.522 7.415 6.953s-2.68 7.019-7.494 7.019h-6.162V36.565zm29.38 31.073c3.391 0 6.557-1.735 7.968-4.508h.158v4.191h5.061V48.263c0-5.14-4.097-8.452-10.409-8.452-5.916 0-10.251 3.391-10.409 8.057h4.903c.395-2.205 2.443-3.628 5.363-3.628 3.391 0 5.363 1.656 5.363 4.587v2.047l-7.019.395c-6.557.395-10.093 3.076-10.093 7.731 0 4.745 3.628 7.889 9.114 7.889zm1.419-4.033c-2.996 0-4.903-1.419-4.903-3.628 0-3.707 1.892-4.191 6.874-4.587l6.241-.395v2.126c0 3.786-3.154 6.478-8.212 6.478v.006zm18.47 13.41c5.284 0 7.81-2.047 9.982-8.215l9.507-26.626h-5.521l-6.399 20.472h-.158l-6.399-20.472h-5.68l9.192 25.603-.474 1.498c-.79 2.68-2.126 3.707-4.429 3.707-.395 0-1.182-.079-1.498-.079v4.191c.316.079 1.577.158 1.877.158v-.237z" fill="#fff"/>
                            </svg>
                          </div>
                          {/* Google Pay */}
                          <div className="bg-white rounded-lg px-3 py-2">
                            <svg className="h-8 w-auto" viewBox="0 0 435.97 173.13">
                              <path fill="#5f6368" d="M206.2 84.58v50.75h-16.1V10h42.7a38.61 38.61 0 0 1 27.65 10.85A34.88 34.88 0 0 1 272 47.3a34.72 34.72 0 0 1-11.55 26.6q-11.2 10.68-27.65 10.67H206.2zm0-59.15v43.72h27a21.28 21.28 0 0 0 15.93-6.48 21.36 21.36 0 0 0 0-30.63 21 21 0 0 0-15.93-6.62h-27z"/>
                              <path fill="#5f6368" d="M309.1 46.78q17.85 0 28.18 9.54t10.32 26.16v52.85h-15.4v-11.9h-.7q-10 14.63-26.6 14.62-14.18 0-23.63-8.4a26.59 26.59 0 0 1-9.45-20.93q0-13.23 10-21.17t26.33-7.96q14 0 23.1 5.12v-3.57a18.49 18.49 0 0 0-6.65-14.36 22.76 22.76 0 0 0-15.58-5.95q-13.65 0-21.7 11.37l-14.18-8.93q12.07-16.8 35.96-16.49zm-20.83 62.3a12.86 12.86 0 0 0 5.25 10.41 19.22 19.22 0 0 0 12.43 4.3 25.53 25.53 0 0 0 18.08-7.61q8-7.61 8-17.33-7.35-5.88-20.3-5.88-9.45 0-16.45 4.87a15.05 15.05 0 0 0-7 11.24z"/>
                              <path fill="#5f6368" d="M436 49.53l-53.9 123.95h-16.62l20-43.23-35.35-80.72h17.5l25.55 61.6h.35l24.85-61.6z"/>
                              <path fill="#4285f4" d="M141.14 73.64A85.79 85.79 0 0 0 139.9 59H72v27.73h38.89a33.33 33.33 0 0 1-14.38 21.88v17.95h23.21c13.59-12.53 21.42-31.06 21.42-52.92z"/>
                              <path fill="#34a853" d="M72 144c19.43 0 35.79-6.38 47.72-17.38l-23.21-17.95c-6.47 4.36-14.79 6.88-24.51 6.88-18.78 0-34.72-12.66-40.42-29.72H7.67v18.55A72 72 0 0 0 72 144z"/>
                              <path fill="#fbbc04" d="M31.58 85.83a43.14 43.14 0 0 1 0-27.66V39.62H7.67a72 72 0 0 0 0 64.76z"/>
                              <path fill="#ea4335" d="M72 28.45a39.09 39.09 0 0 1 27.62 10.8l20.55-20.55A69.18 69.18 0 0 0 72 0 72 72 0 0 0 7.67 39.62l23.91 18.55C37.28 41.11 53.22 28.45 72 28.45z"/>
                            </svg>
                          </div>
                          {/* PayPal */}
                          <div className="bg-white rounded-lg px-3 py-2">
                            <svg className="h-8 w-auto" viewBox="0 0 124 33">
                              <path fill="#253B80" d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.142-2.696-1.746-4.985-1.746zM47 13.154c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.469 1.044.332 1.906zM66.654 13.075h-3.275a.57.57 0 0 0-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .562.66h2.95a.95.95 0 0 0 .939-.803l1.77-11.209a.568.568 0 0 0-.561-.658zm-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883-.484-.574-.668-1.391-.514-2.301.295-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.697 1.411.554 2.317zM84.096 13.075h-3.291a.954.954 0 0 0-.787.417l-4.539 6.686-1.924-6.425a.953.953 0 0 0-.912-.678h-3.234a.57.57 0 0 0-.541.754l3.625 10.638-3.408 4.811a.57.57 0 0 0 .465.9h3.287a.949.949 0 0 0 .781-.408l10.946-15.8a.57.57 0 0 0-.468-.895z"/>
                              <path fill="#179BD7" d="M94.992 6.749h-6.84a.95.95 0 0 0-.938.802l-2.766 17.537a.569.569 0 0 0 .562.658h3.51a.665.665 0 0 0 .656-.562l.785-4.971a.95.95 0 0 1 .938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.142-2.694-1.746-4.983-1.746zm.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.725-4.583a.568.568 0 0 1 .562-.481h.473c1.234 0 2.4 0 3.002.704.359.42.468 1.044.331 1.906zM115.434 13.075h-3.273a.567.567 0 0 0-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .564.66h2.949a.95.95 0 0 0 .938-.803l1.771-11.209a.571.571 0 0 0-.565-.658zm-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.666-1.391-.514-2.301.297-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.699 1.411.554 2.317zM119.295 7.23l-2.807 17.858a.569.569 0 0 0 .562.658h2.822c.469 0 .867-.34.939-.803l2.768-17.536a.57.57 0 0 0-.562-.659h-3.16a.571.571 0 0 0-.562.482z"/>
                            </svg>
                          </div>
                        </div>
                        <p className="text-slate-500 text-xs text-center mt-3">Ασφαλείς πληρωμές μέσω Stripe</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </main>

      {/* Footer with Contact Info */}
      <footer className="bg-navy-200 border-t border-slate-800 py-6 mt-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} J.House of Secretary - Πύλη Συνεργατών
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-slate-400">Χρειάζεστε βοήθεια;</span>
              <a href="tel:+35799931264" className="flex items-center gap-2 text-gold hover:text-amber-400 transition-colors">
                <Phone className="w-4 h-4" />
                <span>+357 99 931264</span>
              </a>
              <a href="tel:70000223" className="flex items-center gap-2 text-gold hover:text-amber-400 transition-colors">
                <Phone className="w-4 h-4" />
                <span>7000 0223</span>
              </a>
              <a href="https://wa.me/35799931264" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors">
                <MessageCircle className="w-4 h-4" />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Secretary Service Configuration Modal */}
      {showSecretaryModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-navy-200 rounded-2xl border border-gold/30 w-full max-w-4xl max-h-[90vh] overflow-y-auto my-4"
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-navy-200 border-b border-slate-700 p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-playfair text-white">Ιατρική Τηλεγραμματεία</h2>
                <p className="text-slate-400 text-sm mt-1">Επιλέξτε το πακέτο που ταιριάζει στις ανάγκες σας</p>
              </div>
              <button 
                onClick={() => {
                  setShowSecretaryModal(false);
                  setSelectedCallPackage(null);
                  setAddEmailService(false);
                }}
                className="w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              {/* Trust & Guarantee Banner */}
              <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/20 rounded-xl border border-green-500/30 p-5 mb-6">
                <div className="flex items-start gap-4">
                  <div className="bg-green-500/20 rounded-full p-3">
                    <Shield className="w-8 h-8 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-lg mb-2">Εγγύηση Ικανοποίησης & Ευελιξία</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2 text-slate-300">
                        <span className="text-green-400 mt-0.5">✓</span>
                        <span><strong className="text-green-400">7 μέρες ΔΩΡΕΑΝ δοκιμή</strong> - Δοκιμάστε τις υπηρεσίες μας χωρίς καμία χρέωση (μόνο το κόστος εγγραφής)</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-300">
                        <span className="text-green-400 mt-0.5">✓</span>
                        <span><strong className="text-white">Μηνιαία πληρωμή</strong> - Πληρώνετε μήνα με μήνα, χωρίς μακροχρόνιες δεσμεύσεις</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-300">
                        <span className="text-green-400 mt-0.5">✓</span>
                        <span><strong className="text-white">Ακύρωση οποτεδήποτε</strong> - Διακόψτε τη συνεργασία αν δεν ικανοποιείστε, χωρίς ποινές</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-xs mt-3 italic">
                      Η εμπιστοσύνη σας είναι η προτεραιότητά μας. Εγγυόμαστε επαγγελματική εξυπηρέτηση.
                    </p>
                  </div>
                </div>
              </div>

              {/* Registration Fee Notice */}
              <div className="bg-gradient-to-r from-gold/20 to-amber-500/10 rounded-xl border border-gold/30 p-4 mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">📋</span>
                  <div>
                    <p className="text-white font-semibold">Κόστος Εγγραφής (για 1 χρόνο)</p>
                    <p className="text-gold text-2xl font-bold">€{registrationFee}</p>
                    <p className="text-slate-500 text-xs">Μετά απαιτείται ανανέωση</p>
                  </div>
                </div>
              </div>

              {/* Call Packages Section */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <span className="text-xl">📞</span> Επιλέξτε Πακέτο Κλήσεων
                  <span className="text-slate-400 text-sm font-normal">(με ΦΠΑ)</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {callPackages.map(pkg => (
                    <div 
                      key={pkg.id}
                      onClick={() => setSelectedCallPackage(pkg.id)}
                      className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedCallPackage === pkg.id 
                          ? 'border-gold bg-gold/10' 
                          : 'border-slate-700 hover:border-gold/50 bg-navy-100/30'
                      } ${pkg.featured ? 'md:col-span-2' : ''}`}
                    >
                      {pkg.featured && (
                        <div className="absolute -top-2 left-4 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                          24/7 ΚΑΛΥΨΗ
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedCallPackage === pkg.id ? 'border-gold bg-gold' : 'border-slate-500'
                          }`}>
                            {selectedCallPackage === pkg.id && (
                              <span className="text-navy-200 text-xs">✓</span>
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium">{pkg.calls} κλήσεις/μήνα</p>
                            {pkg.featured && (
                              <p className="text-slate-400 text-xs">Νοσοκομεία & Ιατρικά Κέντρα - Απεριόριστες κλήσεις</p>
                            )}
                          </div>
                        </div>
                        <p className={`text-xl font-bold ${pkg.featured ? 'text-red-400' : 'text-gold'}`}>
                          €{pkg.price}<span className="text-sm text-slate-400 font-normal">/μήνα</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Services Included */}
              <div className="bg-navy-100/30 rounded-xl border border-slate-700 p-4 mb-6">
                <h3 className="text-white font-semibold mb-3">Υπηρεσίες που περιλαμβάνονται:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Απάντηση και διαχείριση εισερχόμενων κλήσεων</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Διεκπεραίωση εξερχόμενων κλήσεων</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Διαχείριση ραντεβού και ημερολογίων ιατρών</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Καταγραφή και άμεση προώθηση μηνυμάτων</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Συντονισμό με ιατρικά τμήματα & προσωπικό</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Διαχείριση επειγόντων & προτεραιοποίηση</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Οργάνωση follow-up τηλεφωνημάτων</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Υποστήριξη πολλών ειδικοτήτων ταυτόχρονα</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Καθημερινή αναφορά κλήσεων & στατιστικών</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Υπηρεσίες σε Ελληνικά – Αγγλικά – Ρωσικά - Αραβικά</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300 md:col-span-2">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Πλήρης συμμόρφωση με GDPR & ιατρικό απόρρητο</span>
                  </div>
                </div>
              </div>

              {/* Add-On Service */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <span className="text-xl">📧</span> Προαιρετική Υπηρεσία (Add-On)
                </h3>
                <div 
                  onClick={() => setAddEmailService(!addEmailService)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    addEmailService 
                      ? 'border-gold bg-gold/10' 
                      : 'border-slate-700 hover:border-gold/50 bg-navy-100/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        addEmailService ? 'border-gold bg-gold' : 'border-slate-500'
                      }`}>
                        {addEmailService && <span className="text-navy-200 text-xs">✓</span>}
                      </div>
                      <div>
                        <p className="text-white font-medium">Διαχείριση Email & Φακέλων Πελατών</p>
                        <p className="text-slate-400 text-xs mt-1">Ολοκληρωμένη υποστήριξη γραφείου με διαχείριση email, φακέλων, αλληλογραφίας και εγγράφων</p>
                      </div>
                    </div>
                    <p className="text-gold text-xl font-bold">+€{emailServicePrice}<span className="text-sm text-slate-400 font-normal">/μήνα</span></p>
                  </div>
                </div>
              </div>

              {/* Annual Package Option - 10% Discount */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <span className="text-xl">📅</span> Διάρκεια Συνδρομής
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    onClick={() => setIsAnnualPackage(false)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      !isAnnualPackage 
                        ? 'border-gold bg-gold/10' 
                        : 'border-slate-700 hover:border-gold/50 bg-navy-100/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        !isAnnualPackage ? 'border-gold bg-gold' : 'border-slate-500'
                      }`}>
                        {!isAnnualPackage && <span className="text-navy-200 text-xs">✓</span>}
                      </div>
                      <div>
                        <p className="text-white font-medium">Μηνιαία</p>
                        <p className="text-slate-400 text-xs">Πληρωμή κάθε μήνα</p>
                      </div>
                    </div>
                  </div>
                  <div 
                    onClick={() => setIsAnnualPackage(true)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative ${
                      isAnnualPackage 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-slate-700 hover:border-green-500/50 bg-navy-100/30'
                    }`}
                  >
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                      -10%
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isAnnualPackage ? 'border-green-500 bg-green-500' : 'border-slate-500'
                      }`}>
                        {isAnnualPackage && <span className="text-white text-xs">✓</span>}
                      </div>
                      <div>
                        <p className="text-white font-medium">Ετήσια</p>
                        <p className="text-green-400 text-xs font-semibold">10% έκπτωση!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gradient-to-r from-navy-100/50 to-slate-800/30 rounded-xl border border-slate-700 p-5 mb-6">
                <h3 className="text-white font-semibold mb-4">Σύνοψη Παραγγελίας</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span>Κόστος Εγγραφής (για 1 χρόνο)</span>
                    <span className="text-white">€{registrationFee.toFixed(2)}</span>
                  </div>
                  {selectedCallPackage && (
                    <div className="flex justify-between text-slate-300">
                      <span>
                        Πακέτο: {callPackages.find(p => p.id === selectedCallPackage)?.calls} κλήσεις
                        {isAnnualPackage && ' (x12 μήνες)'}
                      </span>
                      <span className="text-white">
                        €{isAnnualPackage 
                          ? (callPackages.find(p => p.id === selectedCallPackage)?.price * 12).toFixed(2) + '/έτος'
                          : callPackages.find(p => p.id === selectedCallPackage)?.price.toFixed(2) + '/μήνα'}
                      </span>
                    </div>
                  )}
                  {addEmailService && (
                    <div className="flex justify-between text-slate-300">
                      <span>Email & Φάκελοι Πελατών{isAnnualPackage && ' (x12 μήνες)'}</span>
                      <span className="text-white">
                        €{isAnnualPackage 
                          ? (emailServicePrice * 12).toFixed(2) + '/έτος'
                          : emailServicePrice.toFixed(2) + '/μήνα'}
                      </span>
                    </div>
                  )}
                  {isAnnualPackage && calculateTotal().discount > 0 && (
                    <div className="flex justify-between text-green-400">
                      <span>🎉 Έκπτωση 10% (Ετήσια)</span>
                      <span>-€{calculateTotal().discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-700 pt-2 mt-2">
                    <div className="flex justify-between text-slate-400 text-xs">
                      <span>Υποσύνολο</span>
                      <span>€{calculateTotal().subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-400 text-xs">
                      <span>Έξοδα συναλλαγής (Stripe)</span>
                      <span>€{calculateTotal().fee.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gold/30">
                    <span className="text-white font-semibold">Σύνολο Πληρωμής</span>
                    <span className="text-3xl font-bold text-gold">€{calculateTotal().total.toFixed(2)}</span>
                  </div>
                  {isAnnualPackage && (
                    <p className="text-green-400 text-xs text-center mt-2">
                      ✓ Εξοικονομείτε €{calculateTotal().discount.toFixed(2)} με την ετήσια συνδρομή!
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    setShowSecretaryModal(false);
                    setSelectedCallPackage(null);
                    setAddEmailService(false);
                    setIsAnnualPackage(false);
                  }}
                  className="flex-1 py-3 border border-slate-600 text-slate-300 hover:bg-slate-700/50 font-medium rounded-xl transition-all"
                >
                  Ακύρωση
                </button>
                <button 
                  onClick={() => {
                    if (!selectedCallPackage) {
                      alert('Παρακαλώ επιλέξτε πακέτο κλήσεων');
                      return;
                    }
                    const selectedPkg = callPackages.find(p => p.id === selectedCallPackage);
                    const totals = calculateTotal();
                    const customConfig = {
                      call_package: selectedCallPackage,
                      call_package_name: selectedPkg?.name,
                      call_package_price: selectedPkg?.price,
                      registration_fee: registrationFee,
                      add_email_service: addEmailService,
                      email_service_price: addEmailService ? emailServicePrice : 0,
                      total: totals.total,
                    };
                    setPaymentModalConfig({
                      package_id: 'secretary_custom',
                      custom_config: customConfig,
                      amount: totals.total,
                      packageName: `Ιατρική Τηλεγραμματεία - ${selectedPkg?.name || 'Custom'}`,
                      onCardPayment: async () => {
                        setSecretaryPaymentLoading(true);
                        try {
                          const response = await axios.post(`${API}/payments/checkout`, {
                            package_id: 'secretary_custom',
                            origin_url: window.location.origin,
                            username: user.username,
                            custom_config: customConfig,
                          });
                          window.location.href = response.data.checkout_url;
                        } catch (err) {
                          console.error('Payment error:', err);
                          alert('Σφάλμα κατά την πληρωμή. Δοκιμάστε ξανά.');
                          setSecretaryPaymentLoading(false);
                        }
                      },
                    });
                  }}
                  disabled={!selectedCallPackage || secretaryPaymentLoading}
                  className={`flex-1 py-3 font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${
                    selectedCallPackage && !secretaryPaymentLoading
                      ? 'bg-gradient-to-r from-gold to-amber-600 hover:from-amber-500 hover:to-gold text-navy-200'
                      : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  {secretaryPaymentLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Μεταφορά στο Stripe...</span>
                    </>
                  ) : (
                    <>
                      <span>Πληρωμή €{calculateTotal().total.toFixed(2)}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Premium Package Configuration Modal */}
      {showPremiumModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-navy-200 rounded-2xl border border-red-500/30 w-full max-w-4xl max-h-[90vh] overflow-y-auto my-4"
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-navy-200 border-b border-slate-700 p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-playfair text-white">Προνομιακό Πακέτο - Νοσοκομεία & Κλινικές</h2>
                <p className="text-slate-400 text-sm mt-1">Απεριόριστες κλήσεις για μεγάλες μονάδες υγείας</p>
              </div>
              <button 
                onClick={() => {
                  setShowPremiumModal(false);
                  setAdd24x7Service(false);
                  setShowContactConfirmation(false);
                }}
                className="w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              {/* Contact Confirmation Message */}
              {showContactConfirmation ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-6">📞</div>
                  <h3 className="text-2xl font-bold text-white mb-4">Ευχαριστούμε για το ενδιαφέρον σας!</h3>
                  <p className="text-slate-300 text-lg mb-6">
                    Θα επικοινωνήσει κάποιος συνεργάτης μας <span className="text-red-400 font-bold">σύντομα</span> μαζί σας για να συζητήσουμε τις ανάγκες σας.
                  </p>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 max-w-md mx-auto mb-6">
                    <p className="text-slate-400 text-sm">Εναλλακτικά, καλέστε μας:</p>
                    <p className="text-2xl font-bold text-red-400">+357 99 931264</p>
                  </div>
                  <button 
                    onClick={() => {
                      setShowPremiumModal(false);
                      setAdd24x7Service(false);
                      setShowContactConfirmation(false);
                    }}
                    className="px-8 py-3 bg-gradient-to-r from-red-600 to-red-500 text-white font-bold rounded-xl hover:from-red-500 hover:to-red-600"
                  >
                    Κλείσιμο
                  </button>
                </div>
              ) : (
                <>
                  {/* Registration Fee Notice */}
                  <div className="bg-gradient-to-r from-red-500/10 to-red-600/5 rounded-xl border border-red-500/30 p-4 mb-6">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">📋</span>
                      <div>
                        <p className="text-white font-semibold">Κόστος Εγγραφής (για 1 χρόνο)</p>
                        <p className="text-red-400 text-2xl font-bold">€300</p>
                        <p className="text-slate-500 text-xs">Μετά απαιτείται ανανέωση</p>
                      </div>
                    </div>
                  </div>

                  {/* Main Package */}
                  <div className="bg-navy-100/30 rounded-xl border border-red-500/30 p-5 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <span className="text-xl">🏥</span> Πακέτο Απεριόριστων Κλήσεων
                      </h3>
                      <span className="bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm font-medium">Premium</span>
                    </div>
                    <p className="text-2xl font-bold text-red-400 mb-4">Επικοινωνήστε μαζί μας</p>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Ανεξαρτήτως του όγκου των κλήσεών σας, μπορούμε να ικανοποιήσουμε όλες τις ανάγκες σας. 
                      Επικοινωνήστε μαζί μας για να συζητήσουμε τη σύναψη συνεργασίας προσαρμοσμένη στις δικές σας απαιτήσεις.
                    </p>
                  </div>

                  {/* Services Included */}
                  <div className="bg-navy-100/30 rounded-xl border border-slate-700 p-4 mb-6">
                    <h3 className="text-white font-semibold mb-3">Υπηρεσίες που περιλαμβάνονται:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-start gap-2 text-slate-300">
                        <span className="text-red-400 mt-0.5">✓</span>
                        <span>Απάντηση και διαχείριση απεριόριστων κλήσεων</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-300">
                        <span className="text-red-400 mt-0.5">✓</span>
                        <span>Αποκλειστική ομάδα γραμματέων</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-300">
                        <span className="text-red-400 mt-0.5">✓</span>
                        <span>Διαχείριση ραντεβού και ημερολογίων</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-300">
                        <span className="text-red-400 mt-0.5">✓</span>
                        <span>Συντονισμός με ιατρικά τμήματα</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-300">
                        <span className="text-red-400 mt-0.5">✓</span>
                        <span>Διαχείριση επειγόντων περιστατικών</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-300">
                        <span className="text-red-400 mt-0.5">✓</span>
                        <span>Καθημερινές αναφορές & στατιστικά</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-300">
                        <span className="text-red-400 mt-0.5">✓</span>
                        <span>Υπηρεσίες σε 4 γλώσσες (EL, EN, RU, AR)</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-300">
                        <span className="text-red-400 mt-0.5">✓</span>
                        <span>Πλήρης συμμόρφωση GDPR</span>
                      </div>
                    </div>
                  </div>

                  {/* Why Choose Us */}
                  <div className="bg-gradient-to-r from-red-500/10 to-gold/10 rounded-xl border border-gold/30 p-5 mb-6">
                    <h3 className="text-gold font-semibold mb-4 flex items-center gap-2">
                      <span>✨</span> Γιατί να μας επιλέξετε
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-3 text-slate-300">
                        <span className="text-gold">✓</span>
                        <span>Προσαρμοσμένες λύσεις για κάθε μέγεθος επιχείρησης</span>
                      </div>
                      <div className="flex items-start gap-3 text-slate-300">
                        <span className="text-gold">✓</span>
                        <span>Ευέλικτη τιμολόγηση ανάλογα με τις ανάγκες σας</span>
                      </div>
                      <div className="flex items-start gap-3 text-slate-300">
                        <span className="text-gold">✓</span>
                        <span>Δυνατότητα 24/7 εξυπηρέτησης</span>
                      </div>
                      <div className="flex items-start gap-3 text-slate-300">
                        <span className="text-gold">✓</span>
                        <span>Αποκλειστική ομάδα γραμματέων</span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="bg-navy-100/50 rounded-xl border border-slate-700 p-5 mb-6">
                    <h3 className="text-white font-semibold mb-4">Στοιχεία Επικοινωνίας</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-slate-300">
                        <span className="text-gold text-xl">📞</span>
                        <div>
                          <p className="text-white font-medium">+357-22321316</p>
                          <p className="text-slate-500 text-xs">Σταθερό</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-slate-300">
                        <span className="text-gold text-xl">📱</span>
                        <div>
                          <p className="text-white font-medium">+357-99931264</p>
                          <p className="text-slate-500 text-xs">Κινητό / WhatsApp</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-slate-300">
                        <span className="text-gold text-xl">✉️</span>
                        <div>
                          <p className="text-white font-medium">j.houseofsecretary@cytanet.com.cy</p>
                          <p className="text-slate-500 text-xs">Email</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button 
                      onClick={() => {
                        setShowPremiumModal(false);
                        setAdd24x7Service(false);
                        setIsAnnualPremium(false);
                      }}
                      className="flex-1 py-3 border border-slate-600 text-slate-300 hover:bg-slate-700/50 font-medium rounded-xl transition-all"
                    >
                      Κλείσιμο
                    </button>
                    <a 
                      href="https://wa.me/35799931264?text=Ενδιαφέρομαι%20για%20το%20Premium%20Πακέτο%20Απεριόριστων%20Κλήσεων"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 py-3 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      <span>Επικοινωνία μέσω WhatsApp</span>
                    </a>
                  </div>
                  
                  <p className="text-center text-slate-500 text-xs mt-4">
                    Ή καλέστε μας στο <span className="text-gold">+357-22321316</span> για άμεση εξυπηρέτηση
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Patient Line Configuration Modal */}
      {showPatientLineModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-navy-200 rounded-2xl border border-green-500/30 w-full max-w-2xl max-h-[90vh] overflow-y-auto my-4"
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-navy-200 border-b border-slate-700 p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-playfair text-white">Γραμμή του Ασθενή</h2>
                <p className="text-slate-400 text-sm mt-1">Επιλέξτε πόσους μήνες θέλετε να προπληρώσετε</p>
              </div>
              <button 
                onClick={() => {
                  setShowPatientLineModal(false);
                  setSelectedMonths(0);
                }}
                className="w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              {/* Registration Fee Notice */}
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/10 rounded-xl border border-green-500/30 p-4 mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">📋</span>
                  <div>
                    <p className="text-white font-semibold">Κόστος Εγγραφής (για 1 χρόνο)</p>
                    <p className="text-green-400 text-2xl font-bold">€{patientLineRegistration}</p>
                    <p className="text-slate-500 text-xs">Περιλαμβάνει τον 1ο μήνα ΔΩΡΕΑΝ!</p>
                  </div>
                </div>
              </div>

              {/* First Month Free Notice */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6 text-center">
                <span className="text-3xl">🎁</span>
                <p className="text-green-400 font-bold text-lg mt-2">Ο 1ος μήνας είναι ΔΩΡΕΑΝ!</p>
                <p className="text-slate-400 text-sm">Με την εγγραφή σας, ξεκινάτε αμέσως χωρίς επιπλέον κόστος</p>
              </div>

              {/* Month Selection */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <span className="text-xl">📅</span> Προπληρωμή Επιπλέον Μηνών
                  <span className="text-slate-400 text-sm font-normal">(Προαιρετικά)</span>
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  Μετά τον 1ο ΔΩΡΕΑΝ μήνα, το κόστος είναι <span className="text-green-400 font-bold">€{patientLineMonthlyPrice}/μήνα</span>.
                  Επιλέξτε πόσους επιπλέον μήνες θέλετε να προπληρώσετε τώρα:
                </p>
                
                {/* Month Selector Buttons */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(month => (
                    <button
                      key={month}
                      onClick={() => setSelectedMonths(month)}
                      className={`p-3 rounded-xl border-2 font-medium transition-all ${
                        selectedMonths === month
                          ? 'border-green-500 bg-green-500/20 text-green-400'
                          : 'border-slate-700 hover:border-green-500/50 text-slate-300 hover:text-white'
                      }`}
                    >
                      {month === 0 ? 'Μόνο εγγραφή' : `+${month} ${month === 1 ? 'μήνας' : 'μήνες'}`}
                    </button>
                  ))}
                </div>
                
                {selectedMonths > 0 && (
                  <p className="text-slate-400 text-sm text-center">
                    Σύνολο μηνών υπηρεσίας: <span className="text-green-400 font-bold">{selectedMonths + 1}</span> (1 ΔΩΡΕΑΝ + {selectedMonths} προπληρωμένοι)
                  </p>
                )}
              </div>

              {/* Services Included */}
              <div className="bg-navy-100/30 rounded-xl border border-slate-700 p-4 mb-6">
                <h3 className="text-white font-semibold mb-3">Υπηρεσίες που περιλαμβάνονται:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Απάντηση κλήσεων ασθενών</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Κλείσιμο ραντεβού</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Υπενθυμίσεις ραντεβού</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Αναφορές κλήσεων</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Διαχείριση επειγόντων</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Πολυγλωσσική υποστήριξη</span>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gradient-to-r from-navy-100/50 to-slate-800/30 rounded-xl border border-slate-700 p-5 mb-6">
                <h3 className="text-white font-semibold mb-4">Σύνοψη Παραγγελίας</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span>Κόστος Εγγραφής (1 χρόνο) + 1ος μήνας ΔΩΡΕΑΝ</span>
                    <span className="text-white">€{patientLineRegistration.toFixed(2)}</span>
                  </div>
                  {selectedMonths > 0 && (
                    <div className="flex justify-between text-slate-300">
                      <span>Προπληρωμή {selectedMonths} {selectedMonths === 1 ? 'μήνα' : 'μηνών'} × €{patientLineMonthlyPrice}</span>
                      <span className="text-white">€{calculatePatientLineTotal().monthsCharge.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-700 pt-2 mt-2">
                    <div className="flex justify-between text-slate-400 text-xs">
                      <span>Υποσύνολο</span>
                      <span>€{calculatePatientLineTotal().subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-400 text-xs">
                      <span>Έξοδα συναλλαγής (Stripe)</span>
                      <span>€{calculatePatientLineTotal().fee.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-green-500/30">
                    <span className="text-white font-semibold">Σύνολο Πληρωμής</span>
                    <span className="text-3xl font-bold text-green-400">€{calculatePatientLineTotal().total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    setShowPatientLineModal(false);
                    setSelectedMonths(0);
                  }}
                  className="flex-1 py-3 border border-slate-600 text-slate-300 hover:bg-slate-700/50 font-medium rounded-xl transition-all"
                >
                  Ακύρωση
                </button>
                <button 
                  onClick={() => {
                    const totals = calculatePatientLineTotal();
                    const customConfig = {
                      registration_fee: patientLineRegistration,
                      selected_months: selectedMonths,
                      monthly_price: patientLineMonthlyPrice,
                      months_charge: totals.monthsCharge,
                      total: totals.total,
                    };
                    setPaymentModalConfig({
                      package_id: 'patient_line_custom',
                      custom_config: customConfig,
                      amount: totals.total,
                      packageName: 'Γραμμή του Ασθενή',
                      onCardPayment: async () => {
                        setPatientLinePaymentLoading(true);
                        try {
                          const response = await axios.post(`${API}/payments/checkout`, {
                            package_id: 'patient_line_custom',
                            origin_url: window.location.origin,
                            username: user.username,
                            custom_config: customConfig,
                          });
                          window.location.href = response.data.checkout_url;
                        } catch (err) {
                          console.error('Payment error:', err);
                          alert('Σφάλμα κατά την πληρωμή. Δοκιμάστε ξανά.');
                          setPatientLinePaymentLoading(false);
                        }
                      },
                    });
                  }}
                  disabled={patientLinePaymentLoading}
                  className={`flex-1 py-3 font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${
                    !patientLinePaymentLoading
                      ? 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-600 text-white'
                      : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  {patientLinePaymentLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Μεταφορά στο Stripe...</span>
                    </>
                  ) : (
                    <span>Πληρωμή €{calculatePatientLineTotal().total.toFixed(2)}</span>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Website Creation Modal */}
      {showWebsiteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-gradient-to-br from-navy-100 to-navy-200 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-blue-500/30 shadow-2xl"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-4">
                  <div className="bg-[#0a1628] rounded-xl p-2 border border-blue-500/30">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/qzalgc9k_IMG_1220.jpeg" 
                      alt="Δημιουργία Ιστοσελίδας" 
                      className="h-14 w-auto rounded-lg mix-blend-lighten"
                      style={{ filter: 'brightness(1.2) saturate(1.6) sepia(0.3) hue-rotate(-5deg)' }}
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Δημιουργία Ιατρικής Ιστοσελίδας</h2>
                    <p className="text-blue-400 text-sm">Επαγγελματική παρουσία στο διαδίκτυο</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowWebsiteModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Description */}
              <div className="bg-navy-200/50 rounded-xl p-4 mb-6 border border-slate-700/50">
                <p className="text-slate-300 text-sm">
                  Δημιουργούμε σύγχρονες, επαγγελματικές ιστοσελίδες για ιατρούς, ιατρεία και κλινικές. 
                  Με responsive design που λειτουργεί άψογα σε κινητά, tablets και υπολογιστές.
                </p>
              </div>

              {/* Features */}
              <div className="bg-gradient-to-r from-blue-900/20 to-navy-200/50 rounded-xl p-5 mb-6 border border-blue-500/20">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-400" />
                  Τι περιλαμβάνει η ιστοσελίδα σας
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>Σύγχρονο responsive design</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>Φωτογραφίες ιατρείου</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>Βιογραφικό γιατρού</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>Λίστα υπηρεσιών</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>Χειρουργικές επεμβάσεις</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>Google Reviews (κριτικές)</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>Φόρμα επικοινωνίας</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>Google Maps ενσωματωμένο</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>Click-to-call κουμπιά</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>SEO βελτιστοποίηση</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>SSL πιστοποιητικό ασφαλείας</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-blue-400 mt-0.5">✓</span>
                    <span>GDPR συμμόρφωση</span>
                  </div>
                </div>
              </div>

              {/* Annual Support */}
              <div className="bg-gradient-to-r from-green-900/20 to-navy-200/50 rounded-xl p-5 mb-6 border border-green-500/20">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-400" />
                  Ετήσια Υποστήριξη
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Φιλοξενία (Hosting)</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Domain name διαχείριση</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Τεχνική υποστήριξη</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Ενημερώσεις ασφαλείας</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>Αλλαγές περιεχομένου</span>
                  </div>
                  <div className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>24/7 monitoring</span>
                  </div>
                </div>
              </div>

              {/* Example Website */}
              <div className="bg-navy-200/50 rounded-xl p-4 mb-6 border border-slate-700/50">
                <p className="text-slate-400 text-sm mb-3">Δείτε ένα παράδειγμα ιστοσελίδας που δημιουργήσαμε:</p>
                <a 
                  href="https://jhouseofsecretary.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>jhouseofsecretary.com</span>
                </a>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowWebsiteModal(false)}
                  className="flex-1 py-3 border border-slate-600 text-slate-300 hover:bg-slate-700/50 font-medium rounded-xl transition-all"
                >
                  Κλείσιμο
                </button>
                <a 
                  href="https://wa.me/35799931264?text=Ενδιαφέρομαι%20για%20δημιουργία%20ιστοσελίδας%20για%20το%20ιατρείο%20μου"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Επικοινωνήστε μαζί μας</span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Floating Chat Bubble */}
      <PartnerChatBubble username={user.username} />

      {/* Payment Method Modal */}
      {paymentModalConfig && (
        <PaymentMethodModal
          isOpen={!!paymentModalConfig}
          onClose={() => {
            setPaymentModalConfig(null);
            setSecretaryPaymentLoading(false);
            setPatientLinePaymentLoading(false);
          }}
          amount={paymentModalConfig.amount}
          packageName={paymentModalConfig.packageName}
          bankTransferConfig={{
            package_id: paymentModalConfig.package_id,
            username: user.username,
            custom_config: paymentModalConfig.custom_config,
          }}
          onCardPayment={() => {
            const fn = paymentModalConfig.onCardPayment;
            setPaymentModalConfig(null);
            if (fn) fn();
          }}
        />
      )}
    </div>
  );
};

// Partners Portal Login Page
const PartnersPortalPage = ({ onBack }) => {
  // Check for stored user session on load
  const getStoredUser = () => {
    try {
      const storedUser = localStorage.getItem('jhouse_partner_user');
      const token = localStorage.getItem('partnerToken');
      if (storedUser && token) {
        return JSON.parse(storedUser);
      }
      // Clear inconsistent state
      localStorage.removeItem('jhouse_partner_user');
      localStorage.removeItem('partnerToken');
      return null;
    } catch {
      localStorage.removeItem('jhouse_partner_user');
      localStorage.removeItem('partnerToken');
      return null;
    }
  };

  // Check for reset_token immediately on initialization
  const getInitialView = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reset_token')) return 'resetPassword';
    // If user is stored, go directly to dashboard
    const storedUser = getStoredUser();
    if (storedUser) {
      // Check if stored user is admin
      if (storedUser.role === 'admin') return 'admin';
      return 'dashboard';
    }
    return 'login';
  };
  
  const getInitialToken = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('reset_token') || '';
  };

  const [view, setView] = useState(getInitialView); // 'login', 'register', 'setPassword', 'forgotPassword', 'resetPassword', 'dashboard', 'admin'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [partnerType, setPartnerType] = useState('full');
  const [resetToken, setResetToken] = useState(getInitialToken);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(getStoredUser);
  const [isAdmin, setIsAdmin] = useState(() => {
    const stored = getStoredUser();
    return stored?.role === 'admin';
  });
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  
  // Phone verification states
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [viberUrl, setViberUrl] = useState('');

  // PWA Install prompt capture
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  // Detect if running as installed PWA
  const isInstalledPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  // Handle install click
  const handleInstallApp = async () => {
    if (deferredPrompt) {
      // Chrome/Android - use native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (outcome === 'accepted') {
        setShowInstallInstructions(false);
      }
    } else {
      // iOS or no prompt available - show instructions
      setShowInstallInstructions(true);
    }
  };

  // Request notification permission on login
  useEffect(() => {
    if (user) {
      requestNotificationPermission().then(granted => {
        setNotificationsEnabled(granted);
        if (granted) {
          sendNotification('J.House Partners', `Καλώς ήρθατε, ${user.name || user.username}!`);
        }
      });
    }
  }, [user]);

  // Save user to localStorage when it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('jhouse_partner_user', JSON.stringify(user));
    }
  }, [user]);

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('jhouse_partner_user');
    localStorage.removeItem('partnerToken');
    setUser(null);
    setIsAdmin(false);
    setView('login');
    setUsername('');
    setPassword('');
  };

  // Handle payment return and reset_token
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Handle reset_token
    if (urlParams.get('reset_token')) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Handle payment return
    const paymentStatus = urlParams.get('payment');
    const returnUsername = urlParams.get('username');
    const sessionId = urlParams.get('session_id');
    
    if (paymentStatus && returnUsername) {
      // Update payment status first
      if (sessionId) {
        axios.get(`${API}/payments/status/${sessionId}`).catch(console.error);
      }
      
      if (paymentStatus === 'success') {
        setPaymentSuccess(true);
        // Auto-load user and go to dashboard
        axios.get(`${API}/partners/${returnUsername}`)
          .then(response => {
            setUser(response.data);
            setView('dashboard');
          })
          .catch(err => {
            console.error('Error loading user after payment:', err);
            setUsername(returnUsername);
            setSuccess('🎉 Η πληρωμή ολοκληρώθηκε! Συνδεθείτε για να δείτε το ιστορικό.');
          });
      } else if (paymentStatus === 'cancelled') {
        setUsername(returnUsername);
        setError('Η πληρωμή ακυρώθηκε.');
      }
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!email || !username || !name) {
      setError('Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία');
      return;
    }
    
    if (username.length < 4) {
      setError('Το όνομα χρήστη πρέπει να έχει τουλάχιστον 4 χαρακτήρες');
      return;
    }
    
    if (!phone) {
      setError('Παρακαλώ εισάγετε τον αριθμό κινητού σας');
      return;
    }
    
    if (!phoneVerified) {
      setError('Παρακαλώ επαληθεύστε πρώτα τον αριθμό κινητού σας');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const params = new URLSearchParams({
        email: email,
        username: username,
        name: name,
        phone: phone,
        company: company || '',
        partner_type: partnerType
      });
      
      await axios.post(`${API}/partners/create-with-phone?${params.toString()}`);
      // Μετά την εγγραφή, πήγαινε κατευθείαν στον ορισμό κωδικού
      setSuccess('Η εγγραφή ολοκληρώθηκε! Τώρα ορίστε τον κωδικό σας.');
      setView('setPassword');
      // Reset fields
      setEmail('');
      setName('');
      setCompany('');
      setPhone('');
      setPhoneVerified(false);
      setShowOtpStep(false);
      setGeneratedOtp('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα εγγραφής. Δοκιμάστε διαφορετικό email ή όνομα χρήστη.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestOtp = async () => {
    if (!phone) {
      setError('Παρακαλώ εισάγετε τον αριθμό κινητού σας');
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/verification/request-otp`, { phone_number: phone });
      setGeneratedOtp(response.data.otp_code);
      setWhatsappUrl(response.data.whatsapp_url);
      setViberUrl(response.data.viber_url);
      setShowOtpStep(true);
      setSuccess('Στείλτε μήνυμα μέσω WhatsApp ή Viber για να λάβετε τον κωδικό σας!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα δημιουργίας κωδικού');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode) {
      setError('Παρακαλώ εισάγετε τον κωδικό επαλήθευσης');
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/verification/verify-otp`, { phone_number: phone, otp_code: otpCode });
      setPhoneVerified(true);
      setSuccess('Ο αριθμός κινητού επαληθεύτηκε επιτυχώς! Συνεχίστε με την εγγραφή.');
      setShowOtpStep(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Λάθος κωδικός επαλήθευσης');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);
    
    try {
      // First try admin login
      try {
        const adminResponse = await axios.post(`${API}/admin/login`, { username, password });
        if (adminResponse.data.is_admin) {
          setIsAdmin(true);
          setUser(adminResponse.data.user);
          localStorage.setItem('partnerToken', adminResponse.data.access_token);
          setView('admin');
          return;
        }
      } catch (adminErr) {
        // Admin login failed - this is expected for non-admin users
        // Continue with regular partner login below
        console.debug('Not an admin user, proceeding with partner login');
      }
      
      // Regular partner login
      const response = await axios.post(`${API}/partners/login`, { username, password });
      
      if (response.data.is_first_login) {
        setView('setPassword');
        setSuccess('Πρώτη σύνδεση! Παρακαλώ ορίστε τον κωδικό σας.');
      } else {
        setUser(response.data.user);
        localStorage.setItem('partnerToken', response.data.access_token);
        setView('dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα σύνδεσης');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (newPassword !== confirmPassword) {
      setError('Οι κωδικοί δεν ταιριάζουν');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/partners/set-password`, {
        username,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      
      setUser(response.data.user);
      localStorage.setItem('partnerToken', response.data.access_token);
      setView('dashboard');
      setSuccess('Ο κωδικός ορίστηκε επιτυχώς!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα ορισμού κωδικού');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/partners/forgot-password`, { email });
      setSuccess('Αν το email υπάρχει στο σύστημα, θα λάβετε οδηγίες επαναφοράς κωδικού.');
      // For testing - show the token (remove in production)
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
        setView('resetPassword');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα αποστολής');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (newPassword !== confirmPassword) {
      setError('Οι κωδικοί δεν ταιριάζουν');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/partners/reset-password`, {
        token: resetToken,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      
      setSuccess('Ο κωδικός άλλαξε επιτυχώς! Μπορείτε τώρα να συνδεθείτε.');
      setView('login');
      setNewPassword('');
      setConfirmPassword('');
      setResetToken('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα αλλαγής κωδικού');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoutOld = () => {
    localStorage.removeItem('partnerToken');
    localStorage.removeItem('jhouse_partner_user');
    setUser(null);
    setView('login');
    setUsername('');
    setPassword('');
  };

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return 'Καλημέρα';
    } else if (hour >= 12 && hour < 17) {
      return 'Καλό μεσημέρι';
    } else {
      return 'Καλησπέρα';
    }
  };

  // Fallback: if view is dashboard/admin but user is missing, reset to login
  useEffect(() => {
    if ((view === 'dashboard' || view === 'admin') && !user) {
      localStorage.removeItem('jhouse_partner_user');
      localStorage.removeItem('partnerToken');
      setView('login');
    }
  }, [view, user]);

  // Admin Dashboard View
  if (view === 'admin' && isAdmin) {
    return <AdminDashboard onBack={() => setView('login')} onLogout={handleLogout} />;
  }

  // Dashboard View with full features
  if (view === 'dashboard' && user && user.username) {
    return <PartnerDashboard user={user} onBack={() => setView('login')} onLogout={handleLogout} getGreeting={getGreeting} paymentSuccess={paymentSuccess} />;
  }

  return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col">
      {/* Header */}
      <header className="bg-navy-200/80 backdrop-blur-md border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-gold transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Πίσω</span>
          </button>
          <img src={COMPANY.logo} alt="J.House of Secretary" className="h-10 w-auto" />
          <div className="w-20"></div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-block p-2 rounded-xl border-2 border-gold/50 bg-[#0a1628] mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/xvo0ymvd_0-02-05-6c5cfaf762f56772ae92db9cd76e206aebb2fc62cf58bc86751c9e9dfa987a9b_c620333ab2e14187.jpg" 
                alt="Είσοδος Συνεργατών" 
                className="h-36 w-auto"
              />
            </div>
            <h1 className="font-playfair text-3xl text-white mb-2">
              {view === 'login' && 'Πύλη Συνεργατών'}
              {view === 'register' && 'Εγγραφή Συνεργάτη'}
              {view === 'setPassword' && 'Ορισμός Κωδικού'}
              {view === 'forgotPassword' && 'Επαναφορά Κωδικού'}
              {view === 'forgotUsername' && 'Υπενθύμιση Ονόματος Χρήστη'}
              {view === 'resetPassword' && 'Νέος Κωδικός'}
            </h1>
            <p className="text-slate-400">
              {view === 'login' && 'Εισάγετε τα στοιχεία σας για πρόσβαση'}
              {view === 'register' && 'Συμπληρώστε τα στοιχεία σας για εγγραφή'}
              {view === 'setPassword' && 'Ορίστε τον κωδικό σας για πρώτη φορά'}
              {view === 'forgotPassword' && 'Εισάγετε το email σας'}
              {view === 'forgotUsername' && 'Θα σας στείλουμε το όνομα χρήστη σας'}
              {view === 'resetPassword' && 'Εισάγετε τον νέο σας κωδικό'}
            </p>
          </div>

          {/* Forms */}
          <div className="bg-navy-100/50 backdrop-blur-md rounded-2xl p-8 border border-gold/30">
            {/* Success Message */}
            {success && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-green-500/20 border border-green-500/50 rounded-lg p-3 text-green-400 text-sm text-center mb-6">
                {success}
              </motion.div>
            )}

            {/* Error Message */}
            {error && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm text-center mb-6">
                {error}
              </motion.div>
            )}

            {/* Login Form */}
            {view === 'login' && (
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-gold text-sm font-medium mb-2">Όνομα Χρήστη</label>
                  <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="Εισάγετε το όνομα χρήστη σας" />
                </div>
                <div>
                  <label className="block text-gold text-sm font-medium mb-2">Κωδικός Πρόσβασης</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="Εισάγετε τον κωδικό σας" />
                </div>
                <button type="submit" disabled={isLoading} className="w-full py-4 bg-gradient-to-r from-gold to-amber-600 hover:from-amber-500 hover:to-gold text-navy-200 font-bold rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2">
                  {isLoading ? <div className="w-5 h-5 border-2 border-navy-200 border-t-transparent rounded-full animate-spin"></div> : <span>Είσοδος</span>}
                </button>
                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px bg-slate-700"></div>
                  <span className="text-slate-500 text-sm">ή</span>
                  <div className="flex-1 h-px bg-slate-700"></div>
                </div>
                <button type="button" onClick={() => { setView('register'); setError(''); setSuccess(''); }} className="w-full py-3 bg-navy-200/50 border border-gold/50 hover:border-gold text-gold font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2">
                  Νέα Εγγραφή Συνεργάτη
                </button>
                <div className="flex items-center justify-center gap-4 mt-4">
                  <button type="button" onClick={() => { setView('forgotPassword'); setError(''); setSuccess(''); }} className="text-slate-400 hover:text-gold text-sm transition-colors">
                    Ξέχασα τον κωδικό μου
                  </button>
                  <span className="text-slate-600">|</span>
                  <button type="button" onClick={() => { setView('forgotUsername'); setError(''); setSuccess(''); }} className="text-slate-400 hover:text-gold text-sm transition-colors">
                    Ξέχασα το όνομα χρήστη
                  </button>
                </div>
                
                {/* Install App Button - Only show if not already installed */}
                {!isInstalledPWA && (
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <button 
                      type="button" 
                      onClick={handleInstallApp}
                      className="w-full py-3 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-600 text-white font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <Download className="w-5 h-5" />
                      <span>Κατέβασε την Εφαρμογή</span>
                    </button>
                    <p className="text-slate-500 text-xs text-center mt-2">Εγκαταστήστε στο κινητό σας για γρήγορη πρόσβαση</p>
                  </div>
                )}
              </form>
            )}

            {/* Register Form */}
            {view === 'register' && (
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Phone Verification Section - FIRST */}
                <div className="border border-gold/50 rounded-xl p-4 bg-navy-100/30">
                  <label className="block text-gold text-sm font-medium mb-2">
                    Αριθμός Κινητού * 
                    {phoneVerified && <span className="text-green-400 ml-2">✓ Επαληθεύτηκε</span>}
                  </label>
                  <p className="text-slate-400 text-xs mb-3">Απαιτείται επαλήθευση για να συνεχίσετε με την εγγραφή</p>
                  <div className="flex gap-2">
                    <input 
                      type="tel" 
                      value={phone} 
                      onChange={(e) => { setPhone(e.target.value); setPhoneVerified(false); setShowOtpStep(false); }} 
                      className="flex-1 px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" 
                      placeholder="π.χ. 99123456" 
                      disabled={phoneVerified}
                      required 
                    />
                    {!phoneVerified && !showOtpStep && (
                      <button 
                        type="button" 
                        onClick={handleRequestOtp}
                        disabled={isLoading || !phone}
                        className="px-4 py-3 bg-gold text-navy-200 font-semibold rounded-xl hover:bg-gold/90 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        Επαλήθευση
                      </button>
                    )}
                    {phoneVerified && (
                      <button 
                        type="button" 
                        onClick={() => { setPhoneVerified(false); setShowOtpStep(false); setPhone(''); }}
                        className="px-4 py-3 bg-slate-600/50 text-slate-300 rounded-xl hover:bg-slate-600 transition-colors"
                      >
                        Αλλαγή
                      </button>
                    )}
                  </div>
                  
                  {/* OTP Verification Step */}
                  {showOtpStep && !phoneVerified && (
                    <div className="mt-4 space-y-3">
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                        <p className="text-amber-400 font-medium mb-2">📱 Βήμα 1: Στείλτε μήνυμα</p>
                        <p className="text-slate-300 text-sm">Στείλτε μήνυμα στον αριθμό μας μέσω WhatsApp ή Viber για να λάβετε τον κωδικό επαλήθευσης.</p>
                      </div>
                      
                      <div className="flex gap-2">
                        <a 
                          href={`https://wa.me/35799931264?text=${encodeURIComponent(`Γεια σας! Θέλω να επαληθεύσω τον αριθμό μου: ${phone}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 py-3 bg-green-600 hover:bg-green-500 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          Στείλτε μέσω WhatsApp
                        </a>
                        <a 
                          href={`viber://chat?number=35799931264`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 py-3 bg-purple-600 hover:bg-purple-500 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M11.398.002C9.473.028 5.331.344 3.014 2.467 1.294 4.182.518 6.793.377 10.087c-.141 3.293-.218 9.478 5.741 11.076v2.537s-.04.969.602 1.167c.777.238 1.234-.5 1.977-1.302.407-.44.971-1.086 1.396-1.58 3.846.325 6.805-.417 7.144-.528.783-.257 5.213-.822 5.935-6.713.744-6.065-.354-9.9-2.347-11.628l.002-.001C19.477.762 15.923.002 12.053.002h-.002c-.217 0-.437-.002-.653 0zM11.5 1.604c3.534-.052 6.562.572 7.896 1.79 1.582 1.39 2.522 4.6 1.885 9.788-.586 4.81-4.051 5.473-4.73 5.696-.28.092-2.757.692-5.907.456 0 0-2.34 2.824-3.072 3.561-.115.116-.25.162-.34.141-.125-.028-.16-.167-.159-.37l.012-3.858C1.81 17.162 1.86 12.055 1.97 9.27c.112-2.783.73-4.878 2.128-6.271 1.845-1.788 5.294-2.207 7.402-2.21v.815z"/>
                          </svg>
                          Στείλτε μέσω Viber
                        </a>
                      </div>
                      
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-3">
                        <p className="text-blue-400 font-medium mb-2">📩 Βήμα 2: Εισάγετε τον κωδικό</p>
                        <p className="text-slate-400 text-xs mb-3">Μετά την αποστολή μηνύματος, θα λάβετε τον κωδικό επαλήθευσης από εμάς. Εισάγετέ τον εδώ:</p>
                        <div className="flex gap-2">
                          <input 
                            type="text" 
                            value={otpCode} 
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} 
                            className="flex-1 px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-lg text-white text-center text-xl tracking-widest placeholder-slate-500 focus:outline-none focus:border-gold" 
                            placeholder="● ● ● ● ● ●" 
                            maxLength={6}
                          />
                          <button 
                            type="button" 
                            onClick={handleVerifyOtp}
                            disabled={isLoading || otpCode.length !== 6}
                            className="px-5 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-400 transition-colors disabled:opacity-50"
                          >
                            ✓
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-slate-500 text-xs text-center">
                        Ο κωδικός ισχύει για 10 λεπτά. Αν δεν λάβετε απάντηση, δοκιμάστε ξανά.
                      </p>
                    </div>
                  )}
                </div>

                {/* Rest of the form - only shown after phone is verified */}
                {phoneVerified && (
                  <>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                      <span className="text-green-400">✓ Ο αριθμός σας επαληθεύτηκε! Συμπληρώστε τα υπόλοιπα στοιχεία.</span>
                    </div>
                    <div>
                      <label className="block text-gold text-sm font-medium mb-2">Ονοματεπώνυμο *</label>
                      <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="π.χ. Γεώργιος Παπαδόπουλος" required />
                    </div>
                    <div>
                      <label className="block text-gold text-sm font-medium mb-2">Email *</label>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="email@example.com" required />
                    </div>
                    <div>
                      <label className="block text-gold text-sm font-medium mb-2">Όνομα Χρήστη *</label>
                      <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="Τουλάχιστον 4 χαρακτήρες" required />
                    </div>
                    <div>
                      <label className="block text-gold text-sm font-medium mb-2">Εταιρεία / Κλινική</label>
                      <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="Προαιρετικό" />
                    </div>
                    <button type="submit" disabled={isLoading} className="w-full py-4 bg-gradient-to-r from-gold to-amber-600 hover:from-amber-500 hover:to-gold text-navy-200 font-bold rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2">
                      {isLoading ? <div className="w-5 h-5 border-2 border-navy-200 border-t-transparent rounded-full animate-spin"></div> : <span>Ολοκλήρωση Εγγραφής</span>}
                    </button>
                  </>
                )}
                
                <button type="button" onClick={() => { setView('login'); setError(''); setSuccess(''); setShowOtpStep(false); setPhoneVerified(false); }} className="w-full text-center text-slate-400 hover:text-white text-sm transition-colors">
                  Έχετε ήδη λογαριασμό; Συνδεθείτε
                </button>
              </form>
            )}

            {/* Set Password Form (First Login) */}
            {view === 'setPassword' && (
              <form onSubmit={handleSetPassword} className="space-y-6">
                <div>
                  <label className="block text-gold text-sm font-medium mb-2">Νέος Κωδικός</label>
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="Τουλάχιστον 6 χαρακτήρες" />
                </div>
                <div>
                  <label className="block text-gold text-sm font-medium mb-2">Επιβεβαίωση Κωδικού</label>
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="Επαναλάβετε τον κωδικό" />
                </div>
                <button type="submit" disabled={isLoading} className="w-full py-4 bg-gradient-to-r from-gold to-amber-600 hover:from-amber-500 hover:to-gold text-navy-200 font-bold rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2">
                  {isLoading ? <div className="w-5 h-5 border-2 border-navy-200 border-t-transparent rounded-full animate-spin"></div> : <span>Ορισμός Κωδικού</span>}
                </button>
              </form>
            )}

            {/* Forgot Password Form */}
            {view === 'forgotPassword' && (
              <form onSubmit={handleForgotPassword} className="space-y-6">
                <div>
                  <label className="block text-gold text-sm font-medium mb-2">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="Εισάγετε το email σας" />
                </div>
                <button type="submit" disabled={isLoading} className="w-full py-4 bg-gradient-to-r from-gold to-amber-600 hover:from-amber-500 hover:to-gold text-navy-200 font-bold rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2">
                  {isLoading ? <div className="w-5 h-5 border-2 border-navy-200 border-t-transparent rounded-full animate-spin"></div> : <span>Αποστολή Link</span>}
                </button>
                <button type="button" onClick={() => { setView('login'); setError(''); setSuccess(''); }} className="w-full text-center text-slate-400 hover:text-white text-sm transition-colors">
                  Επιστροφή στη σύνδεση
                </button>
              </form>
            )}

            {/* Forgot Username Form */}
            {view === 'forgotUsername' && (
              <form onSubmit={async (e) => {
                e.preventDefault();
                setError('');
                setSuccess('');
                setIsLoading(true);
                try {
                  await axios.post(`${API}/partners/forgot-username`, { email });
                  setSuccess('Αν το email υπάρχει στο σύστημα, θα λάβετε το όνομα χρήστη σας.');
                } catch (err) {
                  setError(err.response?.data?.detail || 'Σφάλμα αποστολής');
                } finally {
                  setIsLoading(false);
                }
              }} className="space-y-6">
                <div>
                  <label className="block text-gold text-sm font-medium mb-2">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="Εισάγετε το email σας" required />
                </div>
                <button type="submit" disabled={isLoading} className="w-full py-4 bg-gradient-to-r from-gold to-amber-600 hover:from-amber-500 hover:to-gold text-navy-200 font-bold rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2">
                  {isLoading ? <div className="w-5 h-5 border-2 border-navy-200 border-t-transparent rounded-full animate-spin"></div> : <span>Αποστολή Ονόματος Χρήστη</span>}
                </button>
                <button type="button" onClick={() => { setView('login'); setError(''); setSuccess(''); }} className="w-full text-center text-slate-400 hover:text-white text-sm transition-colors">
                  Επιστροφή στη σύνδεση
                </button>
              </form>
            )}

            {/* Reset Password Form */}
            {view === 'resetPassword' && (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div>
                  <label className="block text-gold text-sm font-medium mb-2">Νέος Κωδικός</label>
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="Τουλάχιστον 6 χαρακτήρες" />
                </div>
                <div>
                  <label className="block text-gold text-sm font-medium mb-2">Επιβεβαίωση Κωδικού</label>
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-4 py-3 bg-navy-200/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold" placeholder="Επαναλάβετε τον κωδικό" />
                </div>
                <button type="submit" disabled={isLoading} className="w-full py-4 bg-gradient-to-r from-gold to-amber-600 hover:from-amber-500 hover:to-gold text-navy-200 font-bold rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2">
                  {isLoading ? <div className="w-5 h-5 border-2 border-navy-200 border-t-transparent rounded-full animate-spin"></div> : <span>Αλλαγή Κωδικού</span>}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-navy-200 border-t border-slate-800 py-4">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} J.House of Secretary
          </p>
        </div>
      </footer>

      {/* Install Instructions Modal */}
      {showInstallInstructions && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-navy-200 rounded-2xl border border-gold/30 w-full max-w-md overflow-hidden"
          >
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-playfair text-white">Εγκατάσταση Εφαρμογής</h2>
              <button 
                onClick={() => setShowInstallInstructions(false)}
                className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* iOS Instructions */}
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">🍎</span>
                  <h3 className="text-white font-semibold">iPhone / iPad (Safari)</h3>
                </div>
                <ol className="text-slate-300 text-sm space-y-2 list-decimal list-inside">
                  <li>Πατήστε το κουμπί <span className="text-gold">Share</span> (τετράγωνο με βέλος)</li>
                  <li>Κάντε scroll κάτω</li>
                  <li>Πατήστε <span className="text-gold">"Προσθήκη στην Αφετηρία"</span></li>
                  <li>Πατήστε <span className="text-gold">"Προσθήκη"</span></li>
                </ol>
              </div>

              {/* Android Instructions */}
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">🤖</span>
                  <h3 className="text-white font-semibold">Android (Chrome)</h3>
                </div>
                <ol className="text-slate-300 text-sm space-y-2 list-decimal list-inside">
                  <li>Πατήστε τις <span className="text-gold">3 τελείες</span> (πάνω δεξιά)</li>
                  <li>Πατήστε <span className="text-gold">"Εγκατάσταση εφαρμογής"</span></li>
                  <li>Ή <span className="text-gold">"Προσθήκη στην αρχική οθόνη"</span></li>
                </ol>
              </div>

              <p className="text-slate-400 text-xs text-center">
                Μετά την εγκατάσταση, η εφαρμογή θα εμφανιστεί στην αρχική οθόνη του κινητού σας!
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Night Pharmacies Dedicated Page
const NightPharmaciesPage = ({ onBack }) => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Header */}
      <header className="bg-navy-200/80 backdrop-blur-md border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-gold transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>{t('splash.back') || t('patientLine.back')}</span>
          </button>
          <img src={COMPANY.logo} alt="J.House of Secretary" className="h-10 w-auto" />
          <LanguageSwitcher />
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Title Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="mb-6">
              <div className="inline-block p-2 rounded-xl border-2 border-gold/50 bg-[#0a1628]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/tknpj30g_0-02-05-c6ac8a9196a9cd42378849b64ad8fa215b57999bc29ff9c8fa938b673dc0b187_c02284b27665ad65.jpg" 
                  alt="Εφημερεύοντα Φαρμακεία" 
                  className="h-28 md:h-36 w-auto"
                />
              </div>
            </div>
            <h1 className="font-playfair text-4xl md:text-5xl text-white mb-4">
              {t('splash.nightPharmacies')}
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              {t('splash.nightPharmaciesDesc')}
            </p>
          </motion.div>

          {/* Pharmacies Widget */}
          <NightPharmaciesWidget />
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-navy-200 border-t border-slate-800 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} J.House of Secretary. {t('splash.nightPharmaciesFooter')}
          </p>
        </div>
      </footer>
    </div>
  );
};

// Splash Landing Page - Split Screen with 2 Images
// Premium hero card with 3D tilt, gold glow pulse, and shimmer sweep on hover.
const HeroCard = ({ onClick, imageSrc, imageAlt, badgeLabel, delay = 0, fromX = 0, fromY = 0, testId, partnerLogo, partnerLogoAlt, partnerLabel }) => {
  const cardRef = React.useRef(null);
  const [tilt, setTilt] = React.useState({ rx: 0, ry: 0 });
  const [hovered, setHovered] = React.useState(false);
  const [mousePos, setMousePos] = React.useState({ x: 50, y: 50 });

  const handleMouseMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const px = x / rect.width;
    const py = y / rect.height;
    // Tilt: subtle, max ±6deg
    setTilt({
      ry: (px - 0.5) * 12,
      rx: -(py - 0.5) * 10,
    });
    setMousePos({ x: px * 100, y: py * 100 });
  };

  const handleMouseLeave = () => {
    setTilt({ rx: 0, ry: 0 });
    setHovered(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: fromX, y: fromY }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{ duration: 0.8, delay }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      ref={cardRef}
      className="w-full md:w-1/3 h-[28vh] md:h-[70vh] relative cursor-pointer group p-3 md:p-4 bg-[#0a1628]"
      style={{ perspective: '1200px' }}
      data-testid={testId}
    >
      <div
        className="relative w-full h-full max-w-md mx-auto rounded-2xl border-2 border-gold/50 overflow-hidden bg-[#0a1225] will-change-transform"
        style={{
          transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) scale(${hovered ? 1.03 : 1})`,
          transition: 'transform 350ms cubic-bezier(0.22, 1, 0.36, 1), border-color 350ms ease, box-shadow 500ms ease',
          borderColor: hovered ? 'rgba(201,168,76,1)' : 'rgba(201,168,76,0.5)',
          boxShadow: hovered
            ? '0 25px 60px -15px rgba(201,168,76,0.45), 0 0 0 1px rgba(201,168,76,0.4) inset, 0 0 80px rgba(201,168,76,0.2)'
            : '0 8px 30px -10px rgba(0,0,0,0.5)',
        }}
      >
        {/* Spotlight glow following cursor */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 transition-opacity duration-300"
          style={{
            opacity: hovered ? 1 : 0,
            background: `radial-gradient(420px circle at ${mousePos.x}% ${mousePos.y}%, rgba(201,168,76,0.22), transparent 55%)`,
            mixBlendMode: 'screen',
          }}
        />

        <img
          src={imageSrc}
          alt={imageAlt}
          className="w-full h-full object-contain transition-transform duration-700"
          style={{ transform: hovered ? 'scale(1.04)' : 'scale(1)' }}
        />

        {/* Shimmer sweep on hover */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            key={hovered ? 'on' : 'off'}
            className={`absolute top-0 -left-1/3 h-full w-1/3 ${hovered ? 'animate-shimmer-sweep' : ''}`}
            style={{
              background:
                'linear-gradient(110deg, transparent 0%, rgba(201,168,76,0) 35%, rgba(255,225,150,0.35) 50%, rgba(201,168,76,0) 65%, transparent 100%)',
            }}
          />
        </div>

        {/* Badge */}
        <div className="absolute top-[70%] -translate-y-1/2 right-2 z-10">
          <div
            className="bg-[#0a1628]/90 backdrop-blur-sm w-[72px] h-[72px] md:w-[88px] md:h-[88px] rounded-lg border flex items-center justify-center text-center p-1 transition-all duration-300"
            style={{
              borderColor: hovered ? 'rgba(201,168,76,1)' : 'rgba(201,168,76,0.5)',
              boxShadow: hovered ? '0 0 25px rgba(201,168,76,0.5)' : 'none',
              transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
            }}
          >
            <p className="text-gold font-bold text-[9px] md:text-[11px] uppercase leading-tight tracking-wide">
              {badgeLabel}
            </p>
          </div>
        </div>

        {/* Partner overlay (top-left) — compact square badge for prestige */}
        {partnerLogo && (
          <div className="absolute top-2 left-2 md:top-3 md:left-3 z-10 pointer-events-none">
            <div className="flex flex-col items-center justify-center gap-0.5 bg-[#0a1628]/85 backdrop-blur-md w-[54px] h-[54px] md:w-[64px] md:h-[64px] rounded-md border border-gold/60 shadow-[0_4px_20px_-6px_rgba(201,168,76,0.5)] p-1">
              <span className="inline-flex items-center justify-center h-5 md:h-6 w-full rounded-sm bg-white/95">
                <img
                  src={partnerLogo}
                  alt={partnerLogoAlt || 'Partner'}
                  className="h-full w-auto object-contain"
                  loading="eager"
                />
              </span>
              <span className="text-[6px] md:text-[7px] uppercase tracking-[0.1em] text-[#c9a84c] font-bold leading-none text-center">
                {partnerLabel || 'Partner'}
              </span>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Hero section images per language.
// Default (used when no language-specific override exists) is the Greek version.
// Each language can override doctors / patients / international with its own image URL.
const DEFAULT_HERO_IMAGES = {
  doctors: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/yxpibc8g_IMG_1348.jpeg',
  patients: '/hero-patients-el-v2.jpg',
  international: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/iqz4rps3_0-02-05-7c1b197432ebb608e0e4ebac206f9762c0eb93ef28afaaa4b564e10a9d1b2153_f170a388f210fe57.jpg',
};

const HERO_IMAGES_BY_LANG = {
  // Greek = default (no override needed)
  el: DEFAULT_HERO_IMAGES,
  // English
  en: {
    doctors: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/l41aco8v_0-02-05-ea551114a2364b1a88ce2341ee0e017384316f1972a05c50f303a470a5e052fc_78ae2a9d6a340e0b.jpg',
    patients: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/70z2mnz2_0-02-05-cf0d81734b5b700fe2585171bf3047037380e037793272f494cd39babe1ad714_d4911412d79e3300.jpg',
    international: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/6c8i5z95_0-02-05-e129444183172aa3bb639dfcd2b9c608221adcf556f79a4cd3fcbd2f12e32f3c_b81712bf0f70aaa2.jpg',
  },
  // Hebrew
  he: {
    doctors: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/zr6ffhdq_0-02-05-a94444f49d8d07cbd72a3fced7e066bae8e88529ba46cab5ce94c30284a4268f_59d11657da71b485.jpg',
    patients: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/7v7v4me8_0-02-05-17672d269d6ebea0af0ef0ccd2631b112d414031a132dc9a82da5f7d16051c76_7bc25dbd971b896.jpg',
    international: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/4xed9koe_0-02-05-ca88405caab39d7edc4ebb16291f5e0f2fd075820c43ed61c2add26f6abda749_5bba46f437834f4.jpg',
  },
  // Russian
  ru: {
    doctors: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/b8b8xy4v_0-02-05-d769693a59e6649298ebabb2494593076ddf49474541da5b880c6d48be374a67_50e7bdfe5cf735fd.jpg',
    patients: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/8a6a35m7_0-02-05-2c4c7c4567f44fe3fd99662d6531f664503c44b2bd1506309904c58c9056376f_5f19493f8a1a17cb.jpg',
    international: 'https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/7aulrbfp_0-02-05-8a2376613f1b11c253ac211928871b52999e5f5374916ff8d1015f691b8705e1_813de484285cca83.jpg',
  },
  // Other languages (he, ru, ar, zh, fr, es) will fall back to default (Greek images)
  // until the user uploads localised versions.
};

const getHeroImages = (language) =>
  HERO_IMAGES_BY_LANG[language] || DEFAULT_HERO_IMAGES;

const SplashPage = ({ onSelectDoctors, onSelectPatients, onSelectInternational }) => {
  const { t, language, isRTL } = useLanguage();
  const heroImages = getHeroImages(language);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/partners`)
      .then(res => { if (mounted) setPartners(res.data.partners || []); })
      .catch(err => console.error('Failed to load partners', err));
    return () => { mounted = false; };
  }, []);

  // Duplicate the partners exactly once for a seamless loop with translateX(-50%).
  const allPartners = partners.length > 0 ? [...partners, ...partners] : [];

  return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col" data-testid="splash-page">
      {/* Company Logo Header */}
      <div className="py-3 flex flex-col items-center bg-[#0a1628] relative z-[100]">
        <div className={`w-full flex px-4 mb-1 md:mb-0 md:w-auto md:px-0 md:absolute md:top-3 ${isRTL ? 'justify-start md:left-4' : 'justify-end md:right-4'} z-[120]`} data-testid="splash-language-row">
          <LanguageSwitcher />
        </div>
        <img src={COMPANY.logo} alt="J.House of Secretary" className="h-12 md:h-16 w-auto relative z-10" />
        <p className="text-[#c9a84c] text-[10px] md:text-xs uppercase tracking-wider mt-1 font-semibold relative z-10 px-4 text-center">{t('splash.tagline')}</p>
      </div>

      {/* Hero promotional video (website only) — muted autoplay + loop, under the logo */}
      <div className="w-full flex justify-center px-4 md:px-6 pt-4 pb-4">
        <div className="w-full max-w-3xl flex flex-col items-center gap-5">
          <div className="w-full flex justify-center">
            <div className="w-full max-w-2xl">
              <HeroVideo src="/JHOUSE_FINAL.mp4" poster="/JHOUSE_FINAL_poster.jpg" vertical={false} testid="home-hero-video" />
            </div>
          </div>
          <AppStoreBadge source="home" lang={language} testid="home-appstore-badge" />
          <a
            href={`${API}/company-profile?lang=${language}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 w-full sm:w-auto inline-flex items-center justify-center text-center gap-3 px-7 py-3.5 rounded-2xl border border-[#c9a84c]/60 text-[#c9a84c] hover:bg-[#c9a84c] hover:text-[#0a1628] transition-all duration-300 text-sm font-semibold tracking-wide shadow-[0_8px_24px_-10px_rgba(201,168,76,0.5)]"
            data-testid="download-company-profile"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M4 6h16M6 4h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2z" />
            </svg>
            {({ el: 'Κατεβάστε το Εταιρικό μας Προφίλ', en: 'Download our Company Profile', ru: 'Скачать профиль компании', fr: 'Télécharger notre profil', es: 'Descargar perfil de empresa', ar: 'تحميل ملف الشركة', he: 'הורדת פרופיל החברה', zh: '下载公司简介' }[language]) || 'Download our Company Profile'}
          </a>
        </div>
      </div>


      {/* Philosophy Quote — the company's soul statement */}
      <PhilosophySection
        labels={{
          eyebrow: t('philosophy.eyebrow'),
          etymology: t('philosophy.etymology'),
          paragraphs: t('philosophy.paragraphs'),
          closing: t('philosophy.closing'),
        }}
      />

      {/* Trust Bar — live stats (right under header for prime visibility) */}
      <TrustBar
        labels={{
          online: t('trust.online'),
          calls: t('trust.calls'),
          coverage: t('trust.coverage'),
          languages: t('trust.languages'),
          satisfaction: t('trust.satisfaction'),
          languagesWord: t('trust.languagesWord'),
          badgeOnline: t('trust.badgeOnline'),
          badgeLive: t('trust.badgeLive'),
          badgeAlwaysOn: t('trust.badgeAlwaysOn'),
          badgeMultilingual: t('trust.badgeMultilingual'),
          badgeExcellence: t('trust.badgeExcellence'),
        }}
      />

      {/* Main Content - Three Options */}
      <div className="flex-1 flex flex-col md:flex-row">
        <HeroCard
          onClick={onSelectDoctors}
          imageSrc={heroImages.doctors}
          imageAlt="House of Secretary - Για Ιατρούς"
          badgeLabel={t('splash.forDoctors')}
          delay={0}
          fromX={-50}
          testId="splash-doctors-btn"
        />
        <HeroCard
          onClick={onSelectPatients}
          imageSrc={heroImages.patients}
          imageAlt="Η Γραμμή Του Ασθενή"
          badgeLabel={t('splash.forPatients')}
          delay={0.2}
          fromY={30}
          testId="splash-patients-btn"
        />
        <HeroCard
          onClick={onSelectInternational}
          imageSrc={heroImages.international}
          imageAlt="Διεθνής Πρόσβαση Υγείας"
          badgeLabel={t('splash.internationalAccess')}
          delay={0.4}
          fromX={50}
          testId="splash-international-btn"
          partnerLogo="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/3mnvyezt_SHEBA1.jpeg"
          partnerLogoAlt="Sheba Global — Tel HaShomer"
          partnerLabel="Partner"
        />
      </div>

      {/* Partners Section at Bottom */}
      <div className="bg-[#0a1628] py-6 border-t border-gold/30">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center text-gold text-sm uppercase tracking-wider mb-4 font-semibold"
        >
          {t('splash.trustedBy')}
        </motion.p>
        
        {/* Partners Marquee */}
        <div className="overflow-hidden py-4">
          <div className="flex w-max animate-scroll" style={{ willChange: 'transform', backfaceVisibility: 'hidden' }}>
            {allPartners.map((partner, index) => {
              const rawUrl = partner.logo_url || partner.logo || '';
              const src = rawUrl.startsWith('/') ? `${BACKEND_URL}${rawUrl}` : rawUrl;
              return (
                <div key={`m-${index}`} className="flex-shrink-0 mx-3">
                  <div className="w-28 h-28 md:w-36 md:h-36 bg-white rounded-lg border-2 border-gold overflow-hidden flex items-center justify-center p-1">
                    <img
                      src={src}
                      alt={partner.name}
                      loading="eager"
                      decoding="async"
                      className={`w-full h-full object-contain ${partner.zoom ? 'scale-150' : ''} ${partner.scale || ''}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <LegalFooter />
    </div>
  );
};

// Patient Line Page - New Page for Patients
const PL_LINES = [
  { cc: 'cy', tel: '70000223', num: '7000 0223' },
  { cc: 'gr', tel: '8011010364', num: '801 101 0364' },
];
const PlFlag = ({ cc, className = 'w-6 h-auto' }) => (
  <img src={`https://flagcdn.com/w80/${cc}.png`} alt={cc.toUpperCase()} className={`${className} rounded-[2px] shadow`} />
);

const PatientLinePage = ({ onBack, onSelectPharmacies }) => {
  const { t, language } = useLanguage();
  
  const benefitIcons = [Phone, Clock, Shield, Heart, CheckCircle2, Award];

  return (
    <div className="min-h-screen bg-navy-200" data-testid="patient-line-page">
      {/* Floating Night Pharmacies Button - Right Side */}
      <div className="fixed z-40 bottom-3 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:bottom-auto md:right-[92px] md:top-1/2 md:-translate-y-1/2" data-testid="pharmacy-float-wrap">
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        onClick={onSelectPharmacies}
        data-testid="pharmacy-float-btn"
        className="block bg-gradient-to-r from-[#1a1a3e] to-[#0d0d2b] hover:from-[#252550] hover:to-[#151540] p-2 rounded-xl border-2 border-gold/50 hover:border-gold transition-all duration-300 hover:scale-110 hover:shadow-xl hover:shadow-gold/40"
      >
        <img 
          src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/tknpj30g_0-02-05-c6ac8a9196a9cd42378849b64ad8fa215b57999bc29ff9c8fa938b673dc0b187_c02284b27665ad65.jpg" 
          alt="Εφημερεύοντα Φαρμακεία" 
          className="h-14 md:h-24 w-auto"
        />
      </motion.button>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-navy-200/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button 
              onClick={onBack}
              className="flex items-center gap-2 text-slate-300 hover:text-gold transition-colors"
              data-testid="back-btn"
            >
              <ChevronDown className="w-5 h-5 rotate-90" />
              <span>{t('patientLine.back')}</span>
            </button>
            <img src={COMPANY.logo} alt="J.House of Secretary" className="h-12 w-auto" />
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              {PL_LINES.map((l) => (
                <a 
                  key={l.cc}
                  href={`tel:${l.tel}`} 
                  className="hidden sm:flex items-center gap-2 bg-gold text-navy-200 px-3 py-2 rounded-lg font-bold hover:bg-gold-light transition-colors"
                  data-testid={`pl-nav-call-${l.cc}`}
                >
                  <PlFlag cc={l.cc} className="w-5 h-auto" />
                  <span>{l.num}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-200 via-navy-100/50 to-navy-200" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-gold uppercase tracking-[0.3em] text-sm mb-4">
              {t('patientLine.tagline')}
            </p>
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              {t('patientLine.title')}
            </h1>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 mb-8" data-testid="pl-hero-numbers">
              {PL_LINES.map((l, i) => (
                <a key={l.cc} href={`tel:${l.tel}`} className="flex flex-col items-center">
                  <span className="flex items-center gap-3">
                    <PlFlag cc={l.cc} className="w-10 md:w-12 h-auto" />
                    <span className="text-4xl md:text-5xl font-bold gold-gradient" dir="ltr">{l.num}</span>
                  </span>
                  <span className="text-gold/80 text-xs md:text-sm tracking-[0.25em] mt-1">{(LINE_CAPTIONS[language] || LINE_CAPTIONS.en)[i]}</span>
                </a>
              ))}
            </div>
            <p className="text-xl md:text-2xl text-slate-300 mb-4 italic">
              {t('patientLine.slogan')}
            </p>
            <p className="text-lg text-gold mb-8">
              {t('patientLine.free')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-navy-100/50 backdrop-blur-sm border border-gold/30 rounded-xl p-8 max-w-2xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('patientLine.mainMessage')}
            </h2>
            <p className="text-xl text-gold italic">
              {t('patientLine.subMessage')}
            </p>
          </motion.div>

          {/* Professional Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 max-w-3xl mx-auto"
          >
            <div className="bg-navy-100/30 backdrop-blur-sm border border-slate-700/50 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <Shield className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <p className="text-slate-300 text-sm leading-relaxed">
                  {t('splash.disclaimer1')}
                </p>
              </div>
              <div className="space-y-3 text-slate-400 text-xs leading-relaxed pl-8">
                <p>• {t('splash.disclaimer2')}</p>
                <p>• {t('splash.disclaimer3')}</p>
                <p>• {t('splash.disclaimer4')}</p>
                <p>• {t('splash.disclaimer5')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 px-4 bg-navy-100/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-playfair text-3xl md:text-4xl text-white mb-4">
              {(HELP_INFO[language] || HELP_INFO.en).handleTitle}
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(HANDLE_LIST[language] || HANDLE_LIST.en).map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: Math.min(index, 8) * 0.05 }}
                className="flex items-center gap-4 bg-navy-200/50 p-4 rounded-lg border border-slate-800 hover:-translate-y-2 hover:shadow-xl hover:shadow-gold/20 hover:border-gold/60 transition-all duration-300 cursor-pointer group"
              >
                <CheckCircle2 className="w-6 h-6 text-gold flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                <span className="text-slate-200 group-hover:text-white transition-colors duration-300">{service}</span>
              </motion.div>
            ))}
          </div>

          {/* Visible CTA → Patient Line multilingual landing (the QR link) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col items-center gap-3 mt-12"
          >
            <a
              href="/help"
              className="inline-flex flex-col items-center gap-1 py-4 px-10 rounded-2xl font-bold uppercase tracking-[0.14em] text-center transition hover:-translate-y-0.5 hover:shadow-[0_18px_45px_-10px_rgba(201,168,76,0.75)]"
              style={{
                background: 'linear-gradient(180deg, #d4b661 0%, #c9a84c 50%, #a17f2f 100%)',
                color: '#0a1628',
                border: '1px solid rgba(201,168,76,0.9)',
                boxShadow: '0 8px 24px -6px rgba(201,168,76,0.55), inset 0 1px 0 rgba(255,255,255,0.4)',
                fontFamily: 'Georgia, serif',
              }}
              data-testid="patientline-open-link-btn"
            >
              <span className="text-base md:text-lg">Ανοίξτε τη Γραμμή του Ασθενή</span>
              <span className="text-[11px] font-semibold tracking-[0.2em] opacity-80">OPEN THE PATIENT LINE · 20 ΓΛΩΣΣΕΣ</span>
            </a>
            <p className="text-slate-400 text-sm text-center max-w-lg">
              Επιλέξτε τη γλώσσα σας και επικοινωνήστε άμεσα μαζί μας — τηλέφωνο, Live Chat, WhatsApp ή Viber.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-3xl md:text-4xl text-white mb-4">
              {(HELP_INFO[language] || HELP_INFO.en).stepsTitle}
            </h2>
            <p className="text-slate-400 text-lg">
              {(HELP_INFO[language] || HELP_INFO.en).stepsSub}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(HELP_INFO[language] || HELP_INFO.en).steps.map((st, index) => ({ number: String(index + 1).padStart(2, '0'), title: st.t, description: st.d })).map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative hover:-translate-y-2 transition-all duration-300 cursor-pointer group"
              >
                <div className="text-6xl font-bold text-gold/20 mb-4 group-hover:text-gold/40 transition-colors duration-300">{step.number}</div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gold transition-colors duration-300">{step.title}</h3>
                <p className="text-slate-400 group-hover:text-slate-300 transition-colors duration-300">{step.description}</p>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-10 right-0 w-full h-0.5 bg-gradient-to-r from-gold/50 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
            data-testid="patientline-closing"
          >
            <div className="mx-auto mb-5 h-px w-40 bg-gradient-to-r from-transparent via-gold/70 to-transparent" />
            <p className="font-playfair text-xl md:text-2xl text-white">{(HELP[language] || HELP.en).freeSupport}</p>
            <p className="mt-3 text-gold text-xs md:text-sm uppercase tracking-[0.3em]">{(HELP[language] || HELP.en).confidential}</p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 px-4 bg-navy-100/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-3xl md:text-4xl text-white mb-4">
              {t('patientLine.benefitsTitle')}
            </h2>
            <p className="text-slate-400 text-lg">
              {t('patientLine.benefitsSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t('patientLine.benefits').map((benefit, index) => {
              const IconComponent = benefitIcons[index];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-glass rounded-xl p-6 text-center hover:-translate-y-2 hover:shadow-xl hover:shadow-gold/20 hover:border-gold/60 transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-gold/40 group-hover:scale-110 transition-all duration-300">
                    <IconComponent className="w-8 h-8 text-gold" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gold transition-colors duration-300">{benefit.title}</h3>
                  <p className="text-slate-400 group-hover:text-slate-300 transition-colors duration-300">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-navy-100 to-navy-200 border border-gold/30 rounded-2xl p-10"
          >
            <h2 className="font-playfair text-3xl md:text-4xl text-white mb-6">
              {t('patientLine.ctaTitle')}
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              {PL_LINES.map((l) => (
                <a 
                  key={l.cc}
                  href={`tel:${l.tel}`}
                  className="inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-light text-navy-200 px-7 py-4 rounded-xl text-xl md:text-2xl font-bold transition-all duration-300 shadow-xl hover:shadow-gold/30 hover:scale-105"
                  data-testid={`pl-cta-call-${l.cc}`}
                >
                  <Phone className="w-6 h-6" />
                  <PlFlag cc={l.cc} className="w-8 h-auto" />
                  <span dir="ltr">{l.num}</span>
                </a>
              ))}
            </div>
            <p className="text-slate-400">
              {t('patientLine.ctaSubtitle')}
            </p>

            {/* Trust Badge */}
            <div className="flex items-center justify-center mt-8">
              <div className="flex flex-col items-center">
                <div className="bg-blue-900 rounded-lg px-4 py-2 mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-bold">GDPR</span>
                </div>
                <span className="text-xs text-slate-500">Compliant</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400">
            &copy; {new Date().getFullYear()} J.House of Secretary. {t('footer.rights')}
          </p>
        </div>
      </footer>
    </div>
  );
};

// ==================== INTAKE FORM BANNER (visible on main page) ====================
const IntakeFormBanner = () => {
  const { language } = useLanguage();
  const [uploadOpen, setUploadOpen] = useState(false);
  const isEl = language === 'el';
  return (
    <section className="max-w-6xl mx-auto px-4 py-10" data-testid="intake-banner">
      <div
        className="rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6"
        style={{
          background: 'linear-gradient(135deg, #0d1a30 0%, #152238 100%)',
          border: '1px solid rgba(201,168,76,0.5)',
          boxShadow: '0 20px 50px -15px rgba(0,0,0,0.6), inset 0 1px 0 rgba(201,168,76,0.15)',
        }}
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.5)' }}>
            <svg className="w-7 h-7 text-gold" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <p className="text-gold text-[10px] uppercase tracking-[0.25em] font-semibold mb-1">Patient Intake & Medical Assessment</p>
            <h3 className="text-white font-serif text-xl md:text-2xl font-bold leading-tight" style={{ fontFamily: 'Georgia, serif' }}>{isEl ? 'Έντυπο Ιατρικής Αξιολόγησης Ασθενούς' : 'Patient Medical Intake Form'}</h3>
            <p className="text-slate-400 text-xs mt-1">{isEl ? 'Κατεβάστε το, συμπληρώστε το ηλεκτρονικά και στείλτε το πίσω — απευθείας στο γραφείο μας.' : 'Download, fill electronically, and send back — straight to our office.'}</p>
          </div>
        </div>
          <div className="flex flex-col gap-3 items-stretch w-full md:w-auto md:max-w-xs">
            <button
              type="button"
              onClick={() => downloadIntakeFormPdf(language)}
              className="inline-flex items-center justify-center gap-2 py-3 px-5 rounded-lg font-semibold text-sm uppercase tracking-[0.15em] transition hover:shadow-[0_10px_30px_-5px_rgba(201,168,76,0.6)] w-full"
              style={{
                background: 'linear-gradient(180deg, #d4b661 0%, #c9a84c 50%, #a17f2f 100%)',
                color: '#0a1628',
                border: '1px solid rgba(201,168,76,0.9)',
                boxShadow: '0 4px 15px -3px rgba(201,168,76,0.4), inset 0 1px 0 rgba(255,255,255,0.4)',
                fontFamily: 'Georgia, serif',
              }}
              data-testid="mainapp-intake-download-btn"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {isEl ? 'Κατεβάστε PDF' : 'Download PDF'}
            </button>
            <button
              onClick={() => setUploadOpen(true)}
              className="inline-flex items-center justify-center gap-2 py-3 px-5 rounded-lg font-semibold text-sm uppercase tracking-[0.15em] transition w-full"
              style={{
                background: 'transparent',
                color: '#c9a84c',
                border: '1px solid rgba(201,168,76,0.7)',
                fontFamily: 'Georgia, serif',
              }}
              data-testid="mainapp-intake-upload-btn"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l4-4m0 0l4 4m-4-4v12" />
              </svg>
              {isEl ? 'Αποστολή Συμπληρωμένου' : 'Send Completed'}
            </button>
          </div>
      </div>
      <IntakeUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} lang={language} />
    </section>
  );
};


// Main App Component (Doctors/Clinics Page)
const MainApp = ({ onBack, onSelectPartners }) => {
  const { dir } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);

  // PWA Install prompt capture
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  // Detect if running as installed PWA
  const isInstalledPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  // Handle install click
  const handleInstallApp = async () => {
    if (deferredPrompt) {
      // Chrome/Android - use native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (outcome === 'accepted') {
        setShowInstallModal(false);
      }
    } else {
      // iOS or no prompt available - show instructions
      setShowInstallModal(true);
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute('dir', dir);
  }, [dir]);

  return (
    <div className="min-h-screen bg-navy-200">
      {/* Back Button */}
      {onBack && (
        <button 
          onClick={onBack}
          className="fixed top-24 left-4 z-50 flex items-center gap-2 bg-navy-200/90 backdrop-blur-md text-slate-300 hover:text-gold px-4 py-2 rounded-lg border border-slate-700 hover:border-gold/50 transition-all duration-300"
          data-testid="back-to-splash-btn"
        >
          <ChevronDown className="w-5 h-5 rotate-90" />
          <span>Πίσω</span>
        </button>
      )}
      
      {/* Partners Portal Button - Top Right */}
      {onSelectPartners && (
        <div className="fixed top-24 right-4 z-50 flex flex-col items-center gap-2">
          <button 
            onClick={onSelectPartners}
            className="bg-[#0a1628] hover:bg-[#0d1a2d] p-1 rounded-xl border-2 border-gold/50 hover:border-gold transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gold/30"
            data-testid="partners-portal-btn"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/xvo0ymvd_0-02-05-6c5cfaf762f56772ae92db9cd76e206aebb2fc62cf58bc86751c9e9dfa987a9b_c620333ab2e14187.jpg" 
              alt="Είσοδος Συνεργατών" 
              className="h-28 md:h-36 w-auto"
            />
          </button>
          
          {/* Download App Button */}
          {!isInstalledPWA && (
            <button 
              onClick={handleInstallApp}
              className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-600 text-white font-bold py-2 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm shadow-lg hover:shadow-green-500/30"
            >
              <Download className="w-4 h-4" />
              <span>Κατέβασε App</span>
            </button>
          )}
        </div>
      )}
      
      <Navigation />
      <HeroSection />
      <ValuePropositionSection />
      <ServicesSection />
      {/* Partners Section removed from main page */}
      <WhyUsSection />
      <FacilitiesSection />
      <TestimonialsSection />
      <FAQSection />
      <ContactSection />
      <Footer />
      <LiveChatWidget />
      
      {/* Install Instructions Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-navy-200 rounded-2xl border border-gold/30 w-full max-w-md overflow-hidden"
          >
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-playfair text-white">Εγκατάσταση Εφαρμογής</h2>
              <button 
                onClick={() => setShowInstallModal(false)}
                className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* iOS Instructions */}
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">🍎</span>
                  <h3 className="text-white font-semibold">iPhone / iPad (Safari)</h3>
                </div>
                <ol className="text-slate-300 text-sm space-y-2 list-decimal list-inside">
                  <li>Πατήστε το κουμπί <span className="text-gold">Share</span> (τετράγωνο με βέλος)</li>
                  <li>Κάντε scroll κάτω</li>
                  <li>Πατήστε <span className="text-gold">"Προσθήκη στην Αφετηρία"</span></li>
                  <li>Πατήστε <span className="text-gold">"Προσθήκη"</span></li>
                </ol>
              </div>

              {/* Android Instructions */}
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">🤖</span>
                  <h3 className="text-white font-semibold">Android (Chrome)</h3>
                </div>
                <ol className="text-slate-300 text-sm space-y-2 list-decimal list-inside">
                  <li>Πατήστε τις <span className="text-gold">3 τελείες</span> (πάνω δεξιά)</li>
                  <li>Πατήστε <span className="text-gold">"Εγκατάσταση εφαρμογής"</span></li>
                  <li>Ή <span className="text-gold">"Προσθήκη στην αρχική οθόνη"</span></li>
                </ol>
              </div>

              <p className="text-slate-400 text-xs text-center">
                Μετά την εγκατάσταση, η εφαρμογή θα εμφανιστεί στην αρχική οθόνη του κινητού σας!
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Root App with Page Router
// ==================== INTERNATIONAL HEALTH ACCESS PAGE ====================
const MedicalUploadModal = ({ isOpen, onClose }) => {
  const { t, language } = useLanguage();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — must remain empty
  const [files, setFiles] = useState([]);
  const [gdpr, setGdpr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const filesInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const MAX_TOTAL = 25 * 1024 * 1024;

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  const handleFilesChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
    setError('');
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const clearAllFiles = () => setFiles([]);

  const reset = () => {
    setName(''); setPhone(''); setEmail(''); setMessage('');
    setFiles([]); setGdpr(false); setError(''); setSuccess(false); setWebsite('');
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !phone.trim() || !email.trim()) {
      setError(t('international.uploadModal.requiredError'));
      return;
    }
    if (!gdpr) {
      setError(t('international.uploadModal.gdprError'));
      return;
    }
    if (totalSize > MAX_TOTAL) {
      setError(t('international.uploadModal.sizeError'));
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('phone', phone.trim());
      formData.append('email', email.trim());
      formData.append('message', message.trim());
      formData.append('lang', language || 'el');
      formData.append('website', website); // honeypot
      files.forEach(f => formData.append('files', f, f.webkitRelativePath || f.name));

      await axios.post(`${API}/international/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setSuccess(true);
      setTimeout(() => { reset(); onClose(); }, 2500);
    } catch (err) {
      console.error('Upload failed', err);
      if (err?.response?.status === 429) {
        const retryAfter = err.response.headers?.['retry-after'];
        const minutes = retryAfter ? Math.ceil(parseInt(retryAfter, 10) / 60) : 60;
        setError(t('international.uploadModal.rateLimitError').replace('{minutes}', minutes));
      } else {
        setError(t('international.uploadModal.errorMsg'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const formatSize = (b) => b < 1024 * 1024 ? `${(b/1024).toFixed(0)} KB` : `${(b/1024/1024).toFixed(1)} MB`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
        onClick={handleClose}
        data-testid="upload-modal-overlay"
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-navy-100 border border-gold/30 rounded-2xl shadow-2xl max-w-2xl w-full my-8 overflow-hidden"
          data-testid="upload-modal"
        >
          <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white" data-testid="upload-modal-title">
                {t('international.uploadModal.title')}
              </h3>
              <p className="text-sm text-slate-400 mt-1">{t('international.uploadModal.subtitle')}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-slate-400 hover:text-white p-1 rounded transition-colors"
              data-testid="upload-modal-close-btn"
              type="button"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {success ? (
            <div className="p-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <p className="text-white font-medium" data-testid="upload-success-msg">
                {t('international.uploadModal.successMsg')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4" autoComplete="off">
              {/* Honeypot field — hidden from users; bots fill it and get rejected */}
              <div
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                aria-hidden="true"
              >
                <label htmlFor="hp-website">Website (do not fill)</label>
                <input
                  id="hp-website"
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">
                    {t('international.uploadModal.nameLabel')} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none"
                    required
                    data-testid="upload-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">
                    {t('international.uploadModal.phoneLabel')} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none"
                    required
                    data-testid="upload-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-1">
                  {t('international.uploadModal.emailLabel')} <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none"
                  required
                  data-testid="upload-email-input"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-1">
                  {t('international.uploadModal.messageLabel')}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder={t('international.uploadModal.messagePlaceholder')}
                  className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none resize-none"
                  data-testid="upload-message-input"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-1">
                  {t('international.uploadModal.filesLabel')}
                </label>
                <p className="text-xs text-slate-500 mb-2">{t('international.uploadModal.filesHint')}</p>

                <div className="flex flex-wrap gap-2 mb-3">
                  <input
                    ref={filesInputRef}
                    type="file"
                    multiple
                    onChange={handleFilesChange}
                    className="hidden"
                    data-testid="upload-files-input"
                  />
                  <button
                    type="button"
                    onClick={() => filesInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gold/15 text-gold border border-gold/40 rounded-lg hover:bg-gold/25 transition-colors text-sm"
                    data-testid="upload-select-files-btn"
                  >
                    <Upload className="w-4 h-4" />
                    {t('international.uploadModal.selectFiles')}
                  </button>

                  <input
                    ref={folderInputRef}
                    type="file"
                    webkitdirectory=""
                    directory=""
                    multiple
                    onChange={handleFilesChange}
                    className="hidden"
                    data-testid="upload-folder-input"
                  />
                  <button
                    type="button"
                    onClick={() => folderInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700/50 text-slate-200 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                    data-testid="upload-select-folder-btn"
                  >
                    <FolderOpen className="w-4 h-4" />
                    {t('international.uploadModal.selectFolder')}
                  </button>

                  {files.length > 0 && (
                    <button
                      type="button"
                      onClick={clearAllFiles}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/15 text-red-400 border border-red-500/40 rounded-lg hover:bg-red-500/25 transition-colors text-sm"
                      data-testid="upload-clear-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('international.uploadModal.clearFiles')}
                    </button>
                  )}
                </div>

                {files.length > 0 && (
                  <div className="bg-navy-200/60 border border-slate-700 rounded-lg p-3 max-h-40 overflow-y-auto" data-testid="upload-files-list">
                    <div className="flex items-center justify-between mb-2 text-xs">
                      <span className="text-slate-300">
                        {files.length} {t('international.uploadModal.filesSelected')}
                      </span>
                      <span className={totalSize > MAX_TOTAL ? 'text-red-400 font-medium' : 'text-slate-400'}>
                        {formatSize(totalSize)} / 25 MB
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {files.map((f, idx) => (
                        <li key={idx} className="flex items-center justify-between text-xs text-slate-300 py-1">
                          <span className="truncate flex-1 mr-2">
                            {f.webkitRelativePath || f.name}
                          </span>
                          <span className="text-slate-500 mr-2">{formatSize(f.size)}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(idx)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`upload-remove-file-${idx}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={gdpr}
                  onChange={(e) => setGdpr(e.target.checked)}
                  className="mt-1 accent-gold"
                  data-testid="upload-gdpr-checkbox"
                />
                <span>{t('international.uploadModal.gdprConsent')}</span>
              </label>

              {error && (
                <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-300" data-testid="upload-error-msg">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={submitting}
                  className="flex-1 px-4 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700/30 transition-colors disabled:opacity-50"
                  data-testid="upload-cancel-btn"
                >
                  {t('international.uploadModal.cancelBtn')}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-gold to-amber-600 text-navy-200 font-bold rounded-lg hover:from-amber-500 hover:to-gold transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2"
                  data-testid="upload-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('international.uploadModal.sending')}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t('international.uploadModal.submitBtn')}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const InternationalHealthPage = ({ onBack }) => {
  const { t, language } = useLanguage();
  const lang = language === 'el' ? 'el' : 'en';
  const [uploadOpen, setUploadOpen] = useState(false);
  const services = [
    { icon: <Globe className="w-6 h-6 text-gold" />, title: t('international.service1Title'), desc: t('international.service1Desc') },
    { icon: <Shield className="w-6 h-6 text-gold" />, title: t('international.service2Title'), desc: t('international.service2Desc') },
    { icon: <FileText className="w-6 h-6 text-gold" />, title: t('international.service3Title'), desc: t('international.service3Desc') },
    { icon: <Calendar className="w-6 h-6 text-gold" />, title: t('international.service4Title'), desc: t('international.service4Desc') },
    { icon: <Users className="w-6 h-6 text-gold" />, title: t('international.service5Title'), desc: t('international.service5Desc') },
    { icon: <Star className="w-6 h-6 text-gold" />, title: t('international.service6Title'), desc: t('international.service6Desc') },
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Header */}
      <header className="bg-navy-200/80 backdrop-blur-md border-b border-gold/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-gold hover:text-amber-400 transition-colors text-sm" data-testid="intl-back-btn">
            <ChevronLeft className="w-4 h-4" />
            <span>{t('international.back')}</span>
          </button>
          <img 
            src={COMPANY.logo}
            alt="J.House of Secretary"
            className="h-12 w-auto"
            style={{ mixBlendMode: 'screen' }}
          />
          <LanguageSwitcher />
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gold/5 to-transparent" />
        <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <img 
              src="/api/chat/files/intl_logo_nobg.png"
              alt="International Health Access"
              className="h-28 md:h-36 w-auto mx-auto mb-8"
            />
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              {t('international.pageTitle')}
            </h1>
            <p className="text-lg md:text-xl text-gold mb-6">
              {t('international.subtitle')}
            </p>
            <p className="text-slate-400 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
              {t('international.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Banner */}
      <section className="py-8 bg-gold/5 border-y border-gold/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gold text-lg md:text-xl font-medium italic">
            {t('international.missionQuote')}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="text-2xl md:text-3xl font-bold text-white text-center mb-12"
          >
            {t('international.servicesTitle')}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-6 hover:border-gold/30 transition-all"
              >
                <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mb-4">
                  {service.icon}
                </div>
                <h3 className="text-white font-semibold mb-2">{service.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is it for */}
      <section className="py-16 bg-navy-100/30">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">{t('international.whoTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-gold" />
              </div>
              <h3 className="text-white font-semibold mb-2">{t('international.patients')}</h3>
              <p className="text-slate-400 text-sm">{t('international.patientsDesc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-gold" />
              </div>
              <h3 className="text-white font-semibold mb-2">{t('international.doctorsTitle')}</h3>
              <p className="text-slate-400 text-sm">{t('international.doctorsDesc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-4">
                <Globe className="w-7 h-7 text-gold" />
              </div>
              <h3 className="text-white font-semibold mb-2">{t('international.hospitalsTitle')}</h3>
              <p className="text-slate-400 text-sm">{t('international.hospitalsDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Partner: Holistic Skin Care Centre */}
      <section className="py-16 md:py-20 relative overflow-hidden" data-testid="featured-partner-holistic">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(201,168,76,0.06) 0%, transparent 65%)' }}
        />
        <div className="max-w-5xl mx-auto px-4 relative">
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-3 mb-8"
          >
            <span className="h-px w-12 md:w-20 bg-gradient-to-r from-transparent to-[#c9a84c]/70" />
            <span className="text-[#c9a84c] text-base">✦</span>
            <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c9a84c] font-semibold whitespace-nowrap">
              {t('international.featuredPartnerLabel')}
            </span>
            <span className="text-[#c9a84c] text-base">✦</span>
            <span className="h-px w-12 md:w-20 bg-gradient-to-l from-transparent to-[#c9a84c]/70" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="relative bg-gradient-to-br from-navy-100/60 via-navy-100/40 to-navy-100/60 backdrop-blur-sm rounded-2xl border border-gold/25 p-8 md:p-12 shadow-[0_20px_60px_-20px_rgba(201,168,76,0.15)]"
          >
            {/* Corner ornaments */}
            <div aria-hidden="true" className="absolute top-3 left-3 w-8 h-8 border-t border-l border-gold/40 rounded-tl-lg" />
            <div aria-hidden="true" className="absolute top-3 right-3 w-8 h-8 border-t border-r border-gold/40 rounded-tr-lg" />
            <div aria-hidden="true" className="absolute bottom-3 left-3 w-8 h-8 border-b border-l border-gold/40 rounded-bl-lg" />
            <div aria-hidden="true" className="absolute bottom-3 right-3 w-8 h-8 border-b border-r border-gold/40 rounded-br-lg" />

            <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-8 md:gap-12 items-center">
              {/* Logo panel */}
              <div className="bg-white rounded-xl p-6 md:p-8 flex items-center justify-center aspect-square md:aspect-auto md:h-56 shadow-lg mx-auto md:mx-0 w-full max-w-[280px]">
                <img
                  src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/3mnvyezt_SHEBA1.jpeg"
                  alt="Sheba Global — Tel HaShomer Patient Services"
                  loading="lazy"
                  className="max-w-full max-h-full object-contain"
                />
              </div>

              {/* Content */}
              <div className="text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Sheba Global — Tel HaShomer
                </h3>
                <p className="text-[#c9a84c] text-sm md:text-base uppercase tracking-widest mb-4 font-medium">
                  {t('international.holisticTagline')}
                </p>
                <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-6">
                  {t('international.holisticDescription')}
                </p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  {(t('international.holisticTags') || []).map((tag, i) => (
                    <span
                      key={i}
                      className="text-[11px] md:text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-gold/10 border border-gold/30 text-[#e4c275]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4">
          <motion.h2 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="text-2xl md:text-3xl font-bold text-white text-center mb-4"
          >
            {t('international.faqTitle')}
          </motion.h2>
          <p className="text-slate-400 text-center mb-12">{t('international.faqSubtitle')}</p>
          <div className="space-y-3">
            {(t('international.faqItems') || []).map((item, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.03 }}
                className="group bg-navy-100/50 rounded-xl border border-slate-700/50 overflow-hidden"
              >
                <summary className="flex items-center justify-between cursor-pointer px-5 py-4 hover:bg-navy-200/30 transition-colors">
                  <span className="text-white text-sm font-medium pr-4">{item.q}</span>
                  <svg className="w-4 h-4 text-gold flex-shrink-0 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-slate-400 text-sm leading-relaxed">{item.a}</p>
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('international.ctaTitle')}</h2>
          <p className="text-slate-400 mb-8">{t('international.ctaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`mailto:${COMPANY.email}?subject=International Health - Αίτημα Συνεδρίας`}
              className="px-8 py-4 bg-gradient-to-r from-gold to-amber-600 text-navy-200 font-bold rounded-xl hover:from-amber-500 hover:to-gold transition-all text-center"
              data-testid="intl-consultation-btn"
            >
              {t('international.ctaConsultation')}
            </a>
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="px-8 py-4 border-2 border-gold text-gold font-bold rounded-xl hover:bg-gold/10 transition-all text-center inline-flex items-center justify-center gap-2"
              data-testid="intl-upload-btn"
            >
              <Upload className="w-5 h-5" />
              {t('international.ctaUpload')}
            </button>
            <a href={`tel:${COMPANY.phone}`}
              className="px-8 py-4 border-2 border-slate-600 text-white font-bold rounded-xl hover:bg-slate-700/30 transition-all text-center"
              data-testid="intl-call-btn"
            >
              {t('international.ctaCall')}
            </a>
          </div>
        </div>
      </section>

      <MedicalUploadModal isOpen={uploadOpen} onClose={() => setUploadOpen(false)} />

      {/* Footer tagline */}
      <footer className="py-8 border-t border-gold/20 text-center">
        <p className="text-gold text-sm font-medium mb-1">J. House of Secretary</p>
        <p className="text-slate-500 text-xs">{t('international.footer')}</p>
      </footer>
    </div>
  );
};

function App() {
  // Check for reset_token or payment return in URL immediately on initialization
  const getInitialPage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const path = (window.location.pathname || '').toLowerCase();
    if (path.includes('/help') || urlParams.get('page') === 'help') return 'help';
    if (path.includes('/flyer') || urlParams.get('page') === 'flyer') return 'flyer';
    if (path.includes('/agent') || urlParams.get('page') === 'agent') return 'agent';
    if (path.includes('/privacy') || urlParams.get('page') === 'privacy') return 'privacy';
    if (path.includes('/terms') || urlParams.get('page') === 'terms') return 'terms';
    if (path.includes('/professionals') || urlParams.get('page') === 'professionals') return 'professionals';
    if (path.includes('/disclaimer') || urlParams.get('page') === 'disclaimer') return 'disclaimer';
    if (urlParams.get('reset_token')) return 'partners';
    if (urlParams.get('payment') && urlParams.get('username')) return 'partners';
    return 'splash';
  };
  
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [isUnderConstruction, setIsUnderConstruction] = useState(false);

  // Under Construction Page
  if (isUnderConstruction) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex flex-col items-center justify-center p-6">
        {/* Logo */}
        <motion.img 
          src={COMPANY.logo} 
          alt="J.House of Secretary"
          className="h-24 w-auto mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        />
        
        {/* Construction Icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-8"
        >
          <div className="w-32 h-32 rounded-full bg-gold/20 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
        </motion.div>

        {/* Main Text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="text-center"
        >
          <h1 className="font-playfair text-4xl md:text-5xl text-white mb-4">
            ΕΡΧΟΜΑΣΤΕ ΣΥΝΤΟΜΑ
          </h1>
          <h2 className="text-2xl md:text-3xl text-gold mb-6">
            Coming Soon
          </h2>
          <p className="text-slate-400 text-lg max-w-md mx-auto mb-8">
            Η ιστοσελίδα μας βρίσκεται υπό κατασκευή. Θα επιστρέψουμε σύντομα με κάτι εκπληκτικό!
          </p>
          <p className="text-slate-500 text-base max-w-md mx-auto">
            Our website is under construction. We'll be back soon with something amazing!
          </p>
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-12 flex flex-col md:flex-row items-center gap-6 text-slate-400"
        >
          <a href={`tel:${COMPANY.mobile}`} className="flex items-center gap-2 hover:text-gold transition-colors">
            <Phone className="w-5 h-5" />
            <span>{COMPANY.mobile}</span>
          </a>
          <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:text-gold transition-colors">
            <Mail className="w-5 h-5" />
            <span>{COMPANY.email}</span>
          </a>
        </motion.div>

        {/* Animated dots */}
        <motion.div 
          className="mt-12 flex gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-3 h-3 rounded-full bg-gold"
              animate={{ 
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.2
              }}
            />
          ))}
        </motion.div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      {currentPage === 'help' && (
        <HelpLinePage />
      )}
      {currentPage === 'flyer' && (
        <FlyerPage />
      )}
      {currentPage === 'agent' && (
        <AgentPortal />
      )}
      {currentPage === 'privacy' && (
        <LegalPage page="privacy" />
      )}
      {currentPage === 'terms' && (
        <LegalPage page="terms" />
      )}
      {currentPage === 'professionals' && (
        <LegalPage page="professionals" />
      )}
      {currentPage === 'disclaimer' && (
        <LegalPage page="disclaimer" />
      )}
      {currentPage === 'splash' && (
        <SplashPage 
          onSelectDoctors={() => setCurrentPage('doctors')}
          onSelectPatients={() => setCurrentPage('patients')}
          onSelectInternational={() => setCurrentPage('international')}
        />
      )}
      {currentPage === 'doctors' && (
        <MainApp 
          onBack={() => setCurrentPage('splash')} 
          onSelectPartners={() => setCurrentPage('partners')}
        />
      )}
      {currentPage === 'patients' && (
        <PatientLinePage 
          onBack={() => setCurrentPage('splash')} 
          onSelectPharmacies={() => setCurrentPage('pharmacies')}
        />
      )}
      {currentPage === 'pharmacies' && (
        <NightPharmaciesPage onBack={() => setCurrentPage('splash')} />
      )}
      {currentPage === 'partners' && (
        <PartnersPortalPage onBack={() => setCurrentPage('splash')} />
      )}
      {currentPage === 'international' && (
        <InternationalHealthPage onBack={() => setCurrentPage('splash')} />
      )}
      {!['help', 'privacy', 'terms', 'professionals', 'disclaimer', 'flyer', 'agent'].includes(currentPage) && <ShebaPartnershipBar onNavigate={() => setCurrentPage('international')} />}
      {/* Floating contact stack (Live Chat + WhatsApp + Viber + Messenger) — public pages only */}
      {['splash', 'doctors', 'patients', 'international', 'pharmacies', 'partners'].includes(currentPage) && (
        <FloatingContactStack />
      )}
      <CookieConsentBanner />
      {!['help', 'privacy', 'terms', 'professionals', 'disclaimer', 'flyer', 'agent'].includes(currentPage) && <LoadingScreen companyLogo={COMPANY.logo} />}
    </LanguageProvider>
  );
}

export default App;
